/**
 * HomeownerHub — Dreamstate Edition
 * Adapted from HomeownerHub_dreamstate.jsx for DashboardLayout integration.
 *
 * Changes from standalone:
 *   - FloatingIsland nav removed (sidebar handles navigation)
 *   - Root page router removed (dashboard routes handle sections)
 *   - setPage() replaced with useNavigate() where applicable
 *   - Padding adapted to match other dashboard pages
 *   - height:100vh removed — lives inside scrollable layout
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable one-var */
/* eslint-disable @typescript-eslint/no-empty-function */
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Clock,
  Home,
  KeyRound,
  ListFilter,
  Plus,
  Search,
  Sun,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import TrashIcon from '@/assets/icons/trash.svg?react';
import { CollapseIcon, ExpandIcon } from '@/components/dashboard/navigation/SidebarIcons';
import { RepairHorizon, ScrollHintList, Tip } from '@/components/dashboard/scoring';
import { CloseButton, Button as DsButton, LegendItem, SortCaret } from '@/components/ds';
import {
  EditCalendarEventDialog,
  EditCalendarEventDialogRef,
} from '@/components/home/calendar/EditCalendarEventDialog';
import {
  EditCalendarReminderDialog,
  EditCalendarReminderDialogRef,
} from '@/components/home/calendar/EditCalendarReminderDialog';
import InspectionSummaryVisualCard from '@/components/home/cards/InspectionSummaryVisualCard';
import {
  EditContactDialog,
  EditContactDialogRef,
} from '@/components/home/directory/EditContactDialog';
import { SelfServeOnboardingModal } from '@/components/home/onboarding/SelfServeOnboardingModal';
import {
  DeleteRecordDialog,
  DeleteRecordDialogRef,
} from '@/components/home/records/DeleteRecordDialog';
import RecordsBrowser from '@/components/home/records/RecordsBrowser';
import { RFQCompileIndicator } from '@/components/home/RFQCompileIndicator';
import { ReviewAndShareCompileDialog } from '@/components/home/rfqs/ReviewAndShareCompileDialog';
import { ReviewAndShareRFQDialog } from '@/components/home/rfqs/ReviewAndShareRFQDialog';
import { RfqGenerationDialog } from '@/components/home/rfqs/RfqGenerationDialog';
import { RfqHistoryDialog } from '@/components/home/rfqs/RfqHistoryDialog';
import { InspectionCarouselOverlay } from '@/components/inspection/InspectionCarouselOverlay';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useRFQCompile } from '@/context/RFQCompileProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { refreshActivityForProperty } from '@/hooks/activity';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { useResolveIssue } from '@/hooks/issues/useResolveIssue';
import { useCalendarEntries } from '@/hooks/useCalendarEntries';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useContacts } from '@/hooks/useContacts';
import { useDocumentRecords } from '@/hooks/useDocumentRecords';
import { useScoringConfig } from '@/hooks/useScoringConfig';
import { useUserRole } from '@/hooks/useUserRole';
import { useVendors } from '@/hooks/useVendors';
import { CalendarEntryType, GenericCalendarEntry } from '@/models/CalendarEntry';
import { ContactType } from '@/models/Contact';
import { JobState } from '@/models/DocumentProcessingJob';
import { Issue, IssueUrgency, urgencyOrder } from '@/models/Issue';
import { RequestForQuotation } from '@/models/RequestForQuotation';
import { VENDOR_TRADE_GROUPS } from '@/models/Vendor';
import { ConditionScoreView } from '@/pages/dashboard/ConditionScoreView';
import { getDocumentProcessingJob } from '@/services/api/document-processing-jobs';
import { requestRFQForIssue } from '@/services/api/issues';
import { getRequestForQuote, searchRequestForQuoteByIssue } from '@/services/api/rfqs';
import { VALUE_FONT, VALUE_FONT_SMALL, VALUE_TRACKING, VALUE_WEIGHT } from '@/theme/consts';
import { useTranslationPrefix } from '@/translations/i18n';
import { formatPhone } from '@/utils/formatPhone';

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Self-contained retrieval that searches for an existing RFQ for the issue,
// otherwise kicks off generation and polls the document-processing-job.
async function retrieveOrCreateRFQ(issueId: string): Promise<RequestForQuotation> {
  const existing = await searchRequestForQuoteByIssue(issueId);
  if (existing.length > 0) return existing[0];

  const job = await requestRFQForIssue(issueId);
  const maxAttempts = 120; // ~4 min at 2s intervals
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    await sleep(2000);
    const current = await getDocumentProcessingJob(job.id);
    if (current.state === JobState.failed) {
      throw new Error(current.errorReason || 'RFQ generation failed');
    }
    if (current.state === JobState.completed) break;
  }

  const afterGen = await searchRequestForQuoteByIssue(issueId);
  if (afterGen.length === 0) {
    throw new Error('RFQ not available after generation completed');
  }
  return afterGen[0];
}

// ─── TOKENS ──────────────────────────────────────────────────────────────────
const C = {
  bg: 'var(--tx-fill-2)',
  bg2: 'var(--tx-glass-panel)',
  bg3: 'var(--tx-glass-panel)',
  bg4: '#383838',
  canvas: '#bbb7af',
  lime: '#c0ff02',
  teal: '#5AE9FF',
  yellow: '#FFF981',
  orange: '#FF9A4D',
  pink: '#DB5282',
  green: '#4CAF79',
  terra: '#C4622D',
  text: 'var(--tx-text)',
  muted: 'var(--tx-faint)',
  border: 'var(--tx-line)',
};

const sevColor: Record<string, string> = {
  critical: C.pink,
  urgent: C.orange,
  moderate: C.yellow,
  low: C.teal,
  monitor: C.muted,
};
const diffColor: Record<string, string> = { Easy: C.lime, Medium: C.yellow, Hard: C.orange };

// ─── SHARED STYLES ───────────────────────────────────────────────────────────
const card = (extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: C.bg2,
  backgroundImage: 'var(--tx-surface-light)',
  boxShadow: 'var(--tx-shadow)',
  border: 0,
  borderRadius: '24px',
  padding: '20px',
  ...extra,
});

const btn = {
  primary: {
    background: 'var(--tx-fill)',
    border: 0,
    borderRadius: 8,
    height: 36,
    padding: '0 16px',
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'var(--tx-muted)',
    cursor: 'pointer',
    fontFamily: "'Manrope',sans-serif",
    whiteSpace: 'nowrap' as const,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'background 0.15s, color 0.15s',
  },
  ghost: {
    background: 'var(--tx-fill)',
    border: 0,
    borderRadius: 10,
    height: 40,
    padding: '0 20px',
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: C.muted,
    cursor: 'pointer',
    fontFamily: "'Manrope',sans-serif",
    whiteSpace: 'nowrap' as const,
  },
};

// ─── ICONS ───────────────────────────────────────────────────────────────────
const Icon: Record<string, React.ReactElement> = {
  file: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="3" y="1" width="10" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5 6h6M5 9h6M5 12h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  folder: (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
      <path
        d="M2 4a1 1 0 011-1h4l2 2h4a1 1 0 011 1v7a1 1 0 01-1 1H3a1 1 0 01-1-1V4Z"
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  ),
  calendar: (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="3" width="14" height="11" rx="2" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M1 7h14M5 1v4M11 1v4"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  ),
  cart: (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path
        d="M1 1h2l1.5 6.5h7L13 4H4"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="6" cy="12" r="1" fill="currentColor" />
      <circle cx="11" cy="12" r="1" fill="currentColor" />
    </svg>
  ),
  upload: (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
      <path
        d="M6 8V2M3 5l3-3 3 3M2 10h8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  chevron: (
    <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
      <path d="M4 5l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  phone: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M6 2h4a1 1 0 011 1v10a1 1 0 01-1 1H6a1 1 0 01-1-1V3a1 1 0 011-1Z"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <circle cx="8" cy="12" r="0.5" fill="currentColor" />
    </svg>
  ),
  checklist: (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <rect x="2" y="10" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M3 4l1 1 2-2"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M8 4h6M8 12h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  users: (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
      <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M1 13c0-2.2 2.2-4 5-4s5 1.8 5 4"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <path
        d="M12 8a2.5 2.5 0 000-5M15 13c0-1.7-1.3-3.1-3-3.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  ),
  paintbrush: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M13 3l2 2-8 8-3 1 1-3 8-8Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path
        d="M3 15c0-1 .5-2 2-2s2 1 2 2-1 2-2 2-2-1-2-2Z"
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  ),
  leaf: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M15 3C9 3 4 7 4 13c0 0 2-4 5-5 0 3-2 5-2 5 4 0 8-3 8-10Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M4 13l3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  bulb: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M9 2a5 5 0 014 8c-.5.7-1 1.3-1 2v1H6v-1c0-.7-.5-1.3-1-2a5 5 0 014-8Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path d="M6.5 14h5M7 16h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  droplet: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M9 3L5 10a4 4 0 008 0L9 3Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  ),
  lock: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="3" y="8" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M6 8V6a3 3 0 016 0v2"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <circle cx="9" cy="13" r="1.2" fill="currentColor" />
    </svg>
  ),
  window: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="2" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.2" />
      <path d="M9 2v14M2 9h14" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  ),
};

// Quick Access tile — icon/label/count header over a small data visual,
// sized ~2.3x the old compact row so the visual has room to read.
const QuickAccessTile = ({
  onClick,
  icon,
  label,
  value,
  unit,
  corner,
  children,
}: {
  onClick: () => void;
  icon: React.ReactElement;
  label: string;
  value: number;
  unit: string;
  corner?: React.ReactElement;
  children: React.ReactNode;
}) => (
  <div
    onClick={onClick}
    style={{
      ...card({ borderRadius: 18, padding: '18px' }),
      flex: 1,
      minHeight: 180,
      display: 'flex',
      flexDirection: 'column',
      cursor: 'pointer',
      transition: 'border-color .15s',
    }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      {/* the icon stays (Hauser is icon-led) but as a quiet 16px hairline beside the title, not a filled tile */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <div
          style={{
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--tx-muted)',
            flexShrink: 0,
          }}>
          {React.cloneElement(icon as React.ReactElement<React.SVGProps<SVGSVGElement>>, { width: 16, height: 16 })}
        </div>
        <div style={{ fontSize: 15, fontWeight: 400, letterSpacing: '-0.01em', color: 'var(--tx-text)' }}>{label}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--tx-faint)', flexShrink: 0 }}>
        {corner && <div onClick={(e) => e.stopPropagation()}>{corner}</div>}
        <ChevronDown size={16} style={{ transform: 'rotate(-90deg)' }} />
      </div>
    </div>
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span
          style={{
            fontSize: 32,
            fontWeight: 200,
            color: 'var(--tx-text)',
            letterSpacing: '-0.05em',
            lineHeight: 1,
            fontVariantNumeric: 'proportional-nums',
          }}>
          {value}
        </span>
        <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>{unit}</span>
      </div>
    </div>
    <div style={{ marginTop: 'auto', paddingTop: 16 }}>{children}</div>
  </div>
);

const QuickAccessCaption = ({ left, right }: { left: string; right?: string }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: 8,
      fontSize: 11,
      fontWeight: 500,
      color: 'var(--tx-faint)',
    }}>
    <span>{left}</span>
    {right && <span>{right}</span>}
  </div>
);

// A row of thin diagonal ticks, one per `fills` entry, each swept left-to-
// right and filled from the bottom by its 0..1 fraction. Shared by the
// Records (binary, filled left-to-right by file count) and Directory
// (fractional, filled by vendors-saved / recommended-3) tile visuals.
const DiagonalFillLines = ({
  fills,
  shear = 6,
  track = true,
}: {
  fills: number[];
  shear?: number;
  /** false: only the lit lines are drawn (a count, not a share of some capacity) */
  track?: boolean;
}) => (
  <svg
    viewBox={`0 0 ${fills.length * 10} 32`}
    preserveAspectRatio="none"
    style={{ width: '100%', height: 32, display: 'block' }}>
    {fills.map((f, i) => {
      const cx = i * 10 + 5;
      const x0 = cx - shear;
      const y0 = 30;
      const x1 = cx + shear;
      const y1 = 2;
      const fx = x0 + f * (x1 - x0);
      const fy = y0 + f * (y1 - y0);
      return (
        <g key={i}>
          {/* no track: only real files draw; an empty folder shows the strip faintly so the tile isn't blank */}
          {(track || fills.every((x) => x === 0)) && <line
            x1={x0}
            y1={y0}
            x2={x1}
            y2={y1}
            stroke={track ? 'var(--tx-fill-2)' : 'var(--tx-fill)'}
            strokeWidth="1"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />}
          {f > 0 && (
            <line
              x1={x0}
              y1={y0}
              x2={fx}
              y2={fy}
              stroke="var(--tx-muted)"
              strokeWidth="1"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </g>
      );
    })}
  </svg>
);

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
}

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

// ─── SCORE RING ──────────────────────────────────────────────────────────────
function ScoreRing({
  size = 120,
  strokeW = 9,
  value = 78,
  color = C.teal,
  scoreDelta = 0,
}: {
  size?: number;
  strokeW?: number;
  value?: number;
  color?: string;
  scoreDelta?: number;
}) {
  const r = (size - strokeW * 2) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#383838"
          strokeWidth={strokeW}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeW}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: 'stroke-dashoffset 500ms cubic-bezier(0.4,0,0.2,1), stroke 300ms linear',
          }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <div
          style={{
            fontSize: size * 0.28,
            fontWeight: 200,
            lineHeight: 1,
            color,
            fontFamily: VALUE_FONT_SMALL,
            letterSpacing: '-0.05em',
          }}>
          {value}
        </div>
        {scoreDelta > 0 && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              fontFamily: VALUE_FONT_SMALL,
              letterSpacing: 0,
              marginTop: 4,
            }}>
            <span style={{ color: C.lime }}>▲</span>
            <span style={{ color: 'var(--tx-text)' }}>{scoreDelta}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DATA ────────────────────────────────────────────────────────────────────
const PROPERTY = {
  address: '19474 Aeronaut Way',
  city: 'San Jose, CA 95002',
  meta: '4 bed · 3 bath · 2,180 sqft · Est. $1.24M',
  conditionScore: { grade: 'B', value: 78 },
  hmi: { grade: 'C+', value: 71 },
  contextScore: { grade: 'B', value: 81 },
};

const gradeColor = (g = '') =>
  ((
    { A: '#f3f3f3', B: '#5AE9FF', C: '#FFF981', D: '#FF9A4D', F: '#DB5282' } as Record<
      string,
      string
    >
  )[g[0]?.toUpperCase()] ?? C.canvas);

const DNA_ITEMS = [
  ...[{ w: 3 }, { w: 5 }, { w: 4 }, { w: 4 }].map((x) => ({ ...x, sev: 'critical' })),
  ...[
    { w: 5 },
    { w: 6 },
    { w: 5 },
    { w: 4 },
    { w: 6 },
    { w: 5 },
    { w: 4 },
    { w: 5 },
    { w: 6 },
    { w: 4 },
  ].map((x) => ({ ...x, sev: 'urgent' })),
  ...[
    { w: 5 },
    { w: 4 },
    { w: 6 },
    { w: 4 },
    { w: 5 },
    { w: 4 },
    { w: 6 },
    { w: 4 },
    { w: 5 },
    { w: 4 },
    { w: 5 },
    { w: 4 },
    { w: 4 },
  ].map((x) => ({ ...x, sev: 'moderate' })),
  ...[{ w: 12 }, { w: 8 }, { w: 10 }, { w: 7 }, { w: 9 }, { w: 8 }, { w: 7 }].map((x) => ({
    ...x,
    sev: 'low',
  })),
  ...Array(31)
    .fill(null)
    .map(() => ({ sev: 'monitor', w: 2 })),
];
const DNA_COUNTS: Record<string, number> = {
  critical: 4,
  urgent: 10,
  moderate: 13,
  low: 7,
  monitor: 31,
};
const DNA_COLORS: Record<string, string> = {
  critical: C.pink,
  urgent: C.orange,
  moderate: C.yellow,
  low: C.teal,
  monitor: C.muted,
};

const REPAIRS = [
  {
    id: 'r1',
    name: 'HVAC Rubber Boot – Gapped',
    cat: 'AC Unit · HVAC',
    note: 'Moisture risk if unaddressed',
    sev: 'critical',
    cost: '$138',
    diy: true,
    difficulty: 'Easy',
    diyTime: '1–2 hrs',
    saves: '$100',
    products: [
      {
        name: 'Diversitech UC-3 Universal Condenser Pad',
        price: '$18.99',
        retailer: 'Amazon',
        rc: '#FF9900',
        rating: '4.6',
      },
      {
        name: 'AC Rubber Boot Seal Kit 3/4"',
        price: '$12.47',
        retailer: 'Home Depot',
        rc: '#F96302',
        rating: '4.4',
      },
      {
        name: 'Frost King AC Foam Seal Tape',
        price: '$7.88',
        retailer: "Lowe's",
        rc: '#004990',
        rating: '4.5',
      },
    ],
  },
  {
    id: 'r2',
    name: 'Window Safety Locks',
    cat: 'Windows · Safety',
    note: 'Child safety locking recommended',
    sev: 'urgent',
    cost: '$325',
    diy: true,
    difficulty: 'Easy',
    diyTime: '30 min',
    saves: '$280',
    products: [
      {
        name: 'Arlo Child Safety Window Locks (8-pack)',
        price: '$19.99',
        retailer: 'Amazon',
        rc: '#FF9900',
        rating: '4.7',
      },
      {
        name: 'Prime-Line Window Sash Lock (4-pack)',
        price: '$14.98',
        retailer: 'Home Depot',
        rc: '#F96302',
        rating: '4.3',
      },
    ],
  },
  {
    id: 'r3',
    name: 'Rear Stair Handrail Missing',
    cat: 'Exterior · Safety',
    note: 'Building code compliance',
    sev: 'urgent',
    cost: '$450',
    diy: false,
    difficulty: 'Hard',
    diyTime: '4–6 hrs',
    saves: null,
    products: [
      {
        name: 'Everbilt Steel Stair Handrail Kit',
        price: '$42.97',
        retailer: 'Home Depot',
        rc: '#F96302',
        rating: '4.2',
      },
      {
        name: 'Muzata 6ft Wall Mount Handrail Bracket',
        price: '$28.99',
        retailer: 'Amazon',
        rc: '#FF9900',
        rating: '4.6',
      },
    ],
  },
  {
    id: 'r4',
    name: 'Inspect Roof Gutters',
    cat: 'Maintenance',
    note: 'Due Mar 20 – overdue in 2 days',
    sev: 'low',
    cost: null,
    isTask: true,
    diy: true,
    difficulty: 'Easy',
    diyTime: '1 hr',
    saves: '$150',
    products: [
      {
        name: 'Amerimax Snap-In Gutter Filter (6-pack)',
        price: '$16.98',
        retailer: 'Home Depot',
        rc: '#F96302',
        rating: '4.4',
      },
      {
        name: 'GutterBrush Leaf Guard 4" (6ft)',
        price: '$34.99',
        retailer: 'Amazon',
        rc: '#FF9900',
        rating: '4.5',
      },
    ],
  },
];

const DIY_INSPIRATION = [
  {
    id: 'd1',
    icon: 'paintbrush',
    title: 'Interior Paint Refresh',
    diff: 'Easy',
    time: 'Weekend',
    saves: '$800',
    tags: ['Interior', 'High ROI'],
  },
  {
    id: 'd2',
    icon: 'leaf',
    title: 'Drought-Tolerant Landscaping',
    diff: 'Medium',
    time: '2 weekends',
    saves: '$1,200',
    tags: ['Exterior', 'CA Rebate'],
  },
  {
    id: 'd3',
    icon: 'bulb',
    title: 'LED Lighting Upgrade',
    diff: 'Easy',
    time: '2–3 hrs',
    saves: '$240/yr',
    tags: ['Energy', 'Quick Win'],
  },
  {
    id: 'd4',
    icon: 'droplet',
    title: 'Low-Flow Fixture Swap',
    diff: 'Easy',
    time: '2 hrs',
    saves: '$180/yr',
    tags: ['Plumbing', 'Water'],
  },
  {
    id: 'd5',
    icon: 'lock',
    title: 'Smart Lock Install',
    diff: 'Easy',
    time: '45 min',
    saves: '$200',
    tags: ['Security', 'Smart Home'],
  },
  {
    id: 'd6',
    icon: 'window',
    title: 'Door Weather Stripping',
    diff: 'Easy',
    time: '1 hr',
    saves: '$120/yr',
    tags: ['Energy', 'Quick Win'],
  },
];

const RECORDS_DATA = [
  {
    id: 'f1',
    label: 'Maintenance & Repairs',
    count: 4,
    files: [
      { name: 'HVAC Service Report 2024.pdf', date: 'Dec 2024', size: '1.2 MB', type: 'pdf' },
      { name: 'Roof Inspection Photos.zip', date: 'Oct 2024', size: '8.4 MB', type: 'zip' },
      { name: 'Gutter Cleaning Invoice.pdf', date: 'Sep 2024', size: '0.3 MB', type: 'pdf' },
      { name: 'Plumbing Repair Receipt.pdf', date: 'Aug 2024', size: '0.2 MB', type: 'pdf' },
    ],
  },
  {
    id: 'f2',
    label: 'Receipts & Invoices',
    count: 7,
    files: [
      { name: 'Window Install Invoice.pdf', date: 'Nov 2024', size: '0.4 MB', type: 'pdf' },
      { name: 'Electrician Receipt #4821.pdf', date: 'Sep 2024', size: '0.2 MB', type: 'pdf' },
      { name: 'Home Depot Purchase.pdf', date: 'Aug 2024', size: '0.1 MB', type: 'pdf' },
    ],
  },
  {
    id: 'f3',
    label: 'Warranties & Manuals',
    count: 3,
    files: [
      { name: 'HVAC System Warranty.pdf', date: 'Jan 2023', size: '2.1 MB', type: 'pdf' },
      { name: 'Appliance Manuals.zip', date: 'Mar 2023', size: '14 MB', type: 'zip' },
      { name: 'Roof Material Warranty.pdf', date: 'Jun 2022', size: '0.8 MB', type: 'pdf' },
    ],
  },
  {
    id: 'f4',
    label: 'Home Records & Docs',
    count: 6,
    files: [
      { name: 'Purchase Agreement.pdf', date: 'Oct 2023', size: '3.2 MB', type: 'pdf' },
      { name: 'Title Insurance Policy.pdf', date: 'Oct 2023', size: '1.8 MB', type: 'pdf' },
      { name: 'Home Inspection Report.pdf', date: 'Sep 2023', size: '6.7 MB', type: 'pdf' },
      { name: 'Property Survey.pdf', date: 'Aug 2023', size: '4.1 MB', type: 'pdf' },
    ],
  },
  {
    id: 'f5',
    label: 'Renovations & Projects',
    count: 2,
    files: [
      { name: 'Kitchen Remodel Plans.pdf', date: 'Feb 2024', size: '2.9 MB', type: 'pdf' },
      { name: 'Contractor Bids Comparison.pdf', date: 'Jan 2024', size: '0.5 MB', type: 'pdf' },
    ],
  },
];

const DIRECTORY_DATA = [
  {
    category: 'General Contracting',
    contacts: [
      { name: 'Pacific Builders Co.', phone: '(408) 555-0142', last: 'Nov 2024', rating: 4.8 },
      { name: 'Bay Area Renovations', phone: '(408) 555-0167', last: 'Mar 2024', rating: 4.6 },
    ],
  },
  {
    category: 'Plumbing',
    contacts: [
      { name: 'Clear Flow Plumbing', phone: '(408) 555-0193', last: 'Aug 2024', rating: 4.9 },
    ],
  },
  {
    category: 'Electrical',
    contacts: [
      { name: 'Bright Wire Electric', phone: '(408) 555-0128', last: 'Oct 2024', rating: 4.7 },
      { name: 'SJ Electric Services', phone: '(408) 555-0154', last: 'Jun 2024', rating: 4.5 },
      { name: 'Pro Circuit Inc.', phone: '(408) 555-0176', last: 'Jan 2024', rating: 4.8 },
    ],
  },
  {
    category: 'HVAC & Air',
    contacts: [
      { name: 'CoolAir Systems', phone: '(408) 555-0139', last: 'Dec 2024', rating: 4.9 },
      { name: 'Bay HVAC Solutions', phone: '(408) 555-0115', last: 'Apr 2024', rating: 4.6 },
    ],
  },
  { category: 'Roofing', contacts: [] },
  {
    category: 'Landscaping',
    contacts: [
      { name: 'Green Thumb Landscapes', phone: '(408) 555-0182', last: 'Sep 2024', rating: 4.7 },
    ],
  },
];

const CAL_FULL = [
  {
    name: 'HVAC Service Appt.',
    date: 'Mar 19',
    day: 19,
    time: '10:00 AM',
    color: C.teal,
    type: 'Service',
  },
  {
    name: 'Gutter Inspection',
    date: 'Mar 23',
    day: 23,
    time: '9:00 AM',
    color: C.orange,
    type: 'Inspection',
  },
  { name: 'HOA Meeting', date: 'Apr 4', day: 4, time: '7:00 PM', color: C.lime, type: 'Meeting' },
  {
    name: 'Water Heater Service',
    date: 'Apr 1',
    day: 1,
    time: '2:00 PM',
    color: C.muted,
    type: 'Service',
  },
];

// ─── POINTS SYSTEM ───────────────────────────────────────────────────────────
const POINT_EVENTS: Record<string, { pts: number; label: string }> = {
  panel_open: { pts: 2, label: 'Explored a module' },
  dna_expand: { pts: 3, label: 'Reviewed your issues' },
  diy_toggle: { pts: 2, label: 'Made a repair decision' },
  shop_parts: { pts: 3, label: 'Shopped for parts' },
  gen_rfq: { pts: 5, label: 'Generated an RFQ' },
};

const LEVELS = [
  { min: 0, label: 'Newcomer', color: C.muted },
  { min: 15, label: 'Explorer', color: C.canvas },
  { min: 35, label: 'Steward', color: C.teal },
  { min: 65, label: 'Expert', color: C.orange },
  { min: 90, label: 'Master', color: C.lime },
];

const MILESTONES = [25, 50, 75, 100];

function getLevel(pts: number) {
  return [...LEVELS].reverse().find((l) => pts >= l.min) || LEVELS[0];
}
function nextLevel(pts: number) {
  return LEVELS.find((l) => l.min > pts);
}

// ─── SUNBURST EFFECT ─────────────────────────────────────────────────────────
function SunburstEffect({ active, color }: { active: boolean; color: string }) {
  const [phase, setPhase] = useState(0);
  const frameRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    if (!active) {
      setPhase(0);
      return;
    }
    setPhase(1);
    startRef.current = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      if (elapsed < 900) {
        frameRef.current = requestAnimationFrame(tick);
        setPhase(elapsed < 500 ? 1 : 2);
      } else {
        setPhase(0);
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [active]);

  if (phase === 0) return null;
  const spokes = 16;
  const size = 260;
  const cx = size / 2,
    cy = size / 2;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        zIndex: 10,
      }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ overflow: 'visible' }}>
        <style>{`
          @keyframes spokeOut { 0%{stroke-dashoffset:60;opacity:0} 20%{opacity:1} 60%{stroke-dashoffset:0;opacity:1} 100%{stroke-dashoffset:0;opacity:0} }
          @keyframes ringPulse { 0%{r:88;opacity:0.9;stroke-width:3} 100%{r:120;opacity:0;stroke-width:1} }
          @keyframes innerGlow { 0%{r:70;opacity:0.4} 100%{r:100;opacity:0} }
        `}</style>
        <circle
          cx={cx}
          cy={cy}
          fill="none"
          stroke={color}
          strokeOpacity="0.5"
          style={{ animation: 'ringPulse 0.8s ease-out forwards' }}
        />
        {Array.from({ length: spokes }).map((_, i) => {
          const angle = (i / spokes) * 360;
          const rad = (angle * Math.PI) / 180;
          const r1 = 92,
            r2 = 92 + 55;
          const x1 = cx + Math.cos(rad) * r1,
            y1 = cy + Math.sin(rad) * r1;
          const isShort = i % 2 === 1;
          const x2 = isShort ? cx + Math.cos(rad) * (r1 + 30) : cx + Math.cos(rad) * r2;
          const y2 = isShort ? cy + Math.sin(rad) * (r1 + 30) : cy + Math.sin(rad) * r2;
          const delay = (i % 4) * 0.04;
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={i % 3 === 0 ? C.lime : i % 3 === 1 ? color : C.teal}
              strokeWidth={isShort ? 1.5 : 2.5}
              strokeLinecap="round"
              strokeDasharray="60"
              strokeDashoffset="60"
              style={{ animation: `spokeOut 0.75s ease-out ${delay}s forwards` }}
            />
          );
        })}
      </svg>
    </div>
  );
}

// ─── WEATHER CARD ────────────────────────────────────────────────────────────
// WMO weather code to icon/label mapping
export const WMO: Record<
  number,
  { label: string; icon: 'sun' | 'cloud' | 'shower' | 'rain' | 'snow' | 'storm' }
> = {
  0: { label: 'Clear', icon: 'sun' },
  1: { label: 'Mostly Clear', icon: 'sun' },
  2: { label: 'Partly Cloudy', icon: 'cloud' },
  3: { label: 'Overcast', icon: 'cloud' },
  45: { label: 'Fog', icon: 'cloud' },
  48: { label: 'Fog', icon: 'cloud' },
  51: { label: 'Light Drizzle', icon: 'shower' },
  53: { label: 'Drizzle', icon: 'shower' },
  55: { label: 'Heavy Drizzle', icon: 'shower' },
  61: { label: 'Light Rain', icon: 'shower' },
  63: { label: 'Rain', icon: 'rain' },
  65: { label: 'Heavy Rain', icon: 'rain' },
  71: { label: 'Light Snow', icon: 'snow' },
  73: { label: 'Snow', icon: 'snow' },
  75: { label: 'Heavy Snow', icon: 'snow' },
  80: { label: 'Showers', icon: 'shower' },
  81: { label: 'Showers', icon: 'rain' },
  82: { label: 'Heavy Showers', icon: 'rain' },
  95: { label: 'Thunderstorm', icon: 'storm' },
  96: { label: 'Thunderstorm', icon: 'storm' },
  99: { label: 'Thunderstorm', icon: 'storm' },
};

const WeatherIcons: Record<string, React.ReactElement<React.SVGProps<SVGSVGElement>>> = {
  sun: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ overflow: 'visible' }}>
      <circle cx="10" cy="10" r="4" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M10 2v2M10 16v2M2 10h2M16 10h2M4.22 4.22l1.42 1.42M14.36 14.36l1.42 1.42M4.22 15.78l1.42-1.42M14.36 5.64l1.42-1.42"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  ),
  cloud: (
    <svg width="20" height="14" viewBox="0 0 20 14" fill="none" style={{ overflow: 'visible' }}>
      <path
        d="M5 11a4 4 0 01-.5-7.97A5 5 0 0114 4h1a3 3 0 010 6H5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  ),
  shower: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ overflow: 'visible' }}>
      <path
        d="M5 11a4 4 0 01-.5-7.97A5 5 0 0114 4h1a3 3 0 010 6H5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M8 15l-1 3M12 15l-1 3"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  ),
  rain: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ overflow: 'visible' }}>
      <path
        d="M5 11a4 4 0 01-.5-7.97A5 5 0 0114 4h1a3 3 0 010 6H5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M7 16l-1 2M11 16l-1 2M15 16l-1 2"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  ),
  snow: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ overflow: 'visible' }}>
      <path
        d="M5 11a4 4 0 01-.5-7.97A5 5 0 0114 4h1a3 3 0 010 6H5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="16" r="1" fill="currentColor" />
      <circle cx="12" cy="15" r="1" fill="currentColor" />
      <circle cx="10" cy="18" r="1" fill="currentColor" />
    </svg>
  ),
  storm: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ overflow: 'visible' }}>
      <path
        d="M5 11a4 4 0 01-.5-7.97A5 5 0 0114 4h1a3 3 0 010 6H5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path
        d="M10 12l-2 4h4l-2 4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface ForecastDay {
  day: string;
  hi: number;
  lo: number;
  precip: number;
  code: number;
}

function tempColor(t: number): string {
  if (t >= 90) return C.pink;
  if (t >= 80) return C.orange;
  if (t >= 70) return C.yellow;
  if (t >= 60) return '#9a968f';
  return '#60a5fa';
}

const WEATHER_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

function getCachedWeather(key: string): ForecastDay[] | null {
  try {
    const raw = localStorage.getItem(`weather:${key}`);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > WEATHER_CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function setCachedWeather(key: string, data: ForecastDay[]) {
  try {
    localStorage.setItem(`weather:${key}`, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
}

// The 7-day forecast for a place (Open-Meteo, keyless), cached an hour per location. Shared by HomeHQ's weather card
// and the advisor Today screen.
// region (a state's full name, e.g. 'California'): take the first match in it, so a bare city name can't land in another state
// coords: a known point (the browser's location), read directly with no geocoding
export function useLocalForecast(
  city: string,
  state: string,
  zipCode: string,
  region?: string,
  coords?: { lat: number; lon: number } | null,
) {
  const [forecast, setForecast] = useState<ForecastDay[] | null>(null);
  const [loading, setLoading] = useState(false);

  const locationKey = coords ? `${coords.lat.toFixed(2)},${coords.lon.toFixed(2)}` : zipCode || (city ? `${city},${state}` : '');

  useEffect(() => {
    if (!locationKey) return;

    const cached = getCachedWeather(locationKey);
    if (cached) {
      setForecast(cached);
      return;
    }

    setLoading(true);

    const geocodeQuery = zipCode || `${city} ${state}`;
    const point: Promise<{ latitude: number; longitude: number }> = coords
      ? Promise.resolve({ latitude: coords.lat, longitude: coords.lon })
      : fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
            geocodeQuery,
          )}&count=${region ? 10 : 1}&language=en&format=json${region ? '&countryCode=US' : ''}`,
        )
          .then((r) => r.json())
          .then((geo) => {
            const result = region
              ? geo.results?.find((r: { admin1?: string }) => r.admin1 === region)
              : geo.results?.[0];
            if (!result) throw new Error('No geocode result');
            return result;
          });
    point
      .then(({ latitude, longitude }) =>
        fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,weathercode&temperature_unit=fahrenheit&timezone=auto&forecast_days=7`,
        ).then((r) => r.json()),
      )
      .then((data) => {
        if (data.daily) {
          const days: ForecastDay[] = data.daily.time.map((t: string, i: number) => ({
            day: DAYS_SHORT[new Date(t + 'T12:00:00').getDay()],
            hi: Math.round(data.daily.temperature_2m_max[i]),
            lo: Math.round(data.daily.temperature_2m_min[i]),
            precip: data.daily.precipitation_probability_max[i] ?? 0,
            code: data.daily.weathercode[i] ?? 0,
          }));
          setForecast(days);
          setCachedWeather(locationKey, days);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [locationKey]);

  return { forecast, loading };
}

// open items that rain finds first: roofs, flashing, gutters and the water path around the house
const WATER_PATH = /roof|shingle|flashing|gutter|downspout|drainage|grading|water intrusion|skylight|weep screed|chimney cap/i;

function WeatherCard() {
  const { selectedProperty, propertyIssues } = usePropertiesContext();
  const navigate = useNavigate();
  const city = selectedProperty?.property.city ?? '';
  const state = selectedProperty?.property.state ?? '';
  const zipCode = selectedProperty?.property.zipCode ?? '';

  const { forecast, loading } = useLocalForecast(city, state, zipCode);
  const m = useWindowWidth() <= 768;

  const hasProperty = !!selectedProperty;
  const days = forecast ?? [];
  const today = days[0];
  const todayWmo = WMO[today?.code] ?? { label: 'Clear', icon: 'sun' as const };

  const allT = days.flatMap((d) => [d.hi, d.lo]);
  const minT = Math.min(...allT),
    maxT = Math.max(...allT);
  // each day's lo→hi span placed on the week's own scale (the Apple Weather range bar), so warm and cool days read at a glance
  const span = Math.max(1, maxT - minT);
  const rangeBottom = (lo: number) => ((lo - minT) / span) * 100;
  const rangeHeight = (lo: number, hi: number) => Math.max(8, ((hi - lo) / span) * 100);

  // rain likely in the next three days and open water-path items on this house → name them; otherwise stay quiet
  const rainDayIndex = days.slice(0, 3).findIndex((d) => d.precip >= 50);
  const waterItems = (propertyIssues ?? []).filter(
    (i) => !i.resolved && WATER_PATH.test(`${i.title ?? ''} ${i.category ?? ''} ${i.trade ?? ''}`),
  ).length;
  const rainWatch =
    rainDayIndex >= 0 && waterItems > 0
      ? {
          count: waterItems,
          precip: days[rainDayIndex].precip,
          when: rainDayIndex === 0 ? 'today' : rainDayIndex === 1 ? 'tomorrow' : `on ${days[rainDayIndex].day}`,
        }
      : null;

  const location = [city, state].filter(Boolean).join(', ') || 'Your Location';
  const todayName = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  if (!hasProperty) {
    return (
      <div
        style={{
          ...card({ padding: 0 }),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 180,
        }}>
        <span style={{ fontSize: 13, color: C.muted, fontFamily: "'Manrope',sans-serif" }}>
          Select a property to see the weather.
        </span>
      </div>
    );
  }

  if (loading || !forecast) {
    return (
      <div
        style={{
          ...card({ padding: 0 }),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 180,
        }}>
        <span style={{ fontSize: 13, color: C.muted, fontFamily: "'Manrope',sans-serif" }}>
          Loading weather…
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        ...card({ padding: 0 }),
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {/* the Tradehaus Today widget grammar (.tw-h / .now / .tw-foot): eyebrow with the place aside, the glyph beside a
          light temperature, the condition line and its rain meta; then a home note when rain meets open water items */}
      <div style={{ padding: '20px 20px 16px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            minHeight: 16,
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--tx-muted)',
            fontFamily: "'Manrope',sans-serif",
          }}>
          <Sun size={14} style={{ color: 'var(--tx-text)' }} />
          Weather
          <span style={{ marginLeft: 'auto', fontWeight: 500, color: 'var(--tx-faint)', whiteSpace: 'nowrap' }}>
            {location} · {todayName}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                className="hx-wx-now"
                style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--tx-text)' }}>
                {React.cloneElement(WeatherIcons[todayWmo.icon] ?? WeatherIcons.sun, { width: 40, height: 40 })}
              </span>
              <span
                style={{
                  fontSize: 44,
                  fontWeight: 300,
                  letterSpacing: '-0.04em',
                  lineHeight: 1,
                  color: 'var(--tx-text)',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                {today?.hi ?? '--'}°
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, fontFamily: "'Manrope',sans-serif" }}>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  color: 'var(--tx-text)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {todayWmo.label}
                <span style={{ fontWeight: 500, color: 'var(--tx-muted)' }}>
                  {' '}
                  · H {today.hi}° L {today.lo}°
                </span>
              </span>
              <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>
                {(today?.precip ?? 0) >= 20 ? `${today.precip}% chance of rain` : 'No rain expected today'}
              </span>
            </div>
          </div>
          {rainWatch && (
            // the one line that makes this a Hauser card: rain is coming and the house has open water-path items
            <button
              type="button"
              onClick={() => navigate('/dashboard/home-hub/issues')}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 2,
                padding: 0,
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                textAlign: 'right',
                fontFamily: "'Manrope',sans-serif",
                minWidth: 0,
              }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--tx-text)' }}>
                <span style={{ width: 2, height: 12, borderRadius: 1, background: C.orange }} />
                {rainWatch.count} open roof & drainage {rainWatch.count === 1 ? 'item' : 'items'}
              </span>
              <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>
                Rain {rainWatch.when} · {rainWatch.precip}% · worth a look first ›
              </span>
            </button>
          )}
        </div>
      </div>
      <div
        style={{
          height: 1,
          background: 'var(--tx-line)',
          margin: '0 20px',
          flexShrink: 0,
        }}
      />
      {loading ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 40,
            color: C.muted,
            fontSize: 12,
            fontFamily: "'Manrope',sans-serif",
          }}>
          Loading forecast…
        </div>
      ) : (
        <div style={{ flex: 1, position: 'relative', padding: '12px 20px 20px' }}>
          {/* Today column highlight */}
          <div
            style={{
              position: 'absolute',
              top: 12,
              bottom: 20,
              left: 20,
              width: 'calc((100% - 40px - 12px) / 7)',
              background: 'rgba(var(--tx-ink-rgb), 0.05)',
              borderRadius: 12,
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7,1fr)',
              gridTemplateRows: 'auto auto auto auto auto auto',
              gap: '0 2px',
              height: '100%',
              alignContent: 'space-between',
            }}>
            {/* Row 1: Day labels */}
            {days.map((d, i) => (
              <div
                key={`day-${i}`}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: 8,
                  paddingBottom: 4,
                }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: i === 0 ? 600 : 500,
                    color: i === 0 ? 'var(--tx-text)' : 'var(--tx-muted)',
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  {i === 0 ? 'Today' : d.day}
                </span>
              </div>
            ))}
            {/* Row 2: Icons */}
            {days.map((d, i) => {
              const wmo = WMO[d.code] ?? { icon: 'sun' };
              return (
                <div
                  key={`icon-${i}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 32,
                    color: i === 0 ? 'var(--tx-text)' : 'var(--tx-muted)',
                  }}>
                  {WeatherIcons[wmo.icon] ?? WeatherIcons.sun}
                </div>
              );
            })}
            {/* Row 3: Precip */}
            {days.map((d, i) => (
              <div
                key={`precip-${i}`}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingTop: 2,
                  paddingBottom: 2,
                  minHeight: 16,
                }}>
                {d.precip >= 20 && (
                  <span
                    style={{
                      fontSize: 10,
                      color: '#7ab8cc',
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                      fontFamily: "'Manrope',sans-serif",
                    }}>
                    {d.precip}%
                  </span>
                )}
              </div>
            ))}
            {/* Row 4: Hi temps */}
            {days.map((d, i) => (
              <div
                key={`hi-${i}`}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: 4,
                  paddingBottom: 4,
                }}>
                <span
                  style={{
                    fontSize: 17,
                    fontWeight: 300,
                    color: 'var(--tx-text)',
                    fontFamily: VALUE_FONT_SMALL,
                    letterSpacing: '-0.03em',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                  {d.hi}°
                </span>
              </div>
            ))}
            {/* Row 5: Bars */}
            {days.map((d, i) => {
              const barBg = `linear-gradient(to top, ${tempColor(d.lo)}, ${tempColor(d.hi)})`;
              return (
                <div
                  key={`bar-${i}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: 4,
                    paddingBottom: 4,
                  }}>
                  {/* a hairline track for the week's full range, the day's own span lit within it */}
                  <div
                    style={{
                      position: 'relative',
                      width: 3,
                      height: 48,
                      borderRadius: 1.5,
                      background: 'var(--tx-fill-2)',
                    }}>
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: `${rangeBottom(d.lo)}%`,
                        height: `${rangeHeight(d.lo, d.hi)}%`,
                        background: barBg,
                        borderRadius: 1.5,
                      }}
                    />
                  </div>
                </div>
              );
            })}
            {/* Row 6: Lo temps */}
            {days.map((d, i) => (
              <div
                key={`lo-${i}`}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: 4,
                  paddingBottom: 8,
                }}>
                <span
                  style={{
                    fontSize: 17,
                    fontWeight: 300,
                    color: 'var(--tx-muted)',
                    fontFamily: VALUE_FONT_SMALL,
                    letterSpacing: '-0.03em',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                  {d.lo}°
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HOME DNA STRIP ──────────────────────────────────────────────────────────
const URGENCY_ORDER: Record<string, number> = {
  [IssueUrgency.Critical]: 0,
  [IssueUrgency.Urgent]: 1,
  [IssueUrgency.Moderate]: 2,
  [IssueUrgency.Low]: 3,
  [IssueUrgency.Monitor]: 4,
};

// Punch List tile: urgency as shades of one neutral (Critical brightest, Monitor faintest)
const PUNCH_SHADE: Record<string, number> = {
  [IssueUrgency.Critical]: 0.95,
  [IssueUrgency.Urgent]: 0.72,
  [IssueUrgency.Moderate]: 0.52,
  [IssueUrgency.Low]: 0.36,
  [IssueUrgency.Monitor]: 0.22,
};

const URGENCY_COLORS: Record<string, string> = {
  [IssueUrgency.Critical]: C.pink,
  [IssueUrgency.Urgent]: C.orange,
  [IssueUrgency.Moderate]: C.yellow,
  [IssueUrgency.Low]: C.teal,
  [IssueUrgency.Monitor]: C.muted,
};

function getAvgCost(issue: Issue) {
  const lo = issue.costEstimateMin ?? 0;
  const hi = issue.costEstimateMax ?? 0;
  return lo > 0 && hi > 0 ? (lo + hi) / 2 : Math.max(lo, hi);
}

function HomeDNAStrip({ awardPoints }: { awardPoints: (k: string) => void }) {
  const { propertyIssues } = usePropertiesContext();
  const [hovered, setHovered] = useState<number | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [needsOpen, setNeedsOpen] = useState(false);
  const [modes, setModes] = useState<Record<string, string>>(() =>
    Object.fromEntries(REPAIRS.map((r) => [r.id, r.diy ? 'diy' : 'hire'])),
  );
  const [shopOpen, setShop] = useState<string | null>(null);
  const m = useWindowWidth() <= 768;

  const issues = propertyIssues ?? [];
  const unresolvedIssues = useMemo(
    () =>
      [...issues]
        .filter((i) => !i.resolved)
        .sort((a, b) => URGENCY_ORDER[a.urgency] - URGENCY_ORDER[b.urgency]),
    [issues],
  );

  const urgencyCounts = useMemo(() => {
    const counts: Record<string, number> = {
      Critical: 0,
      Urgent: 0,
      Moderate: 0,
      Low: 0,
      Monitor: 0,
    };
    unresolvedIssues.forEach((i) => {
      counts[i.urgency] = (counts[i.urgency] || 0) + 1;
    });
    return counts;
  }, [unresolvedIssues]);

  const totalCost = useMemo(
    () => unresolvedIssues.reduce((sum, i) => sum + getAvgCost(i), 0),
    [unresolvedIssues],
  );

  const badges = [
    {
      key: IssueUrgency.Critical,
      label: 'Critical',
      count: urgencyCounts[IssueUrgency.Critical] || 0,
      color: C.pink,
    },
    {
      key: IssueUrgency.Urgent,
      label: 'Urgent',
      count: urgencyCounts[IssueUrgency.Urgent] || 0,
      color: C.orange,
    },
    {
      key: IssueUrgency.Moderate,
      label: 'Moderate',
      count: urgencyCounts[IssueUrgency.Moderate] || 0,
      color: C.yellow,
    },
    {
      key: IssueUrgency.Low,
      label: 'Low',
      count: urgencyCounts[IssueUrgency.Low] || 0,
      color: C.teal,
    },
    {
      key: IssueUrgency.Monitor,
      label: 'Monitor',
      count: urgencyCounts[IssueUrgency.Monitor] || 0,
      color: C.muted,
    },
  ];

  const filteredIssues = active
    ? unresolvedIssues.filter((i) => i.urgency === active)
    : unresolvedIssues;

  return (
    <div style={{ ...card({ padding: '20px' }) }}>
      <div
        style={{
          display: 'flex',
          alignItems: m ? 'flex-start' : 'center',
          gap: 14,
          marginBottom: 14,
          flexDirection: m ? 'column' : 'row',
        }}>
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: C.text,
              fontFamily: "'Manrope',sans-serif",
            }}>
            HomeDNA™
          </div>
          <button
            onClick={() => {
              window.location.href = '/dashboard/inspection-report';
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontSize: 13,
              color: 'var(--tx-muted)',
              cursor: 'pointer',
              fontFamily: "'Manrope',sans-serif",
              marginTop: 4,
            }}>
            See inspection details →
          </button>
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: m ? 'flex-start' : 'flex-end',
            gap: 6,
            flexWrap: 'wrap',
          }}>
          {badges.map((b) => (
            <div
              key={b.key}
              onClick={() => setActive((f) => (f === b.key ? null : b.key))}
              style={{
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                background: active === b.key ? `${b.color}33` : `${b.color}1a`,
                color: b.color,
                border: 0,
                opacity: active && active !== b.key ? 0.35 : 1,
                fontFamily: "'Manrope',sans-serif",
                transition: 'opacity .12s',
              }}>
              {b.label.toUpperCase()}: {b.count}
            </div>
          ))}
        </div>
        {!m && (
          <div
            style={{
              display: 'flex',
              background: 'rgba(var(--tx-ink-rgb), 0.05)',
              borderRadius: '12px',
              overflow: 'hidden',
              flexShrink: 0,
            }}>
            {[
              { l: String(unresolvedIssues.length), s: '# of remarks' },
              { l: `$${Math.round(totalCost).toLocaleString()}`, s: 'Est. cost' },
            ].map((s, i) => (
              <div key={s.s} style={{ padding: '9px 14px', textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 600,
                    color: 'var(--tx-text)',
                    lineHeight: 1,
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  {s.l}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: 'var(--tx-faint)',
                    marginTop: 3,
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  {s.s}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          fontSize: 12,
          color: C.muted,
          opacity: 0.6,
          marginBottom: 10,
          fontFamily: "'Manrope',sans-serif",
        }}>
        Bar width reflects estimated cost and urgency · Click to filter
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, height: 52, width: '100%' }}>
        {filteredIssues.length === 0 ? (
          <div
            style={{
              fontSize: 12,
              color: C.muted,
              fontFamily: "'Manrope',sans-serif",
              fontStyle: 'italic',
            }}>
            No issues found
          </div>
        ) : (
          filteredIssues.map((issue, i) => {
            const col = URGENCY_COLORS[issue.urgency] ?? C.muted;
            const avgCost = getAvgCost(issue);
            const widthPct = totalCost > 0 ? (avgCost / totalCost) * 100 : 0;
            const hov = hovered === i;
            return (
              <div
                key={issue.id}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  flexGrow: Math.max(widthPct, 0.3),
                  flexShrink: 0,
                  flexBasis: 0,
                  height: 52,
                  borderRadius: 10,
                  background: col,
                  cursor: 'pointer',
                  minWidth: 2,
                  transition: 'opacity .15s',
                  opacity: hov ? 1 : 1,
                }}
              />
            );
          })
        )}
      </div>

      {/* Needs Attention accordion */}
      <div
        onClick={() => {
          setNeedsOpen((o) => {
            if (!o) awardPoints('dna_expand');
            return !o;
          });
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 16,
          padding: '11px 14px',
          background: C.bg3,
          borderRadius: '12px',
          cursor: 'pointer',
          userSelect: 'none',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: C.text,
              fontFamily: "'Manrope',sans-serif",
            }}>
            Needs attention
          </span>
          <span
            style={{
              fontSize: 10,
              padding: '2px 8px',
              borderRadius: 10,
              background: `${C.pink}1a`,
              color: C.pink,
              fontWeight: 600,
              fontFamily: "'Manrope',sans-serif",
            }}>
            {REPAIRS.filter((r) => r.sev === 'critical' || r.sev === 'urgent').length} priority
          </span>
        </div>
        <div
          style={{
            color: C.muted,
            transform: needsOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform .2s',
          }}>
          {Icon.chevron}
        </div>
      </div>

      {needsOpen && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          {REPAIRS.map((r) => {
            const mode = modes[r.id];
            const open = shopOpen === r.id;
            return (
              <div
                key={r.id}
                style={{
                  background: 'var(--tx-fill)',
                  border: 0,
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: `inset 3px 0 0 ${sevColor[r.sev]}`,
                }}>
                <div
                  style={{
                    padding: '13px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}>
                  <div style={{ flex: 1, minWidth: 0, paddingLeft: 4 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: C.text,
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      {r.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: C.muted,
                        marginTop: 2,
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      {r.cat} · {r.note}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '3px 9px',
                      borderRadius: 6,
                      flexShrink: 0,
                      fontFamily: "'Manrope',sans-serif",
                      background: `${r.isTask ? C.yellow : sevColor[r.sev]}1a`,
                      color: r.isTask ? C.yellow : sevColor[r.sev],
                    }}>
                    {r.isTask ? 'Due soon' : r.sev}
                  </span>
                  {r.cost ? (
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: C.canvas,
                        flexShrink: 0,
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      {r.cost}
                    </span>
                  ) : (
                    <span
                      style={{ fontSize: 11, color: C.muted, fontFamily: "'Manrope',sans-serif" }}>
                      Task
                    </span>
                  )}
                  {!r.isTask && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'flex',
                        background: C.bg3,
                        borderRadius: '9px',
                        overflow: 'hidden',
                        flexShrink: 0,
                      }}>
                      {['diy', 'hire'].map((mm) => (
                        <div
                          key={mm}
                          onClick={() => {
                            setModes((p) => ({ ...p, [r.id]: mm }));
                            setShop(null);
                            awardPoints('diy_toggle');
                          }}
                          style={{
                            padding: '4px 9px',
                            fontSize: 10,
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontFamily: "'Manrope',sans-serif",
                            background:
                              mode === mm ? (mm === 'diy' ? C.lime : C.bg4) : 'transparent',
                            color: mode === mm ? (mm === 'diy' ? '#111' : C.text) : C.muted,
                            transition: 'background .12s',
                          }}>
                          {mm === 'diy' ? 'DIY' : 'Hire'}
                        </div>
                      ))}
                    </div>
                  )}
                  {!r.isTask && mode === 'diy' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShop((p) => (p === r.id ? null : r.id));
                        awardPoints('shop_parts');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        background: open ? C.teal : `${C.teal}22`,
                        border: 0,
                        borderRadius: 8,
                        padding: '4px 9px',
                        fontSize: 10,
                        fontWeight: 600,
                        color: open ? '#111' : C.teal,
                        cursor: 'pointer',
                        fontFamily: "'Manrope',sans-serif",
                        flexShrink: 0,
                      }}>
                      {Icon.cart} Shop
                    </button>
                  )}
                  {!r.isTask && mode === 'hire' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        awardPoints('gen_rfq');
                      }}
                      style={{
                        background: 'var(--tx-fill-2)',
                        border: 'none',
                        borderRadius: 8,
                        padding: '4px 9px',
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'var(--tx-text)',
                        cursor: 'pointer',
                        fontFamily: "'Manrope',sans-serif",
                        flexShrink: 0,
                      }}>
                      Gen RFQ
                    </button>
                  )}
                </div>
                {!r.isTask && mode === 'diy' && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 14,
                      padding: '0 16px 10px',
                      borderTop: `1px solid ${C.border}22`,
                    }}>
                    <span
                      style={{
                        fontSize: 10,
                        color: diffColor[r.difficulty],
                        fontWeight: 600,
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      {r.difficulty}
                    </span>
                    <span
                      style={{ fontSize: 10, color: C.muted, fontFamily: "'Manrope',sans-serif" }}>
                      ⏱ {r.diyTime}
                    </span>
                    {r.saves && (
                      <span
                        style={{
                          fontSize: 10,
                          color: C.lime,
                          fontWeight: 600,
                          fontFamily: "'Manrope',sans-serif",
                        }}>
                        Save ~{r.saves} vs. hiring
                      </span>
                    )}
                  </div>
                )}
                {open && mode === 'diy' && r.products && (
                  <div
                    style={{
                      borderTop: `1px solid ${C.border}`,
                      padding: '14px 16px 16px',
                      background: C.bg2,
                      backgroundImage: 'var(--tx-surface-light)',
                      boxShadow: 'var(--tx-shadow)',
                    }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: C.muted,
                        marginBottom: 12,
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      Matched products
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${r.products.length},1fr)`,
                        gap: 10,
                      }}>
                      {r.products.map((p, pi) => (
                        <div
                          key={pi}
                          style={{
                            background: C.bg3,
                            backgroundImage: 'var(--tx-surface-light)',
                            boxShadow: 'var(--tx-shadow)',
                            border: 0,
                            borderRadius: '16px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                          }}>
                          <div
                            style={{
                              height: 80,
                              background: C.bg4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                            }}>
                            <div
                              style={{
                                position: 'absolute',
                                top: 6,
                                right: 6,
                                fontSize: 10,
                                fontWeight: 600,
                                padding: '2px 7px',
                                borderRadius: 6,
                                background: `${p.rc}22`,
                                color: p.rc,
                                fontFamily: "'Manrope',sans-serif",
                              }}>
                              {p.retailer}
                            </div>
                            <div style={{ color: C.muted, opacity: 0.3 }}>{Icon.cart}</div>
                          </div>
                          <div
                            style={{
                              padding: '10px 12px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 5,
                              flex: 1,
                            }}>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: C.text,
                                lineHeight: 1.3,
                                fontFamily: "'Manrope',sans-serif",
                              }}>
                              {p.name}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginTop: 'auto',
                              }}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: C.text,
                                  fontFamily: "'Manrope',sans-serif",
                                }}>
                                {p.price}
                              </div>
                              <div
                                style={{
                                  fontSize: 10,
                                  fontWeight: 600,
                                  color: C.lime,
                                  background: `${C.lime}15`,
                                  padding: '3px 8px',
                                  borderRadius: 6,
                                  fontFamily: "'Manrope',sans-serif",
                                }}>
                                Shop →
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── DIY INSPIRATION ─────────────────────────────────────────────────────────
function DIYInspiration() {
  const m = useWindowWidth() <= 768;
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: C.muted,
            fontFamily: "'Manrope',sans-serif",
          }}>
          DIY Inspiration
        </span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: m ? 'repeat(2,1fr)' : 'repeat(6,1fr)',
          gap: 10,
        }}>
        {DIY_INSPIRATION.map((p) => (
          <div
            key={p.id}
            style={{
              ...card({ borderRadius: 16, padding: '20px' }),
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              cursor: 'pointer',
            }}>
            <div
              style={{
                width: 34,
                height: 34,
                background: C.bg3,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: C.muted,
              }}>
              {Icon[p.icon]}
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: C.text,
                lineHeight: 1.3,
                fontFamily: "'Manrope',sans-serif",
              }}>
              {p.title}
            </div>
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: diffColor[p.diff],
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  {p.diff}
                </span>
                <span style={{ fontSize: 10, color: C.muted, fontFamily: "'Manrope',sans-serif" }}>
                  {p.time}
                </span>
              </div>
              {p.saves && (
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: C.lime,
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  Save {p.saves}
                </div>
              )}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {p.tags.map((t) => (
                  <span
                    key={t}
                    style={{
                      fontSize: 10,
                      padding: '2px 6px',
                      borderRadius: 6,
                      background: C.bg3,
                      color: C.muted,
                      fontFamily: "'Manrope',sans-serif",
                    }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CLOSE BUTTON ────────────────────────────────────────────────────────────
// the sub-pages close like Settings and the bid request page: the ds X, top-right, back to HomeHQ
function BackBtn() {
  const navigate = useNavigate();
  return (
    <CloseButton
      label="Close"
      onClose={() => navigate('/dashboard/home-hub')}
    />
  );
}

function HoverTip({ label, children }: { label: string; children: React.ReactNode }) {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--tx-glass)',
            boxShadow: 'var(--tx-shadow-sm)',
            backdropFilter: 'var(--tx-blur)',
            WebkitBackdropFilter: 'var(--tx-blur)',
            borderRadius: '9px',
            padding: '6px 10px',
            fontSize: 11,
            color: 'var(--tx-text)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 9999,
            fontFamily: "'Manrope',sans-serif",
            fontWeight: 400,
          }}>
          {label}
        </div>
      )}
    </div>
  );
}

// ─── GRID FILE TILE ──────────────────────────────────────────────────────────
function GridFileTile({
  f,
  renamingId,
  renamingExt,
  setRenamingId,
  setRenameValue,
  setRenamingExt,
  splitExt,
  renameDocumentRecord,
  onFilePreviewClick,
  onFileDownloadClick,
  onFileDeleteClick,
  C,
}: {
  f: any;
  renamingId: string | null;
  renamingExt: string;
  setRenamingId: (id: string | null) => void;
  setRenameValue: (v: string) => void;
  setRenamingExt: (v: string) => void;
  splitExt: (name: string) => { base: string; ext: string };
  renameDocumentRecord: (id: string, name: string) => void;
  onFilePreviewClick: (e: React.MouseEvent, id: string) => void;
  onFileDownloadClick: (e: React.MouseEvent, id: string) => void;
  onFileDeleteClick: (e: React.MouseEvent, id: string) => void;
  C: Record<string, string>;
}) {
  const [hovered, setHovered] = useState(false);
  const isRenaming = renamingId === f.id;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 130,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        padding: '12px 8px 10px',
        borderRadius: '12px',
        background: hovered ? `${C.border}55` : 'transparent',
        transition: 'background 0.15s',
        cursor: 'default',
        userSelect: 'none',
      }}>
      {/* Large file icon */}
      <div
        style={{
          color: 'var(--tx-muted)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <svg width="48" height="48" viewBox="0 0 16 16" fill="none">
          <rect x="3" y="1" width="10" height="14" rx="2" stroke="currentColor" strokeWidth="1" />
          <path
            d="M5 6h6M5 9h6M5 12h3"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {/* Filename (editable) */}
      <div style={{ width: '100%', textAlign: 'center' }}>
        <div
          contentEditable={isRenaming}
          suppressContentEditableWarning
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const base = (e.currentTarget.textContent ?? '').trim();
              if (base) renameDocumentRecord(f.id, base + renamingExt);
              setRenamingId(null);
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              e.currentTarget.textContent = splitExt(f.name).base;
              setRenamingId(null);
              e.currentTarget.blur();
            }
          }}
          onBlur={(e) => {
            const base = (e.currentTarget.textContent ?? '').trim();
            const newName = base + renamingExt;
            if (base && newName !== f.name) renameDocumentRecord(f.id, newName);
            setRenamingId(null);
          }}
          style={
            {
              fontSize: 11,
              color: C.text,
              fontFamily: "'Manrope',sans-serif",
              lineHeight: 1.3,
              overflow: isRenaming ? 'visible' : 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: isRenaming ? undefined : 2,
              WebkitBoxOrient: isRenaming ? undefined : 'vertical',
              wordBreak: 'break-word',
              outline: 'none',
              cursor: isRenaming ? 'text' : 'default',
              borderBottom: isRenaming ? `1px solid ${C.border}` : 'none',
              textAlign: 'center',
            } as React.CSSProperties
          }>
          {isRenaming ? splitExt(f.name).base : f.name}
        </div>
      </div>

      {/* CTAs + rename — always in flow, visible on hover */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          gap: 12,
          alignItems: 'center',
          opacity: hovered && !isRenaming ? 1 : 0,
          transition: 'opacity 0.15s',
          height: 20,
        }}>
        <HoverTip label="Rename">
          <div
            onClick={() => {
              const { base, ext } = splitExt(f.name);
              setRenamingId(f.id);
              setRenameValue(base);
              setRenamingExt(ext);
            }}
            style={{
              color: 'var(--tx-muted)',
              opacity: 0.7,
              cursor: 'pointer',
              display: 'flex',
              borderRadius: 4,
              transition: 'color 0.15s, opacity 0.15s',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.color = 'var(--tx-text)';
              el.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.color = 'var(--tx-muted)';
              el.style.opacity = '0.7';
            }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </div>
        </HoverTip>
        <HoverTip label="Preview">
          <div
            onClick={(e) => onFilePreviewClick(e, f.id)}
            style={{
              color: 'var(--tx-muted)',
              opacity: 0.7,
              cursor: 'pointer',
              display: 'flex',
              borderRadius: 4,
              transition: 'color 0.15s, opacity 0.15s',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.color = 'var(--tx-text)';
              el.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.color = 'var(--tx-muted)';
              el.style.opacity = '0.7';
            }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </div>
        </HoverTip>
        <HoverTip label="Download">
          <div
            onClick={(e) => onFileDownloadClick(e, f.id)}
            style={{
              color: 'var(--tx-muted)',
              opacity: 0.7,
              cursor: 'pointer',
              display: 'flex',
              borderRadius: 4,
              transition: 'color 0.15s, opacity 0.15s',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.color = 'var(--tx-text)';
              el.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.color = 'var(--tx-muted)';
              el.style.opacity = '0.7';
            }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.1"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M8 2v8M5 7l3 3 3-3M2 13h12" />
            </svg>
          </div>
        </HoverTip>
        <HoverTip label="Delete">
          <div
            onClick={(e) => onFileDeleteClick(e, f.id)}
            style={{
              color: 'var(--tx-muted)',
              opacity: 0.7,
              cursor: 'pointer',
              display: 'flex',
              borderRadius: 4,
              transition: 'color 0.15s, opacity 0.15s',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.color = '#ff4d4d';
              el.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLElement;
              el.style.color = 'var(--tx-muted)';
              el.style.opacity = '0.7';
            }}>
            <TrashIcon width={14} height={14} strokeWidth={2.3} />
          </div>
        </HoverTip>
      </div>
    </div>
  );
}

// ─── RECORDS PANEL (live data) ───────────────────────────────────────────────
// shorter display names for the backend's default folders (the stored names stay as they are)
const FOLDER_DISPLAY_NAMES: Record<string, string> = {
  'Home Records & Documents': 'Home Records',
};

function RecordsPanel() {
  const m = useWindowWidth() <= 768;
  const wideRecords = !m && window.innerWidth >= 1680 && window.innerHeight >= 1000;
  // the page's Upload button opens the picker in the folder view (no dialog)
  const [pickNonce, setPickNonce] = useState(0);
  const { selectedProperty } = usePropertiesContext();
  const {
    documentRecords,
    createDocumentRecord,
    deleteDocumentRecord,
    renameDocumentRecord,
    generateDocumentRecordUrl,
    generatedDocumentRecordUrl,
    isCreatingDocumentRecord,
    isDeletingDocumentRecord,
    isDocumentRecordCreated,
    isDocumentRecordDeleted,
    isDocumentRecordUrlGenerated,
  } = useDocumentRecords(selectedProperty?.property.id);
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const deleteRecordDialogRef = useRef<DeleteRecordDialogRef>(null);
  const [selectedRecord, setSelectedRecord] = useState<string | null>(null);
  const records = documentRecords ?? [];
  const [search, setSearch] = useState('');
  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set());

  const [pendingFileAction, setPendingFileAction] = useState<{
    id: string;
    action: 'preview' | 'download';
  } | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renamingExt, setRenamingExt] = useState('');

  const splitExt = (name: string) => {
    const i = name.lastIndexOf('.');
    return i > 0 ? { base: name.slice(0, i), ext: name.slice(i) } : { base: name, ext: '' };
  };

  const onFileDeleteClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedRecord(id);
    deleteRecordDialogRef.current?.open();
  };

  const onFilePreviewClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setPendingFileAction({ id, action: 'preview' });
    generateDocumentRecordUrl(id);
  };

  const onFileDownloadClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setPendingFileAction({ id, action: 'download' });
    generateDocumentRecordUrl(id);
  };

  useEffect(() => {
    if (isDocumentRecordCreated) {
      showAlert(t('file.uploaded'), 'success');
    }
  }, [isDocumentRecordCreated]);

  useEffect(() => {
    if (isDocumentRecordDeleted) {
      deleteRecordDialogRef.current?.close();
      setSelectedRecord(null);
      showAlert(t('file.deleted'), 'success');
    }
  }, [isDocumentRecordDeleted]);

  useEffect(() => {
    if (!isDocumentRecordUrlGenerated || !generatedDocumentRecordUrl || !pendingFileAction) return;
    if (pendingFileAction.action === 'preview') {
      window.open(generatedDocumentRecordUrl, '_blank');
    } else {
      const filename = records.find((r) => r.id === pendingFileAction.id)?.name ?? 'file';
      fetch(generatedDocumentRecordUrl)
        .then((res) => res.blob())
        .then((blob) => {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = filename;
          a.click();
          URL.revokeObjectURL(a.href);
        });
    }
    setPendingFileAction(null);
  }, [isDocumentRecordUrlGenerated, generatedDocumentRecordUrl]);

  // Group records into folders (by parentId) and files
  const folders = useMemo(() => {
    const folderRecords = records.filter((r: any) => r.type === 'folder');
    const fileRecords = records.filter((r: any) => r.type === 'file');
    return folderRecords.map((folder: any) => ({
      id: folder.id,
      label: FOLDER_DISPLAY_NAMES[folder.name] ?? folder.name,
      files: fileRecords.filter((f: any) => f.parentId === folder.id),
    }));
  }, [records]);

  // Also show ungrouped files
  const ungroupedFiles = useMemo(() => {
    const folderIds = new Set(
      records.filter((r: any) => r.type === 'folder').map((r: any) => r.id),
    );
    return records.filter(
      (r: any) => r.type === 'file' && (!r.parentId || !folderIds.has(r.parentId)),
    );
  }, [records]);

  const allFolders = useMemo(() => {
    const result = [...folders];
    if (ungroupedFiles.length > 0) {
      result.push({ id: '_ungrouped', label: 'Other files', files: ungroupedFiles });
    }
    return result;
  }, [folders, ungroupedFiles]);


  const totalFiles = records.filter((r: any) => r.type === 'file').length;

  const filtered = allFolders
    .map((f) => ({
      ...f,
      files: f.files.filter(
        (fi: any) => !search || fi.name.toLowerCase().includes(search.toLowerCase()),
      ),
    }))
    .filter((f) => !search || f.files.length > 0);

  const fmtDate = (ts: number) =>
    new Date(ts * 1000).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  return (
    <div
      style={{
        margin: '0 auto',
        paddingTop: m ? 0 : 20,
        paddingBottom: 20,
        paddingLeft: m ? 10 : 0,
        paddingRight: m ? 10 : 0,
        fontFamily: "'Manrope',sans-serif",
      }}>
      <div
        style={{
          ...card(),
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: m ? 'calc(100dvh - 104px)' : 'calc(100dvh - 40px)',
          position: 'relative',
          overflow: 'hidden',
        }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: m ? 10 : 14,
            // 27": more air above and around the title and search, so the folder row isn't crowded
            padding: wideRecords ? '30px 28px 22px' : '20px 20px 16px',
            flexShrink: 0,
          }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 17,
                fontWeight: 300,
                color: C.text,
                fontFamily: "'Manrope',sans-serif",
              }}>
              Records & files
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--tx-muted)',
                marginTop: 4,
                fontFamily: "'Manrope',sans-serif",
              }}>
              {totalFiles} {totalFiles === 1 ? 'file' : 'files'} across {allFolders.length}{' '}
              {allFolders.length === 1 ? 'folder' : 'folders'}
            </div>
          </div>
          {/* the same action as the folder's "Add a file" sleeve: its round + mark and its words, on a tonal button */}
          <button
            type="button"
            className="hx-add-file"
            onClick={() => setPickNonce((n) => n + 1)}
            aria-label="Add a file"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              height: 36,
              padding: m ? '0 4px' : '0 14px 0 4px',
              flexShrink: 0,
              border: 0,
              borderRadius: 10,
              background: 'var(--tx-fill)',
              color: 'var(--tx-text)',
              fontFamily: "'Manrope',sans-serif",
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background 0.15s ease',
            }}>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                background: 'var(--tx-fill-2)',
                color: 'var(--tx-muted)',
              }}>
              <Plus size={15} strokeWidth={1.8} />
            </span>
            {!m && 'Add a file'}
          </button>
          <BackBtn />
        </div>
        <div
          style={{
            padding: wideRecords ? '0 28px 12px' : '0 20px 16px',
            flexShrink: 0,
            display: 'flex',
            gap: 10,
            alignItems: 'center',
          }}>
          <div
            style={{
              flex: 1,
              background: 'var(--tx-fill)',
              border: 0,
              borderRadius: '12px',
              padding: '0 16px',
              height: 40,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="7" cy="7" r="4.5" stroke={C.muted} strokeWidth="1.3" />
              <path d="M11 11l3 3" stroke={C.muted} strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files and folders…"
              style={{
                background: 'none',
                border: 'none',
                outline: 'none',
                fontSize: 13,
                color: C.text,
                fontFamily: "'Manrope',sans-serif",
                flex: 1,
              }}
            />
          </div>
        </div>
        {/* single screen: the slider and the viewer fill the card; the viewer scrolls inside itself */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: wideRecords ? '0 28px 28px' : '0 20px 20px',
          }}>
          {filtered.length === 0 && (
            <div
              style={{
                padding: 20,
                textAlign: 'center',
                color: C.muted,
                fontSize: 13,
                fontFamily: "'Manrope',sans-serif",
              }}>
              No records found
            </div>
          )}
          {/* the folders ride a slider along the top; the open one's contents sit below in a viewer of the same materials */}
          {filtered.length > 0 && (
            <RecordsBrowser
              folders={filtered}
              activeId={
                filtered.find((f) => openFolders.has(f.id))?.id ??
                (filtered.find((f) => f.files.length > 0) ?? filtered[0]).id
              }
              onSelect={(id) => setOpenFolders(new Set([id]))}
              onPreview={onFilePreviewClick}
              onDownload={onFileDownloadClick}
              onDelete={onFileDeleteClick}
              onRename={(id, name) => renameDocumentRecord(id, name)}
              propertyId={selectedProperty?.property.id}
              onCreate={createDocumentRecord}
              pickNonce={pickNonce}
              isMobile={m}
            />
          )}
        </div>
        <DeleteRecordDialog
          ref={deleteRecordDialogRef}
          fileName={records.find((r) => r.id === selectedRecord)?.name ?? ''}
          onConfirm={() => selectedRecord && deleteDocumentRecord(selectedRecord)}
          onClose={() => setSelectedRecord(null)}
          isLoading={isDeletingDocumentRecord}
        />
      </div>
    </div>
  );
}

// ─── DIRECTORY PANEL (live data) ─────────────────────────────────────────────
function DirectoryPanel() {
  const m = useWindowWidth() <= 768;
  const { selectedProperty } = usePropertiesContext();
  const { contacts, createContact, isCreatingContact, isContactCreated } = useContacts(
    selectedProperty?.property.id,
  );
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const createContactDialogRef = useRef<EditContactDialogRef>(null);
  const allContacts = contacts ?? [];
  const [openCats, setOpenCats] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const toggleCat = (id: string) =>
    setOpenCats((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  useEffect(() => {
    if (isContactCreated) {
      createContactDialogRef.current?.close();
      showAlert(t('contact.added'), 'success');
    }
  }, [isContactCreated]);

  const categories = useMemo(() => {
    const cats = allContacts.filter((c) => c.type === ContactType.Category);
    const individuals = allContacts.filter((c) => c.type === ContactType.Individual);
    return cats.map((cat) => ({
      id: cat.id,
      category: cat.name,
      contacts: individuals.filter((ind) => ind.parentId === cat.id),
    }));
  }, [allContacts]);

  const totalContacts = allContacts.filter((c) => c.type === ContactType.Individual).length;

  const allCatsExpanded = categories.length > 0 && categories.every((c) => openCats.has(c.id));
  const toggleAllCats = () =>
    setOpenCats(allCatsExpanded ? new Set() : new Set(categories.map((c) => c.id)));

  const filtered = categories
    .map((c) => ({
      ...c,
      contacts: c.contacts
        .filter((ct) => !search || ct.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) =>
          sortDir === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name),
        ),
    }))
    .filter((c) => !search || c.contacts.length > 0)
    .sort((a, b) =>
      sortDir === 'asc'
        ? a.category.localeCompare(b.category)
        : b.category.localeCompare(a.category),
    );

  return (
    <div
      style={{
        margin: '0 auto',
        paddingTop: m ? 0 : 20,
        paddingBottom: 20,
        paddingLeft: m ? 10 : 0,
        paddingRight: m ? 10 : 0,
        fontFamily: "'Manrope',sans-serif",
      }}>
      <div
        style={{
          ...card(),
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: m ? 'calc(100dvh - 104px)' : 'calc(100dvh - 40px)',
        }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: m ? 10 : 14,
            padding: '20px 20px 16px',
            flexShrink: 0,
          }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: C.text,
                fontFamily: "'Manrope',sans-serif",
              }}>
              Directory
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--tx-muted)',
                marginTop: 4,
                fontFamily: "'Manrope',sans-serif",
              }}>
              {totalContacts} {totalContacts === 1 ? 'contact' : 'contacts'} across{' '}
              {categories.length} {categories.length === 1 ? 'category' : 'categories'}
            </div>
          </div>
          <button
            onClick={() => createContactDialogRef.current?.open()}
            style={{ ...btn.primary, flexShrink: 0 }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-text)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-muted)';
            }}>
            <span style={{ color: C.lime, display: 'flex' }}>
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                <path
                  d="M6 1v10M1 6h10"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            {m ? 'Add' : 'Add Contractor'}
          </button>
          <BackBtn />
        </div>
        <div
          style={{
            padding: '0 20px 16px',
            flexShrink: 0,
            display: 'flex',
            gap: 8,
            alignItems: 'center',
          }}>
          <div
            style={{
              flex: 1,
              height: 40,
              background: 'var(--tx-fill)',
              border: 0,
              borderRadius: '12px',
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <circle cx="7" cy="7" r="4.5" stroke={C.muted} strokeWidth="1.3" />
              <path d="M11 11l3 3" stroke={C.muted} strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contractors…"
              style={{
                background: 'none',
                border: 'none',
                outline: 'none',
                fontSize: 13,
                color: C.text,
                fontFamily: "'Manrope',sans-serif",
                flex: 1,
              }}
            />
          </div>
          <HoverTip label={sortDir === 'asc' ? 'Sort Z → A' : 'Sort A → Z'}>
            <button
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              style={{
                background: 'var(--tx-fill)',
                border: 0,
                borderRadius: 8,
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: C.muted,
                flexShrink: 0,
              }}>
              <SortCaret active dir={sortDir} />
            </button>
          </HoverTip>
          <HoverTip label={allCatsExpanded ? 'Collapse all' : 'Expand all'}>
            <button
              onClick={toggleAllCats}
              style={{
                background: 'var(--tx-fill)',
                border: 0,
                borderRadius: 8,
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: C.muted,
                flexShrink: 0,
              }}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round">
                {allCatsExpanded ? (
                  <>
                    <polyline points="4 14 10 14 10 20" />
                    <polyline points="20 10 14 10 14 4" />
                    <line x1="10" y1="14" x2="3" y2="21" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                  </>
                ) : (
                  <>
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </>
                )}
              </svg>
            </button>
          </HoverTip>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 20px' }}>
          {filtered.length === 0 && (
            <div
              style={{
                padding: 20,
                textAlign: 'center',
                color: C.muted,
                fontSize: 13,
                fontFamily: "'Manrope',sans-serif",
              }}>
              No contacts found
            </div>
          )}
          {filtered.map((cat) => (
            <div
              key={cat.id}
              style={{
                background: C.bg2,
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                border: 0,
                borderRadius: '16px',
                overflow: 'hidden',
                marginBottom: 8,
              }}>
              <div
                onClick={() => toggleCat(cat.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '14px 18px',
                  cursor: 'pointer',
                }}>
                <div style={{ color: C.muted }}>{Icon.users}</div>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                    flex: 1,
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  {cat.category}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    background: C.bg3,
                    padding: '2px 8px',
                    borderRadius: '12px',
                    color: C.muted,
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  {cat.contacts.length} {cat.contacts.length === 1 ? 'contact' : 'contacts'}
                </span>
                <div
                  style={{
                    color: C.muted,
                    transform: openCats.has(cat.id) ? 'rotate(180deg)' : 'none',
                    transition: 'transform .2s',
                  }}>
                  {Icon.chevron}
                </div>
              </div>
              {openCats.has(cat.id) && (
                <div style={{ borderTop: `1px solid ${C.border}` }}>
                  {cat.contacts.length === 0 ? (
                    <div
                      style={{
                        padding: '16px 18px',
                        fontSize: 12,
                        color: C.muted,
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      No contractors added yet.
                    </div>
                  ) : (
                    cat.contacts.map((ct, i) => (
                      <div
                        key={ct.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 14,
                          padding: '14px 18px',
                          borderBottom:
                            i < cat.contacts.length - 1 ? `1px solid ${C.border}22` : 'none',
                        }}>
                        {/* Avatar */}
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: '50%',
                            background: C.bg4,
                            border: `1px solid ${C.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            fontWeight: 600,
                            color: C.canvas,
                            fontFamily: "'Manrope',sans-serif",
                            flexShrink: 0,
                          }}>
                          {ct.name
                            .split(' ')
                            .map((w) => w[0])
                            .slice(0, 2)
                            .join('')}
                        </div>
                        {/* Name + company */}
                        <div style={{ flex: '0 0 auto', minWidth: 120, maxWidth: 160 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: C.text,
                              fontFamily: "'Manrope',sans-serif",
                            }}>
                            {ct.name}
                          </div>
                          {ct.companyName && (
                            <div
                              style={{
                                fontSize: 12,
                                color: C.muted,
                                marginTop: 2,
                                fontFamily: "'Manrope',sans-serif",
                              }}>
                              {ct.companyName}
                            </div>
                          )}
                        </div>
                        {/* Contact details */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                          {ct.emailAddress && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 7,
                                fontSize: 12,
                                color: C.muted,
                                fontFamily: "'Manrope',sans-serif",
                              }}>
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ flexShrink: 0 }}>
                                <rect x="2" y="4" width="20" height="16" rx="2" />
                                <path d="M2 7l10 7 10-7" />
                              </svg>
                              {ct.emailAddress}
                            </div>
                          )}
                          {ct.phoneNumber && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 7,
                                fontSize: 12,
                                color: C.muted,
                                fontFamily: "'Manrope',sans-serif",
                              }}>
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ flexShrink: 0 }}>
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.06 6.06l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                              </svg>
                              {formatPhone(ct.phoneNumber)}
                            </div>
                          )}
                        </div>
                        {/* CTA icons */}
                        <div
                          style={{ display: 'flex', gap: 10, flexShrink: 0, alignItems: 'center' }}>
                          {ct.phoneNumber && (
                            <HoverTip label="Call">
                              <a
                                href={`tel:${ct.phoneNumber}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 32,
                                  height: 32,
                                  borderRadius: '9px',
                                  background: 'var(--tx-fill)',
                                  border: 0,
                                  color: 'var(--tx-muted)',
                                  transition: 'color 0.15s, background 0.15s',
                                }}
                                onMouseEnter={(e) => {
                                  const el = e.currentTarget as HTMLElement;
                                  el.style.color = 'var(--tx-text)';
                                  el.style.background = 'var(--tx-fill-2)';
                                }}
                                onMouseLeave={(e) => {
                                  const el = e.currentTarget as HTMLElement;
                                  el.style.color = 'var(--tx-muted)';
                                  el.style.background = 'var(--tx-fill)';
                                }}>
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round">
                                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.06 6.06l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                                </svg>
                              </a>
                            </HoverTip>
                          )}
                          {ct.emailAddress && (
                            <HoverTip label="Email">
                              <a
                                href={`mailto:${ct.emailAddress}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: 32,
                                  height: 32,
                                  borderRadius: '9px',
                                  background: 'var(--tx-fill)',
                                  border: 0,
                                  color: 'var(--tx-muted)',
                                  transition: 'color 0.15s, background 0.15s',
                                }}
                                onMouseEnter={(e) => {
                                  const el = e.currentTarget as HTMLElement;
                                  el.style.color = 'var(--tx-text)';
                                  el.style.background = 'var(--tx-fill-2)';
                                }}
                                onMouseLeave={(e) => {
                                  const el = e.currentTarget as HTMLElement;
                                  el.style.color = 'var(--tx-muted)';
                                  el.style.background = 'var(--tx-fill)';
                                }}>
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round">
                                  <rect x="2" y="4" width="20" height="16" rx="2" />
                                  <path d="M2 7l10 7 10-7" />
                                </svg>
                              </a>
                            </HoverTip>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <EditContactDialog
          ref={createContactDialogRef}
          selectedContact={undefined}
          onSubmit={createContact}
          isLoading={isCreatingContact}
        />
      </div>
    </div>
  );
}

// ─── CALENDAR PANEL (live data) ──────────────────────────────────────────────
const CAL_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const CAL_DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
function calGrid(y: number, m: number) {
  const f = new Date(y, m, 1).getDay(),
    t = new Date(y, m + 1, 0).getDate();
  return [...Array(f).fill(null), ...Array.from({ length: t }, (_, i) => i + 1)];
}

const EVENT_COLORS = [C.teal, C.orange, C.lime, C.yellow, C.pink];

function CalendarPanel() {
  const m = useWindowWidth() <= 768;
  const { selectedProperty } = usePropertiesContext();
  const {
    allCalendarEntries,
    createCalendarEvent,
    createCalendarReminder,
    updateCalendarEvent,
    deleteCalendarEvent,
    updateCalendarReminder,
    deleteCalendarReminder,
    isCreatingCalendarEvent,
    isCreatingCalendarReminder,
    isUpdatingCalendarEvent,
    isUpdatingCalendarReminder,
    isCalendarEventCreated,
    isCalendarEventUpdated,
    isCalendarReminderCreated,
    isCalendarReminderUpdated,
  } = useCalendarEntries(selectedProperty?.property.id);
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const createEventDialogRef = useRef<EditCalendarEventDialogRef>(null);
  const createEventButtonRef = useRef<HTMLButtonElement>(null);
  const createReminderDialogRef = useRef<EditCalendarReminderDialogRef>(null);
  const editEventDialogRef = useRef<EditCalendarEventDialogRef>(null);
  const editReminderDialogRef = useRef<EditCalendarReminderDialogRef>(null);
  const [selectedEntryForEdit, setSelectedEntryForEdit] = useState<GenericCalendarEntry | null>(
    null,
  );

  const [colorOverrides, setColorOverrides] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem('hauser:event-colors') ?? '{}');
    } catch {
      return {};
    }
  });
  const [createColor, setCreateColor] = useState<string | undefined>(undefined);
  const [editColor, setEditColor] = useState<string | undefined>(undefined);
  const pendingColorRef = useRef<string | undefined>(undefined);
  const prevEntryIdsRef = useRef<Set<string>>(new Set(allCalendarEntries?.map((e) => e.id) ?? []));
  const [taskTick, setTaskTick] = useState(0);
  useEffect(() => {
    const handler = () => setTaskTick((t) => t + 1);
    window.addEventListener('hauser:tasks-updated', handler);
    return () => window.removeEventListener('hauser:tasks-updated', handler);
  }, []);

  const taskEntries = useMemo<GenericCalendarEntry[]>(() => {
    const propertyId = selectedProperty?.property.id;
    if (!propertyId || !selectedProperty?.isOwned || typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(`homehq:owner-tasks:${propertyId}`);
      if (!raw) return [];
      const tasks = JSON.parse(raw);
      if (!Array.isArray(tasks)) return [];
      return tasks
        .filter((t: { date?: string | null; done?: boolean }) => t?.date && !t.done)
        .map((t: { id: string; text: string; date: string }) => {
          const d = new Date(t.date.includes('T') ? t.date : `${t.date}T00:00:00`);
          const ts = d.getTime();
          const tsSec = Math.floor(ts / 1000);
          return {
            id: `task-${t.id}`,
            name: t.text,
            startDate: tsSec,
            endDate: tsSec,
            type: CalendarEntryType.Task,
            userId: '',
            propertyId,
            createdAt: tsSec,
            updatedAt: tsSec,
          } satisfies GenericCalendarEntry;
        });
    } catch {
      return [];
    }
  }, [selectedProperty?.property.id, taskTick]);

  const entries = [...(allCalendarEntries ?? []), ...taskEntries];

  useEffect(() => {
    if (isCalendarEventCreated) {
      createEventDialogRef.current?.close();
      showAlert(t('event.created'), 'success');
      setCreateColor(undefined);
    }
  }, [isCalendarEventCreated]);

  useEffect(() => {
    if (isCalendarReminderCreated) {
      createReminderDialogRef.current?.close();
      showAlert(t('reminder.created'), 'success');
    }
  }, [isCalendarReminderCreated]);

  // Detect newly added entries and apply any pending color override
  useEffect(() => {
    if (!allCalendarEntries) return;
    const currentIds = new Set(allCalendarEntries.map((e) => e.id));
    if (pendingColorRef.current) {
      const newId = [...currentIds].find((id) => !prevEntryIdsRef.current.has(id));
      if (newId) {
        saveColorOverride(newId, pendingColorRef.current);
        pendingColorRef.current = undefined;
      }
    }
    prevEntryIdsRef.current = currentIds;
  }, [allCalendarEntries]);

  useEffect(() => {
    if (isCalendarEventUpdated) {
      editEventDialogRef.current?.close();
      if (selectedEntryForEdit && editColor) saveColorOverride(selectedEntryForEdit.id, editColor);
    }
  }, [isCalendarEventUpdated]);

  useEffect(() => {
    if (isCalendarReminderUpdated) editReminderDialogRef.current?.close();
  }, [isCalendarReminderUpdated]);

  const entryColor = (entry: GenericCalendarEntry) => {
    if (entry.type === CalendarEntryType.Task) return C.lime;
    if (colorOverrides[entry.id]) return colorOverrides[entry.id];
    let h = 0;
    for (let i = 0; i < entry.id.length; i++) h = (h * 31 + entry.id.charCodeAt(i)) & 0xffff;
    return EVENT_COLORS[h % EVENT_COLORS.length];
  };

  const saveColorOverride = (id: string, color: string) => {
    const updated = { ...colorOverrides, [id]: color };
    setColorOverrides(updated);
    localStorage.setItem('hauser:event-colors', JSON.stringify(updated));
  };

  const handleEditEntry = (entry: GenericCalendarEntry) => {
    setSelectedEntryForEdit(entry);
    setEditColor(colorOverrides[entry.id]);
    if (entry.type === CalendarEntryType.Event) editEventDialogRef.current?.open();
    else if (entry.type === CalendarEntryType.Reminder) editReminderDialogRef.current?.open();
  };

  const handleDeleteEntry = (entry: GenericCalendarEntry) => {
    if (entry.type === CalendarEntryType.Event) deleteCalendarEvent(entry.id);
    else if (entry.type === CalendarEntryType.Reminder) deleteCalendarReminder(entry.id);
  };

  const now = new Date();
  const [view, setView] = useState<'month' | 'week' | '3day'>('month');
  const [anchor, setAnchor] = useState<Date>(new Date());
  const [sel, setSel] = useState<number | null>(null);
  const [hoveredUpcomingId, setHoveredUpcomingId] = useState<string | null>(null);
  const [showUpcoming, setShowUpcoming] = useState(true);
  const month = anchor.getMonth();
  const year = anchor.getFullYear();
  const cells = calGrid(year, month);
  const prev = () => {
    setAnchor((prevAnchor) => {
      const d = new Date(prevAnchor);
      if (view === 'month') d.setMonth(d.getMonth() - 1);
      else if (view === 'week') d.setDate(d.getDate() - 7);
      else d.setDate(d.getDate() - 3);
      return d;
    });
  };
  const next = () => {
    setAnchor((prevAnchor) => {
      const d = new Date(prevAnchor);
      if (view === 'month') d.setMonth(d.getMonth() + 1);
      else if (view === 'week') d.setDate(d.getDate() + 7);
      else d.setDate(d.getDate() + 3);
      return d;
    });
  };

  // Compute date range for week/3day views
  const viewDays: Date[] = useMemo(() => {
    if (view === 'month') return [];
    const start = new Date(anchor);
    start.setHours(0, 0, 0, 0);
    if (view === 'week') start.setDate(start.getDate() - start.getDay()); // Sunday
    const days: Date[] = [];
    const count = view === 'week' ? 7 : 3;
    for (let i = 0; i < count; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return days;
  }, [view, anchor]);

  const eventsForDay = (d: number) =>
    entries.filter((e) => {
      const eDate = new Date(e.startDate * 1000);
      return eDate.getDate() === d && eDate.getMonth() === month && eDate.getFullYear() === year;
    });

  const eventsForDate = (date: Date) =>
    entries
      .filter((e) => {
        const eDate = new Date(e.startDate * 1000);
        return (
          eDate.getDate() === date.getDate() &&
          eDate.getMonth() === date.getMonth() &&
          eDate.getFullYear() === date.getFullYear()
        );
      })
      .sort((a, b) => a.startDate - b.startDate);

  const rangeLabel = () => {
    if (view === 'month') return `${CAL_MONTHS[month]} ${year}`;
    if (view === 'week') {
      const s = viewDays[0];
      const e = viewDays[viewDays.length - 1];
      if (!s || !e) return '';
      const sameMonth = s.getMonth() === e.getMonth();
      return sameMonth
        ? `${CAL_MONTHS[s.getMonth()]} ${s.getDate()}–${e.getDate()}, ${e.getFullYear()}`
        : `${CAL_MONTHS[s.getMonth()]} ${s.getDate()} – ${
            CAL_MONTHS[e.getMonth()]
          } ${e.getDate()}, ${e.getFullYear()}`;
    }
    const s = viewDays[0];
    const e = viewDays[viewDays.length - 1];
    if (!s || !e) return '';
    const sameMonth = s.getMonth() === e.getMonth();
    return sameMonth
      ? `${CAL_MONTHS[s.getMonth()]} ${s.getDate()}–${e.getDate()}, ${e.getFullYear()}`
      : `${CAL_MONTHS[s.getMonth()]} ${s.getDate()} – ${
          CAL_MONTHS[e.getMonth()]
        } ${e.getDate()}, ${e.getFullYear()}`;
  };

  const isSameDay = (a: Date, b: Date) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear();

  const upcoming = useMemo(
    () =>
      [...entries]
        .filter((e) => e.startDate * 1000 >= Date.now())
        .sort((a, b) => a.startDate - b.startDate)
        .slice(0, 6),
    [entries],
  );

  const fmtEntryDate = (ts: number) => {
    const d = new Date(ts * 1000);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const fmtEntryTime = (ts: number) => {
    const d = new Date(ts * 1000);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const today = now.getDate();
  const isCurrentMonth = month === now.getMonth() && year === now.getFullYear();

  return (
    <div
      style={{
        margin: '0 auto',
        paddingTop: m ? 0 : 20,
        paddingBottom: 20,
        paddingLeft: m ? 10 : 0,
        paddingRight: m ? 10 : 0,
        fontFamily: "'Manrope',sans-serif",
      }}>
      <div
        style={{
          ...card(),
          padding: 0,
          display: 'flex',
          flexDirection: m ? 'column' : 'row',
          height: m ? 'calc(100dvh - 104px)' : 'calc(100dvh - 40px)',
        }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
            overflow: 'hidden',
          }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: m ? 8 : 14,
              marginBottom: 20,
              flexWrap: 'wrap',
            }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 400,
                  letterSpacing: '-0.015em',
                  color: C.text,
                  fontFamily: "'Manrope',sans-serif",
                }}>
                Calendar
              </div>
            </div>
            <button
              ref={createEventButtonRef}
              onClick={() => createEventDialogRef.current?.open()}
              style={{ ...btn.primary, flexShrink: 0 }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)';
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-text)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)';
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-muted)';
              }}>
              <span style={{ color: C.lime, display: 'flex' }}>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M6 1v10M1 6h10"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              {m ? 'Event' : 'Create Event'}
            </button>

            {(m || !showUpcoming) && <BackBtn />}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
              flexWrap: 'wrap',
              gap: 10,
            }}>
            {/* the period on show: a light figure-style heading, not a bold one (card titles are 17/400) */}
            <div
              style={{
                fontSize: 22,
                fontWeight: 300,
                letterSpacing: '-0.03em',
                color: C.text,
                fontFamily: "'Manrope',sans-serif",
              }}>
              {rangeLabel()}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {/* View switcher */}
              <div
                style={{
                  display: 'inline-flex',
                  background: 'var(--tx-fill)',
                  border: 0,
                  borderRadius: '12px',
                  padding: 2,
                }}>
                {(['month', 'week', '3day'] as const).map((v) => (
                  <button
                    key={v}
                    onClick={() => setView(v)}
                    style={{
                      background: view === v ? C.bg4 : 'transparent',
                      border: 'none',
                      color: view === v ? C.text : 'var(--tx-muted)',
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '5px 10px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontFamily: "'Manrope',sans-serif",
                    }}>
                    {v === '3day' ? '3-Day' : v}
                  </button>
                ))}
              </div>
              <button
                onClick={prev}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '12px',
                  background: 'var(--tx-fill)',
                  border: 0,
                  color: C.muted,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M8 2L4 6l4 4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <button
                onClick={next}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '12px',
                  background: 'var(--tx-fill)',
                  border: 0,
                  color: C.muted,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M4 2l4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
          {view === 'month' && (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7,1fr)',
                  gap: 3,
                  marginBottom: 3,
                }}>
                {CAL_DOW.map((d) => (
                  <div
                    key={d}
                    style={{
                      fontSize: 10,
                      color: C.muted,
                      textAlign: 'center',
                      fontWeight: 600,
                      padding: '4px 0',
                      fontFamily: "'Manrope',sans-serif",
                    }}>
                    {d}
                  </div>
                ))}
              </div>
              <div
                style={{
                  flex: 1,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7,1fr)',
                  gridTemplateRows: `repeat(${Math.ceil(cells.length / 7)},1fr)`,
                  gap: 3,
                }}>
                {cells.map((d, i) => {
                  if (!d) return <div key={i} />;
                  const isToday = isCurrentMonth && d === today;
                  const evs = eventsForDay(d);
                  const isSel = sel === d;
                  return (
                    <div
                      key={i}
                      onClick={() => setSel(d === sel ? null : d)}
                      style={{
                        padding: '8px 10px',
                        borderRadius: 10,
                        cursor: 'pointer',
                        background: isSel ? C.bg3 : isToday ? `#bbb7af18` : C.bg2,
                        border: `1px solid ${isSel ? '#555' : isToday ? `#bbb7af` : C.border}`,
                        overflow: 'hidden',
                      }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: isToday || evs.length > 0 ? 600 : 400,
                          color: isToday ? 'var(--tx-muted)' : evs.length > 0 ? C.text : C.muted,
                          fontFamily: "'Manrope',sans-serif",
                          marginBottom: 4,
                        }}>
                        {d}
                      </div>
                      {evs.slice(0, 2).map((ev) => {
                        const evColor = entryColor(ev);
                        const evDate = new Date(ev.startDate * 1000);
                        const pillHasTime = !(evDate.getHours() === 0 && evDate.getMinutes() === 0);
                        return (
                          <Tooltip
                            key={ev.id}
                            placement="top"
                            enterDelay={300}
                            title={
                              <div style={{ fontFamily: "'Manrope',sans-serif", lineHeight: 1.5 }}>
                                <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                                  {ev.name}
                                </div>
                                <div style={{ fontSize: 11, opacity: 0.8 }}>
                                  {fmtEntryDate(ev.startDate)}
                                  {pillHasTime ? ` · ${fmtEntryTime(ev.startDate)}` : ''}
                                  {ev.endDate && ev.endDate !== ev.startDate && (
                                    <>
                                      {' '}
                                      → {fmtEntryDate(ev.endDate)}
                                      {pillHasTime ? ` · ${fmtEntryTime(ev.endDate)}` : ''}
                                    </>
                                  )}
                                </div>
                              </div>
                            }>
                            <div
                              style={{
                                fontSize: 10,
                                padding: '2px 5px',
                                borderRadius: 4,
                                background: `${evColor}22`,
                                color: evColor,
                                fontFamily: "'Manrope',sans-serif",
                                marginTop: 2,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>
                              {ev.name}
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Week & 3-Day views */}
          {view !== 'month' && (
            <div
              style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: `repeat(${viewDays.length},1fr)`,
                gap: 4,
                overflow: 'hidden',
              }}>
              {viewDays.map((date, idx) => {
                const isToday = isSameDay(date, now);
                const evs = eventsForDate(date);
                return (
                  <div
                    key={idx}
                    style={{
                      background: isToday ? `#bbb7af18` : C.bg2,
                      border: `1px solid ${isToday ? `#bbb7af` : C.border}`,
                      borderRadius: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                    }}>
                    <div
                      style={{
                        padding: '10px',
                        borderBottom: `1px solid ${C.border}`,
                        textAlign: 'center',
                      }}>
                      <div
                        style={{
                          fontSize: 10,
                          color: isToday ? 'var(--tx-muted)' : C.muted,
                          fontFamily: "'Manrope',sans-serif",
                          fontWeight: 600,
                        }}>
                        {date.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 600,
                          color: isToday ? `#e8e5e1` : C.text,
                          fontFamily: "'Manrope',sans-serif",
                          marginTop: 2,
                        }}>
                        {date.getDate()}
                      </div>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4,
                      }}>
                      {evs.length === 0 ? (
                        <div
                          style={{
                            fontSize: 10,
                            color: C.muted,
                            fontFamily: "'Manrope',sans-serif",
                            textAlign: 'center',
                            padding: '10px 4px',
                            fontStyle: 'italic',
                          }}>
                          No events
                        </div>
                      ) : (
                        evs.map((ev, ei, arr) => {
                          const evColor = entryColor(ev);
                          const d = new Date(ev.startDate * 1000);
                          const hasTime = !(d.getHours() === 0 && d.getMinutes() === 0);
                          const isTask = ev.type === CalendarEntryType.Task;
                          return (
                            <div
                              key={ev.id}
                              style={{
                                display: 'flex',
                                alignItems: 'stretch',
                                gap: 8,
                                padding: '6px 0',
                                borderBottom:
                                  ei < arr.length - 1 ? `1px solid ${C.border}44` : 'none',
                              }}>
                              <div
                                style={{
                                  width: 3,
                                  borderRadius: 2,
                                  background: evColor,
                                  flexShrink: 0,
                                }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                {hasTime && (
                                  <div
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 600,
                                      color: evColor,
                                      fontFamily: "'Manrope',sans-serif",
                                      marginBottom: 1,
                                    }}>
                                    {fmtEntryTime(ev.startDate)}
                                  </div>
                                )}
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: C.text,
                                    fontFamily: "'Manrope',sans-serif",
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}>
                                  {ev.name}
                                </div>
                              </div>
                              {!isTask && (
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: 2,
                                    flexShrink: 0,
                                    alignItems: 'center',
                                  }}>
                                  <button
                                    onClick={() => handleEditEntry(ev)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: C.muted,
                                      padding: '2px',
                                      lineHeight: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                    title="Edit">
                                    <EditIcon style={{ fontSize: 12 }} />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteEntry(ev)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: C.muted,
                                      padding: '2px',
                                      lineHeight: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                    title="Delete">
                                    <DeleteIcon style={{ fontSize: 12 }} />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div
          style={{
            width: m ? '100%' : showUpcoming ? 260 : 40,
            flexShrink: 0,
            borderLeft: m ? 'none' : `1px solid ${C.border}`,
            borderTop: m ? `1px solid ${C.border}` : 'none',
            padding: showUpcoming ? '20px' : '12px 0',
            overflowY: showUpcoming ? 'auto' : 'hidden',
            transition: 'width 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
          }}>
          {showUpcoming ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 16,
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  onClick={() => setShowUpcoming(false)}
                  title="Collapse"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}>
                  <CollapseIcon color={C.muted} />
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.muted,
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  Upcoming
                </div>
              </div>
              {!m && <BackBtn />}
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 20 }}>
              <div
                onClick={() => setShowUpcoming(true)}
                title="Expand"
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <ExpandIcon color={C.muted} />
              </div>
            </div>
          )}
          {showUpcoming && upcoming.length === 0 && (
            <div
              style={{
                padding: 16,
                textAlign: 'center',
                color: C.muted,
                fontSize: 12,
                fontFamily: "'Manrope',sans-serif",
              }}>
              No upcoming events
            </div>
          )}
          {showUpcoming &&
            upcoming.map((ev, i, arr) => {
              const evColor = entryColor(ev);
              const isTask = ev.type === CalendarEntryType.Task;
              return (
                <div
                  key={ev.id}
                  onMouseEnter={() => setHoveredUpcomingId(ev.id)}
                  onMouseLeave={() => setHoveredUpcomingId(null)}
                  style={{
                    display: 'flex',
                    alignItems: 'stretch',
                    gap: 12,
                    padding: '10px 0',
                    borderBottom: i < arr.length - 1 ? `1px solid ${C.border}44` : 'none',
                  }}>
                  <div
                    style={{
                      width: 3,
                      borderRadius: 2,
                      background: evColor,
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: C.text,
                        fontFamily: "'Manrope',sans-serif",
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                      {ev.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: C.muted,
                        marginTop: 2,
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      {(() => {
                        const d = new Date(ev.startDate * 1000);
                        const hasTime = !(d.getHours() === 0 && d.getMinutes() === 0);
                        return hasTime
                          ? `${fmtEntryDate(ev.startDate)} · ${fmtEntryTime(ev.startDate)}`
                          : fmtEntryDate(ev.startDate);
                      })()}
                    </div>
                  </div>
                  {!isTask && hoveredUpcomingId === ev.id && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 4,
                        flexShrink: 0,
                      }}>
                      <button
                        onClick={() => handleEditEntry(ev)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: C.muted,
                          padding: '2px',
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title="Edit">
                        <EditIcon style={{ fontSize: 14 }} />
                      </button>
                      <button
                        onClick={() => handleDeleteEntry(ev)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: C.muted,
                          padding: '2px',
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title="Delete">
                        <DeleteIcon style={{ fontSize: 14 }} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
        <EditCalendarEventDialog
          ref={createEventDialogRef}
          anchorRef={createEventButtonRef}
          selectedCalendarEntry={undefined}
          onSubmit={(values: import('@/models/CalendarEntry').CreateCalendarEvent) => {
            pendingColorRef.current = createColor;
            createCalendarEvent(values);
          }}
          isLoading={isCreatingCalendarEvent}
          selectedColor={createColor}
          onColorChange={setCreateColor}
        />
        <EditCalendarReminderDialog
          ref={createReminderDialogRef}
          selectedCalendarEntry={undefined}
          onSubmit={createCalendarReminder}
          isLoading={isCreatingCalendarReminder}
        />
        <EditCalendarEventDialog
          ref={editEventDialogRef}
          selectedCalendarEntry={
            selectedEntryForEdit?.type === CalendarEntryType.Event ? selectedEntryForEdit : null
          }
          onSubmit={(id, values) => updateCalendarEvent(id, values)}
          isLoading={isUpdatingCalendarEvent}
          selectedColor={editColor}
          onColorChange={setEditColor}
        />
        <EditCalendarReminderDialog
          ref={editReminderDialogRef}
          selectedCalendarEntry={
            selectedEntryForEdit?.type === CalendarEntryType.Reminder ? selectedEntryForEdit : null
          }
          onSubmit={(id, values) => updateCalendarReminder(id, values)}
          isLoading={isUpdatingCalendarReminder}
        />
      </div>
    </div>
  );
}

// ─── ISSUES PANEL ───────────────────────────────────────────────────────────
const ISSUES_SEV_W: Record<string, number> = {
  Critical: 2.5,
  Urgent: 1.5,
  Moderate: 1,
  Low: 0.5,
  Monitor: 0.25,
};
const ISSUES_SEV_COLORS: Record<string, string> = {
  Critical: C.pink,
  Urgent: C.orange,
  Moderate: C.yellow,
  Low: C.teal,
  Monitor: C.muted,
};
type IssuesSortKey = 'urgency' | 'cost' | 'category' | 'name';

function IssuesPanel() {
  const { selectedProperty, propertyIssues } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const queryClient = useQueryClient();
  const { resolveIssue, unresolveIssue } = useResolveIssue(propertyId ?? '');
  const allIssues = propertyIssues ?? [];
  const m = useWindowWidth() <= 768;
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const rfqCompile = useRFQCompile();

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<IssuesSortKey>('urgency');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [urgencyFilter, setUrgencyFilter] = useState<Set<string>>(new Set());
  const [categoryFilter, setCategoryFilter] = useState<Set<string>>(new Set());
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [urgencyMenuOpen, setUrgencyMenuOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'resolved'>('open');
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [shareProgress, setShareProgress] = useState<{ done: number; total: number } | null>(null);
  const [rfqGenerationIssues, setRfqGenerationIssues] = useState<Issue[]>([]);
  const [reviewRFQ, setReviewRFQ] = useState<RequestForQuotation | null>(null);
  const [reviewCompileRFQs, setReviewCompileRFQs] = useState<RequestForQuotation[] | null>(null);
  const [reviewSuggestedTrades, setReviewSuggestedTrades] = useState<string[]>([]);
  const [rfqHistoryOpen, setRfqHistoryOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Activity entries and shared links can reopen an existing RFQ by setting ?rfqId=...
  useEffect(() => {
    const rfqId = searchParams.get('rfqId');
    if (!rfqId || reviewRFQ?.id === rfqId) return;

    let cancelled = false;
    getRequestForQuote(rfqId)
      .then((rfq) => {
        if (cancelled) return;
        if (selectedProperty && rfq.propertyId !== selectedProperty.property.id) return;
        const linkedIssue = rfq.issueId ? allIssues.find((i) => i.id === rfq.issueId) : undefined;
        const trade = linkedIssue?.trade ?? linkedIssue?.componentCategory ?? linkedIssue?.category;
        setReviewSuggestedTrades(trade ? [trade] : []);
        setReviewRFQ(rfq);
      })
      .catch((err) => {
        console.error('Failed to load RFQ from URL', err);
        showAlert("Couldn't load that RFQ.", 'error');
      });

    return () => {
      cancelled = true;
    };
  }, [allIssues, searchParams, reviewRFQ?.id, selectedProperty, showAlert]);

  useEffect(() => {
    if (!reviewRFQ?.issueId || reviewSuggestedTrades.length > 0) return;
    const linkedIssue = allIssues.find((i) => i.id === reviewRFQ.issueId);
    const trade = linkedIssue?.trade ?? linkedIssue?.componentCategory ?? linkedIssue?.category;
    if (trade) setReviewSuggestedTrades([trade]);
  }, [allIssues, reviewRFQ?.issueId, reviewSuggestedTrades.length]);

  const clearRfqSearchParam = () => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.delete('rfqId');
      return next;
    });
  };

  // Dev mock — visit /dashboard/home-hub/issues?reviewMock=1 to open the
  // Review & Adjust dialog with sample data. No backend / RFQ generation needed.
  useEffect(() => {
    if (!import.meta.env.DEV || searchParams.get('reviewMock') !== '1') return;
    if (reviewRFQ) return;
    setReviewRFQ({
      id: 'mock-rfq-1',
      propertyId: selectedProperty?.property.id ?? 'mock-property',
      issueId: 'mock-issue-1',
      propertyAddress: selectedProperty?.property.address ?? '123 Main Street',
      clientContact: { name: 'Homeowner', phoneNumber: '[Redacted]', email: '[Redacted]' },
      projectTitle: 'Installation of Handrail on Rear Stairs',
      objective:
        'Install a compliant handrail on rear stairs to enhance safety and meet building code requirements.',
      scopeOfWork: [
        'Measure and evaluate stair dimensions',
        'Supply and install a durable, weather-resistant handrail',
        'Ensure compliance with local codes (34-38" height, 1 1/4-2" grip diameter)',
        'Securely anchor handrail and ensure proper alignment',
        'Remove debris and clean the worksite upon completion',
      ],
      timeline: { desiredStartDate: 'ASAP', completionDate: 'Within 1-2 days of project start' },
      nextSteps:
        'Contractors are welcome to review prior inspection reports, perform their own onsite assessment, or order to provide accurate estimates and timelines.',
      submissionRequirements: {
        dueInDays: 7,
        method: 'Email proposal to homeowner@example.com',
        include: [
          'Cost breakdown for materials and labor',
          'Proposed timeline for start and completion',
          'Proof of licensing and insurance (Bond, WC and GL)',
          'Warranty information for materials and workmanship',
        ],
      },
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }, [searchParams, reviewRFQ, selectedProperty]);

  const issueCost = (i: Issue) => {
    const min = i.costEstimateMin ?? 0;
    const max = i.costEstimateMax ?? min;
    return (min + max) / 2;
  };

  const allCategories = useMemo(
    () => Array.from(new Set(allIssues.map((i) => i.trade ?? i.category).filter(Boolean))).sort(),
    [allIssues],
  );

  const filtered = useMemo(() => {
    let result = allIssues;
    if (statusFilter === 'open') result = result.filter((i) => !i.resolved);
    if (statusFilter === 'resolved') result = result.filter((i) => i.resolved);
    if (urgencyFilter.size > 0) result = result.filter((i) => urgencyFilter.has(i.urgency));
    if (categoryFilter.size > 0)
      result = result.filter((i) => categoryFilter.has(i.trade ?? i.category));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (i) =>
          i.title.toLowerCase().includes(q) || (i.trade ?? i.category).toLowerCase().includes(q),
      );
    }
    const sorted = [...result].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'urgency')
        cmp = (ISSUES_SEV_W[b.urgency] ?? 0) - (ISSUES_SEV_W[a.urgency] ?? 0);
      else if (sortKey === 'cost') cmp = issueCost(b) - issueCost(a);
      else if (sortKey === 'category')
        cmp = (a.trade ?? a.category).localeCompare(b.trade ?? b.category);
      else if (sortKey === 'name') cmp = a.title.localeCompare(b.title);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [allIssues, statusFilter, urgencyFilter, categoryFilter, search, sortKey, sortDir]);

  const openCount = allIssues.filter((i) => !i.resolved).length;
  const resolvedCount = allIssues.filter((i) => i.resolved).length;
  // legend counts follow the status filter (open / all / resolved), so each count matches what picking it shows
  const urgencyCounts = useMemo(() => {
    const counts: Record<string, number> = {
      Critical: 0,
      Urgent: 0,
      Moderate: 0,
      Low: 0,
      Monitor: 0,
    };
    for (const i of allIssues) {
      if (statusFilter === 'open' && i.resolved) continue;
      if (statusFilter === 'resolved' && !i.resolved) continue;
      if (counts[i.urgency] !== undefined) counts[i.urgency] += 1;
    }
    return counts;
  }, [allIssues, statusFilter]);

  const SORT_LABELS: Record<IssuesSortKey, string> = {
    urgency: 'By Urgency',
    cost: 'By Cost',
    category: 'By Category',
    name: 'By Name',
  };
  const selectedCost = useMemo(
    () => filtered.filter((i) => selectedIds.has(i.id)).reduce((s, i) => s + issueCost(i), 0),
    [filtered, selectedIds],
  );

  const toggleSort = (key: IssuesSortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const toggleUrgency = (u: string) =>
    setUrgencyFilter((prev) => {
      const next = new Set(prev);
      if (next.has(u)) next.delete(u);
      else next.add(u);
      return next;
    });
  const toggleCategory = (c: string) =>
    setCategoryFilter((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleExpand = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length && filtered.length > 0) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((i) => i.id)));
  };
  const bulkResolve = async () => {
    const ids = Array.from(selectedIds);
    for (const id of ids) await resolveIssue(id);
    setSelectedIds(new Set());
  };
  const onToggleResolved = async (issue: Issue) => {
    if (issue.resolved) await unresolveIssue(issue.id);
    else await resolveIssue(issue.id);
  };

  const handleShareRFQs = async () => {
    if (shareProgress) return;
    const issues = filtered.filter((i) => selectedIds.has(i.id));
    if (issues.length === 0) return;

    setRfqGenerationIssues(issues);
    setShareProgress({ done: 0, total: issues.length });
    try {
      setReviewSuggestedTrades(
        issues.map((issue) => issue.trade ?? issue.componentCategory ?? issue.category),
      );
      const rfqs: RequestForQuotation[] = [];
      for (let idx = 0; idx < issues.length; idx += 1) {
        const rfq = await retrieveOrCreateRFQ(issues[idx].id);
        rfqs.push(rfq);
        setShareProgress({ done: idx + 1, total: issues.length });
      }
      refreshActivityForProperty(queryClient, propertyId);

      // Single issue → Review & Adjust dialog (uses RequestForQuotationCard's
      // existing edit affordances). Multi (compile) → consolidated review
      // dialog with one Timeline & Submission set governing all projects.
      if (rfqs.length === 1) {
        setReviewRFQ(rfqs[0]);
      } else {
        setReviewCompileRFQs(rfqs);
      }
    } catch (err) {
      console.error('Failed to create share link', err);
      showAlert(t('rfq.share_failed'), 'error');
    } finally {
      setShareProgress(null);
      setRfqGenerationIssues([]);
    }
  };

  const fmtTimeline = (i: Issue): string | null => {
    const min = i.projectTimelineMinDays;
    const max = i.projectTimelineMaxDays;
    if (!min && !max) return null;
    const upper = (max ?? min) as number;
    if (upper <= 1) return '≤ 1 Day';
    if (min && max && min !== max) return `${min}–${max} Days`;
    return `${upper} Days`;
  };

  const fmtCost = (i: Issue) => {
    const min = i.costEstimateMin;
    const max = i.costEstimateMax;
    if (!min && !max) return '—';
    if (min && max && min !== max)
      return `$${
        min / 1000 >= 1
          ? `${(min / 1000).toFixed(min % 1000 === 0 ? 0 : 1)}K`
          : min.toLocaleString()
      }–${
        max / 1000 >= 1
          ? `${(max / 1000).toFixed(max % 1000 === 0 ? 0 : 1)}K`
          : max.toLocaleString()
      }`;
    const v = max ?? min ?? 0;
    return v >= 1000 ? `$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K` : `$${v.toLocaleString()}`;
  };

  return (
    <div
      style={{
        margin: '0 auto',
        paddingTop: m ? 0 : 20,
        paddingBottom: 20,
        paddingLeft: m ? 10 : 0,
        paddingRight: m ? 10 : 0,
        fontFamily: "'Manrope',sans-serif",
      }}>
      <div
        style={{
          ...card(),
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: m ? 'calc(100dvh - 104px)' : 'calc(100dvh - 40px)',
          position: 'relative',
          overflow: 'hidden',
        }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '20px 20px 12px',
            flexShrink: 0,
          }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 17,
                fontWeight: 400,
                letterSpacing: '-0.015em',
                color: C.text,
                fontFamily: "'Manrope',sans-serif",
              }}>
              Punch list
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--tx-muted)',
                marginTop: 4,
                fontFamily: "'Manrope',sans-serif",
              }}>
              {openCount} open · {resolvedCount} resolved
            </div>
          </div>
          <RFQCompileIndicator />
          <BackBtn />
        </div>

        {/* Toolbar: search + filters + bulk actions */}
        <div
          style={{
            padding: '0 20px 12px',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}>
          {/* Row 1: Search (+ Status on desktop) + Sort / Filter / Expand all */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div
              style={{
                flex: 1,
                // Mobile: shrink search so sort/filter/expand buttons stay
                // on the same row as the search input.
                minWidth: m ? 0 : 180,
                height: 40,
                background: 'var(--tx-fill)',
                border: 0,
                borderRadius: '12px',
                padding: '0 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxSizing: 'border-box',
              }}>
              <Search size={16} color={C.muted} strokeWidth={2} style={{ flexShrink: 0 }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search punch list…"
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  fontSize: 12,
                  color: C.text,
                  fontFamily: "'Manrope',sans-serif",
                  flex: 1,
                  minWidth: 0,
                }}
              />
            </div>

            {/* Status toggle (desktop only — mobile gets it on row 2): a tonal segment, the selected tile on the pick */}
            {!m && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 40,
                  boxSizing: 'border-box',
                  background: 'var(--tx-fill)',
                  border: 0,
                  borderRadius: 8,
                  padding: 4,
                  gap: 2,
                }}>
                {(['open', 'all', 'resolved'] as const).map((s) => {
                  const active = statusFilter === s;
                  return (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      aria-pressed={active}
                      style={{
                        height: 32,
                        background: active ? 'var(--tx-sel-bg)' : 'transparent',
                        boxShadow: active ? 'var(--tx-sel-shadow)' : 'none',
                        border: 'none',
                        color: active ? 'var(--tx-text)' : 'var(--tx-muted)',
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '0 14px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontFamily: "'Manrope',sans-serif",
                        textTransform: 'capitalize',
                        transition: 'background 0.15s, color 0.15s',
                      }}>
                      {s}
                    </button>
                  );
                })}
              </div>
            )}

            <Stack direction="row" alignItems="center" spacing={'8px'}>
              {m ? (
                <IconButton
                  onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                  size="small"
                  aria-label="Sort"
                  sx={{
                    bgcolor: 'var(--tx-fill)',
                    border: 0,
                    color: 'var(--tx-muted)',
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    transition: 'color 0.15s, background 0.15s',
                    '&:hover': { bgcolor: 'var(--tx-fill-2)', color: C.text },
                  }}>
                  <ArrowUpDown size={16} />
                </IconButton>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                  startIcon={<ArrowUpDown size={14} />}
                  sx={{
                    bgcolor: 'var(--tx-fill)',
                    border: 0,
                    color: 'var(--tx-muted)',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    height: 40,
                    borderRadius: '8px',
                    px: '20px',
                    boxShadow: 'none',
                    transition: 'color 0.15s, background 0.15s',
                    '&:hover': {
                      bgcolor: 'var(--tx-fill-2)',
                      color: C.text,
                      boxShadow: 'none',
                    },
                  }}>
                  Sort
                </Button>
              )}
              {m ? (
                <IconButton
                  onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                  size="small"
                  aria-label="Filter"
                  sx={{
                    bgcolor: 'var(--tx-fill)',
                    border: 0,
                    color: 'var(--tx-muted)',
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    transition: 'color 0.15s, background 0.15s',
                    '&:hover': { bgcolor: 'var(--tx-fill-2)', color: C.text },
                  }}>
                  <ListFilter size={16} />
                </IconButton>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                  startIcon={<ListFilter size={14} />}
                  sx={{
                    bgcolor: 'var(--tx-fill)',
                    border: 0,
                    color: 'var(--tx-muted)',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 13,
                    height: 40,
                    borderRadius: '8px',
                    px: '20px',
                    boxShadow: 'none',
                    transition: 'color 0.15s, background 0.15s',
                    '&:hover': {
                      bgcolor: 'var(--tx-fill-2)',
                      color: C.text,
                      boxShadow: 'none',
                    },
                  }}>
                  Filter
                </Button>
              )}
              {(() => {
                const allExpanded = filtered.length > 0 && expandedIds.size === filtered.length;
                return (
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedIds(allExpanded ? new Set() : new Set(filtered.map((i) => i.id)))
                    }
                    title={allExpanded ? 'Collapse all' : 'Expand all'}
                    aria-label={allExpanded ? 'Collapse all' : 'Expand all'}
                    style={{
                      background: 'var(--tx-fill)',
                      border: 0,
                      borderRadius: 8,
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: 'var(--tx-muted)',
                      transition: 'color 0.15s, background 0.15s',
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = C.text;
                      (e.currentTarget as HTMLButtonElement).style.background = 'var(--tx-fill-2)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = 'var(--tx-muted)';
                      (e.currentTarget as HTMLButtonElement).style.background = 'var(--tx-fill)';
                    }}>
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      {allExpanded ? (
                        <>
                          <polyline points="4 14 10 14 10 20" />
                          <polyline points="20 10 14 10 14 4" />
                          <line x1="10" y1="14" x2="3" y2="21" />
                          <line x1="21" y1="3" x2="14" y2="10" />
                        </>
                      ) : (
                        <>
                          <polyline points="15 3 21 3 21 9" />
                          <polyline points="9 21 3 21 3 15" />
                          <line x1="21" y1="3" x2="14" y2="10" />
                          <line x1="3" y1="21" x2="10" y2="14" />
                        </>
                      )}
                    </svg>
                  </button>
                );
              })()}
            </Stack>
          </div>

          {/* Filters row — mobile: Status + Urgency + Category dropdowns; desktop: Urgency legend + Category */}
          <div
            style={{
              display: m ? 'grid' : 'flex',
              gridTemplateColumns: m ? '1fr 1fr 1fr' : undefined,
              gap: m ? 8 : 16,
              flexWrap: m ? undefined : 'wrap',
              alignItems: 'center',
            }}>
            {/* Status dropdown (mobile only here — single select) */}
            {m && (
              <div style={{ position: 'relative', width: '100%' }}>
                <button
                  onClick={() => setStatusMenuOpen(!statusMenuOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 6,
                    padding: '5px 12px',
                    borderRadius: 6,
                    background: statusFilter !== 'open' ? `${C.canvas}2a` : 'var(--tx-fill)',
                    border: 0,
                    color: statusFilter !== 'open' ? C.text : 'var(--tx-muted)',
                    fontSize: 10,
                    fontWeight: 600,
                    fontFamily: "'Manrope',sans-serif",
                    cursor: 'pointer',
                    width: '100%',
                  }}>
                  {statusFilter === 'open'
                    ? 'Open'
                    : statusFilter === 'resolved'
                    ? 'Resolved'
                    : 'All'}
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      transform: statusMenuOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform .15s',
                    }}>
                    <ChevronDown size={14} />
                  </span>
                </button>
                {statusMenuOpen && (
                  <>
                    <div
                      onClick={() => setStatusMenuOpen(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 100 }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        zIndex: 101,
                        minWidth: 140,
                        background: 'var(--tx-glass)',
                        backdropFilter: 'var(--tx-blur)',
                        WebkitBackdropFilter: 'var(--tx-blur)',
                        borderRadius: '12px',
                        boxShadow: 'var(--tx-shadow-sm)',
                        padding: 6,
                      }}>
                      {(['open', 'all', 'resolved'] as const).map((s) => {
                        const active = statusFilter === s;
                        return (
                          <div
                            key={s}
                            onClick={() => {
                              setStatusFilter(s);
                              setStatusMenuOpen(false);
                            }}
                            style={{
                              padding: '7px 10px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              background: active ? 'var(--tx-sel-bg)' : 'transparent',
                              boxShadow: active ? 'var(--tx-sel-shadow)' : 'none',
                              fontSize: 11,
                              fontWeight: active ? 600 : 500,
                              color: active ? C.text : 'var(--tx-muted)',
                              fontFamily: "'Manrope',sans-serif",
                              textTransform: 'capitalize',
                            }}>
                            {s}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Urgency — dropdown on mobile, the legend row on desktop */}
            {m ? (
              <div style={{ position: 'relative', width: '100%' }}>
                <button
                  onClick={() => setUrgencyMenuOpen(!urgencyMenuOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 6,
                    padding: '5px 12px',
                    borderRadius: 6,
                    background: urgencyFilter.size > 0 ? `${C.canvas}2a` : 'var(--tx-fill)',
                    border: 0,
                    color: urgencyFilter.size > 0 ? C.text : 'var(--tx-muted)',
                    fontSize: 10,
                    fontWeight: 600,
                    fontFamily: "'Manrope',sans-serif",
                    cursor: 'pointer',
                    width: '100%',
                  }}>
                  {urgencyFilter.size === 0 ? 'Urgency' : `${urgencyFilter.size} urgency`}
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      transform: urgencyMenuOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform .15s',
                    }}>
                    <ChevronDown size={14} />
                  </span>
                </button>

                {urgencyMenuOpen && (
                  <>
                    <div
                      onClick={() => setUrgencyMenuOpen(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 100 }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        zIndex: 101,
                        minWidth: 200,
                        maxWidth: 'calc(100vw - 40px)',
                        maxHeight: 320,
                        overflowY: 'auto',
                        background: 'var(--tx-glass)',
                        backdropFilter: 'var(--tx-blur)',
                        WebkitBackdropFilter: 'var(--tx-blur)',
                        borderRadius: '12px',
                        boxShadow: 'var(--tx-shadow-sm)',
                        padding: 6,
                      }}>
                      {urgencyFilter.size > 0 && (
                        <button
                          onClick={() => setUrgencyFilter(new Set())}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            padding: '6px 10px',
                            fontSize: 10,
                            fontWeight: 600,
                            color: 'var(--tx-muted)',
                            cursor: 'pointer',
                            fontFamily: "'Manrope',sans-serif",
                            borderBottom: `1px solid ${C.border}`,
                            marginBottom: 4,
                          }}>
                          Clear all
                        </button>
                      )}
                      {(['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'] as const).map((u) => {
                        const active = urgencyFilter.has(u);
                        const col = ISSUES_SEV_COLORS[u];
                        return (
                          <div
                            key={u}
                            onClick={() => toggleUrgency(u)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '7px 10px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              background: active ? `${col}15` : 'transparent',
                              transition: 'background .15s',
                            }}>
                            <Checkbox checked={active} onChange={() => toggleUrgency(u)} />
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span
                                style={{ width: 8, height: 8, borderRadius: 2, background: col }}
                              />
                              <span
                                style={{
                                  fontSize: 11,
                                  color: C.text,
                                  fontFamily: "'Manrope',sans-serif",
                                }}>
                                {u}: <span style={{ fontWeight: 600 }}>{urgencyCounts[u]}</span>
                              </span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 280,
                }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: C.muted,
                    fontFamily: "'Manrope',sans-serif",
                    marginRight: 4,
                  }}>
                  Urgency
                </span>
                {(['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'] as const).map((u) => {
                  const active = urgencyFilter.has(u);
                  const col = ISSUES_SEV_COLORS[u];
                  return (
                    <button
                      key={u}
                      onClick={() => toggleUrgency(u)}
                      aria-pressed={active}
                      style={{
                        // the app's legend item (a 2px tick in the urgency colour, the label, its count in ink); a
                        // selected filter takes the tonal tile instead of a coloured pill
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        height: 26,
                        padding: '0 8px',
                        borderRadius: 7,
                        border: 'none',
                        background: active ? 'var(--tx-sel-bg)' : 'transparent',
                        boxShadow: active ? 'var(--tx-sel-shadow)' : 'none',
                        color: active ? 'var(--tx-text)' : 'var(--tx-muted)',
                        fontSize: 11,
                        fontWeight: 500,
                        fontFamily: "'Manrope',sans-serif",
                        cursor: 'pointer',
                        transition: 'background 0.15s, color 0.15s',
                      }}>
                      <span style={{ width: 2, height: 12, borderRadius: 1, background: col }} />
                      {u}
                      <span
                        style={{
                          color: 'var(--tx-text)',
                          fontWeight: 600,
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                        {urgencyCounts[u]}
                      </span>
                    </button>
                  );
                })}
                {urgencyFilter.size > 0 && (
                  <button
                    onClick={() => setUrgencyFilter(new Set())}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: 10,
                      color: 'var(--tx-muted)',
                      cursor: 'pointer',
                      fontFamily: "'Manrope',sans-serif",
                      marginLeft: 4,
                    }}>
                    Clear
                  </button>
                )}
              </div>
            )}

            {/* Category dropdown */}
            {allCategories.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  gap: 6,
                  alignItems: 'center',
                  position: 'relative',
                  width: m ? '100%' : 'auto',
                }}>
                {!m && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: C.muted,
                      fontFamily: "'Manrope',sans-serif",
                    }}>
                    Category
                  </span>
                )}
                <button
                  onClick={() => setCategoryMenuOpen(!categoryMenuOpen)}
                  style={{
                    display: m ? 'flex' : 'inline-flex',
                    alignItems: 'center',
                    justifyContent: m ? 'space-between' : 'flex-start',
                    gap: 6,
                    // tonal like the urgency filters: a fill, no outline; the selected tile once a category is picked
                    height: 26,
                    padding: '0 10px',
                    borderRadius: 7,
                    background: categoryFilter.size > 0 ? 'var(--tx-sel-bg)' : 'var(--tx-fill)',
                    boxShadow: categoryFilter.size > 0 ? 'var(--tx-sel-shadow)' : 'none',
                    border: 'none',
                    color: categoryFilter.size > 0 ? C.text : 'var(--tx-muted)',
                    fontSize: 11,
                    fontWeight: 500,
                    fontFamily: "'Manrope',sans-serif",
                    cursor: 'pointer',
                    width: m ? '100%' : 'auto',
                  }}>
                  {m
                    ? categoryFilter.size === 0
                      ? 'Category'
                      : `${categoryFilter.size} category`
                    : categoryFilter.size === 0
                    ? 'All'
                    : `${categoryFilter.size} selected`}
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      transform: categoryMenuOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform .15s',
                    }}>
                    <ChevronDown size={14} />
                  </span>
                </button>

                {categoryMenuOpen && (
                  <>
                    <div
                      onClick={() => setCategoryMenuOpen(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 100 }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        right: 0,
                        zIndex: 101,
                        minWidth: m ? 200 : 240,
                        maxWidth: 'calc(100vw - 40px)',
                        maxHeight: 320,
                        overflowY: 'auto',
                        background: 'var(--tx-glass)',
                        backdropFilter: 'var(--tx-blur)',
                        WebkitBackdropFilter: 'var(--tx-blur)',
                        borderRadius: '12px',
                        boxShadow: 'var(--tx-shadow-sm)',
                        padding: 6,
                      }}>
                      {categoryFilter.size > 0 && (
                        <button
                          onClick={() => setCategoryFilter(new Set())}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            background: 'none',
                            border: 'none',
                            padding: '6px 10px',
                            fontSize: 10,
                            fontWeight: 600,
                            color: 'var(--tx-muted)',
                            cursor: 'pointer',
                            fontFamily: "'Manrope',sans-serif",
                            borderBottom: `1px solid ${C.border}`,
                            marginBottom: 4,
                          }}>
                          Clear all
                        </button>
                      )}
                      {allCategories.map((cat) => {
                        const active = categoryFilter.has(cat);
                        return (
                          <div
                            key={cat}
                            onClick={() => toggleCategory(cat)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              padding: '7px 10px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              background: active ? `${C.lime}10` : 'transparent',
                              transition: 'background .15s',
                            }}
                            onMouseEnter={(e) => {
                              if (!active) e.currentTarget.style.background = C.bg4;
                            }}
                            onMouseLeave={(e) => {
                              if (!active) e.currentTarget.style.background = 'transparent';
                            }}>
                            <Checkbox checked={active} onChange={() => toggleCategory(cat)} />
                            <span
                              style={{
                                fontSize: 11,
                                color: C.text,
                                fontFamily: "'Manrope',sans-serif",
                              }}>
                              {cat}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* RFQ action bar — always rendered so Create RFQ / History are discoverable before a selection */}
          {(() => {
            const hasSelection = selectedIds.size > 0;
            const isCompile = selectedIds.size >= 2;
            const isBusy = Boolean(shareProgress);
            const secondaryBtn: React.CSSProperties = {
              // tonal (ds secondary): a fill, no stroke
              height: 32,
              background: 'var(--tx-fill)',
              border: 'none',
              borderRadius: 9,
              padding: m ? '0 10px' : '0 14px',
              fontSize: m ? 11 : 12,
              fontWeight: 600,
              color: 'var(--tx-text)',
              cursor: 'pointer',
              fontFamily: "'Manrope',sans-serif",
              whiteSpace: 'nowrap',
            };
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: m ? 8 : 12,
                  // phones: with a selection the four buttons can outgrow 390px, so they wrap rather than overflow
                  flexWrap: m ? 'wrap' : undefined,
                  padding: '4px 0',
                }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: hasSelection ? 'var(--tx-text)' : 'var(--tx-muted)',
                    fontFamily: "'Manrope',sans-serif",
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}>
                  {shareProgress
                    ? `Generating ${shareProgress.done}/${shareProgress.total} RFQs…`
                    : hasSelection
                    ? `${selectedIds.size} selected${
                        m ? '' : ` · ~$${Math.round(selectedCost).toLocaleString()} total`
                      }`
                    : m
                    ? 'RFQs'
                    : 'Create or recall RFQs'}
                </span>
                <div style={{ flex: 1 }} />
                <button
                  onClick={() => setRfqHistoryOpen(true)}
                  style={{
                    ...secondaryBtn,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                  <Clock size={m ? 12 : 13} />
                  History
                </button>
                <button
                  onClick={handleShareRFQs}
                  disabled={!hasSelection || isBusy}
                  style={{
                    // the page's one action: lime primary once rows are picked, a quiet tonal button until then
                    height: 32,
                    background: hasSelection ? 'var(--tx-lime)' : 'var(--tx-fill)',
                    border: 'none',
                    borderRadius: 9,
                    padding: m ? '0 12px' : '0 16px',
                    fontSize: m ? 11 : 12,
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: hasSelection ? '#262626' : 'var(--tx-muted)',
                    cursor: !hasSelection ? 'not-allowed' : isBusy ? 'wait' : 'pointer',
                    fontFamily: "'Manrope',sans-serif",
                    whiteSpace: 'nowrap',
                    opacity: !hasSelection ? 0.45 : isBusy ? 0.6 : 1,
                  }}>
                  {isCompile ? 'Compile RFQ' : 'Create RFQ'}
                </button>
                {hasSelection && (
                  <>
                    <button onClick={bulkResolve} style={secondaryBtn}>
                      {m ? 'Resolve' : 'Mark resolved'}
                    </button>
                    <button onClick={() => setSelectedIds(new Set())} style={secondaryBtn}>
                      Clear
                    </button>
                  </>
                )}
              </div>
            );
          })()}
        </div>

        {/* Table header */}
        {m ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 20px',
              borderTop: `1px solid ${C.border}`,
              borderBottom: `1px solid ${C.border}`,
              flexShrink: 0,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow)',
            }}>
            <Checkbox
              checked={filtered.length > 0 && selectedIds.size === filtered.length}
              onChange={toggleSelectAll}
            />
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: C.muted,
                fontFamily: "'Manrope',sans-serif",
              }}>
              Select all
            </span>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
              title={`Sort ${sortDir === 'asc' ? 'ascending' : 'descending'} by urgency`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: "'Manrope',sans-serif",
              }}>
              <span
                style={{
                  fontSize: 10,
                  color: C.muted,
                  fontWeight: 600,
                }}>
                Sort
              </span>
              <SortCaret active dir={sortDir} />
            </button>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '36px 24px 1fr 140px 120px 96px 80px',
              gap: 0,
              padding: '8px 20px',
              borderTop: `1px solid ${C.border}`,
              borderBottom: `1px solid ${C.border}`,
              flexShrink: 0,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow)',
            }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={filtered.length > 0 && selectedIds.size === filtered.length}
                onChange={toggleSelectAll}
              />
            </div>
            <div />
            <HeaderCell
              label="Item"
              active={sortKey === 'name'}
              dir={sortDir}
              onClick={() => toggleSort('name')}
            />
            <HeaderCell
              label="Category"
              active={sortKey === 'category'}
              dir={sortDir}
              onClick={() => toggleSort('category')}
              marginLeft={-20}
            />
            <HeaderCell
              label="Urgency"
              active={sortKey === 'urgency'}
              dir={sortDir}
              onClick={() => toggleSort('urgency')}
              marginLeft={20}
            />
            <HeaderCell
              label="Est. cost"
              active={sortKey === 'cost'}
              dir={sortDir}
              onClick={() => toggleSort('cost')}
              align="right"
            />
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: C.muted,
                fontFamily: "'Manrope',sans-serif",
                textAlign: 'center',
              }}>
              Status
            </div>
          </div>
        )}

        {/* Rows */}
        <style>{`.pl-scroll::-webkit-scrollbar{width:0;background:transparent}.pl-scroll{scrollbar-width:none}`}</style>
        <ScrollHintList className="pl-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {filtered.length === 0 ? (
            <div
              style={{
                padding: 40,
                textAlign: 'center',
                color: C.muted,
                fontSize: 13,
                fontFamily: "'Manrope',sans-serif",
              }}>
              {!selectedProperty
                ? 'Select a property to view your punch list'
                : 'No items match your filters'}
            </div>
          ) : (
            filtered.map((issue, idx) => {
              const col = ISSUES_SEV_COLORS[issue.urgency] ?? C.muted;
              const isSel = selectedIds.has(issue.id);
              const isExpanded = expandedIds.has(issue.id);
              const timelineLabel = fmtTimeline(issue);
              const detailBlock = isExpanded ? (
                <div
                  style={{
                    paddingTop: 8,
                    minWidth: 0,
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  {issue.description && (
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--tx-muted)',
                        lineHeight: 1.55,
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                      }}>
                      {issue.description}
                    </div>
                  )}
                  {/* Mobile: timeline lives here. Desktop puts it under cost. */}
                  {m && timelineLabel && (
                    <div
                      style={{
                        marginTop: issue.description ? 8 : 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 11,
                        color: C.text,
                        fontWeight: 600,
                      }}>
                      <Clock size={13} color={C.muted} />
                      {timelineLabel}
                    </div>
                  )}
                </div>
              ) : null;
              const costTimelineBlock =
                isExpanded && timelineLabel ? (
                  <div
                    style={{
                      paddingTop: 8,
                      display: 'inline-flex',
                      alignItems: 'baseline',
                      gap: 6,
                      fontSize: 12,
                      lineHeight: 1.55,
                      color: C.text,
                      fontFamily: "'Manrope',sans-serif",
                      fontWeight: 600,
                    }}>
                    <Clock size={14} color={C.muted} style={{ alignSelf: 'center' }} />
                    {timelineLabel}
                  </div>
                ) : null;

              const chevronBtn = (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(issue.id);
                  }}
                  aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                  aria-expanded={isExpanded}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    padding: 0,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: C.muted,
                    flexShrink: 0,
                  }}>
                  <ChevronRight
                    size={16}
                    style={{
                      transform: isExpanded ? 'rotate(90deg)' : 'none',
                      transition: 'transform .15s',
                    }}
                  />
                </button>
              );

              // the reference's square tonal status tag, kept as a button here: the homeowner resolves from the list
              const statusBtn = (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleResolved(issue);
                  }}
                  aria-label={issue.resolved ? 'Mark as open' : 'Mark as resolved'}
                  style={{
                    background: issue.resolved
                      ? 'rgba(192,255,2,0.12)'
                      : 'rgba(var(--tx-ink-rgb), 0.06)',
                    border: 'none',
                    color: issue.resolved ? 'var(--tx-lime-ink)' : C.muted,
                    borderRadius: 6,
                    padding: '3px 8px',
                    fontSize: 10,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  {issue.resolved ? '✓ Done' : 'Open'}
                </button>
              );

              if (m) {
                return (
                  <div
                    key={issue.id}
                    style={{
                      borderBottom: idx < filtered.length - 1 ? `1px solid ${C.border}44` : 'none',
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        gap: 10,
                        padding: '12px 20px',
                        alignItems: 'flex-start',
                        background: isSel
                          ? `${C.lime}08`
                          : issue.resolved
                          ? '#1a1a1a'
                          : 'transparent',
                      }}>
                      <Checkbox checked={isSel} onChange={() => toggleSelect(issue.id)} />
                      <div style={{ marginTop: -2 }}>{chevronBtn}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 8,
                          }}>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: issue.resolved ? C.muted : C.text,
                              textDecoration: issue.resolved ? 'line-through' : 'none',
                              fontFamily: "'Manrope',sans-serif",
                              lineHeight: 1.3,
                              minWidth: 0,
                              flex: 1,
                            }}>
                            {issue.title}
                          </span>
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: C.text,
                              fontFamily: "'Manrope',sans-serif",
                              flexShrink: 0,
                            }}>
                            {fmtCost(issue)}
                          </span>
                        </div>
                        {detailBlock}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginTop: 6,
                            flexWrap: 'wrap',
                          }}>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 500,
                              color: 'var(--tx-text)',
                              fontFamily: "'Manrope',sans-serif",
                            }}>
                            {issue.urgency}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              color: 'var(--tx-muted)',
                              fontFamily: "'Manrope',sans-serif",
                            }}>
                            {issue.trade ?? issue.category}
                          </span>
                          <div style={{ flex: 1 }} />
                          {statusBtn}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div
                  key={issue.id}
                  onClick={() => toggleExpand(issue.id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '36px 24px 1fr 140px 120px 96px 80px',
                    gap: 0,
                    padding: '10px 20px',
                    borderBottom: idx < filtered.length - 1 ? `1px solid ${C.border}44` : 'none',
                    alignItems: 'start',
                    background: isSel ? `${C.lime}08` : issue.resolved ? '#1a1a1a' : 'transparent',
                    transition: 'background .15s',
                    cursor: 'pointer',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', height: 24 }}>
                    <Checkbox checked={isSel} onChange={() => toggleSelect(issue.id)} />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      height: 24,
                    }}>
                    {chevronBtn}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0,
                      minWidth: 0,
                      paddingRight: 80,
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        minWidth: 0,
                        height: 24,
                      }}>
                      <div
                        style={{
                          width: 3,
                          height: 20,
                          borderRadius: 2,
                          background: col,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: issue.resolved ? C.muted : C.text,
                          textDecoration: issue.resolved ? 'line-through' : 'none',
                          fontFamily: "'Manrope',sans-serif",
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          minWidth: 0,
                          flex: 1,
                        }}>
                        {issue.title}
                      </span>
                    </div>
                    {detailBlock}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: 24,
                      marginLeft: -20,
                      fontSize: 11,
                      color: 'var(--tx-muted)',
                      fontFamily: "'Manrope',sans-serif",
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                    {issue.trade ?? issue.category}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      height: 24,
                      marginLeft: 20,
                    }}>
                    {/* plain text: the row's own tick already carries the urgency colour */}
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: 'var(--tx-muted)',
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      {issue.urgency}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      fontFamily: "'Manrope',sans-serif",
                    }}>
                    <div
                      style={{
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: 12,
                        fontWeight: 600,
                        color: C.text,
                      }}>
                      {fmtCost(issue)}
                    </div>
                    {costTimelineBlock}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 24,
                    }}>
                    {statusBtn}
                  </div>
                </div>
              );
            })
          )}
        </ScrollHintList>

        {/* Footer count */}
        <div
          style={{
            padding: m ? '10px 20px 20px' : '12px 20px 20px',
            borderTop: `1px solid ${C.border}`,
            fontSize: 11,
            color: C.muted,
            fontFamily: "'Manrope',sans-serif",
            flexShrink: 0,
          }}>
          Showing {filtered.length} of {allIssues.length}{' '}
          {allIssues.length === 1 ? 'item' : 'items'}
        </div>

        {m && selectedProperty && (
          <RfqHistoryDialog
            open={rfqHistoryOpen}
            propertyId={selectedProperty.property.id}
            issues={allIssues}
            containedMobile
            onClose={() => setRfqHistoryOpen(false)}
            onOpenRFQ={(rfq) => {
              const linkedIssue = rfq.issueId
                ? allIssues.find((i) => i.id === rfq.issueId)
                : undefined;
              const trade =
                linkedIssue?.trade ?? linkedIssue?.componentCategory ?? linkedIssue?.category;
              setReviewSuggestedTrades(trade ? [trade] : []);
              setReviewRFQ(rfq);
            }}
          />
        )}
      </div>

      {/* Sort menu */}
      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={() => setSortMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              borderRadius: '16px',
              minWidth: 200,
              mt: 1,
              pt: '20px',
              px: '20px',
              pb: '14px',
              boxShadow: 'var(--tx-shadow)',
            },
          },
        }}
        sx={{
          '& .MuiMenuItem-root': { borderBottom: 'none' },
          '& .MuiList-root': { p: 0 },
        }}>
        <Typography
          variant="caption"
          sx={{
            color: 'var(--tx-muted)',
            px: '12px',
            pb: '20px',
            display: 'block',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.3em',
          }}>
          SORT
        </Typography>
        {(Object.keys(SORT_LABELS) as IssuesSortKey[]).map((key) => (
          <MenuItem
            key={key}
            onClick={() => {
              setSortKey(key);
              setSortMenuAnchor(null);
            }}
            selected={sortKey === key}
            sx={{
              color: sortKey === key ? 'var(--tx-text)' : 'var(--tx-muted)',
              bgcolor: sortKey === key ? 'var(--tx-fill-2)' : 'transparent',
              fontSize: 14,
              fontWeight: sortKey === key ? 600 : 400,
              borderRadius: '8px',
              mx: 0,
              paddingTop: '6px !important',
              paddingBottom: '6px !important',
              paddingLeft: '12px !important',
              paddingRight: '12px !important',
              lineHeight: '1.4 !important',
              '&.Mui-selected': {
                bgcolor: 'var(--tx-fill-2)',
                color: 'var(--tx-text)',
                '&:hover': { bgcolor: '#BBB7AF' },
              },
              '&:hover': {
                bgcolor: sortKey === key ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
              },
            }}>
            {SORT_LABELS[key]}
          </MenuItem>
        ))}
      </Menu>

      {/* Filter menu (categories) */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              borderRadius: '16px',
              minWidth: 220,
              maxHeight: 360,
              mt: 1,
              pt: '20px',
              px: '20px',
              pb: '14px',
              boxShadow: 'var(--tx-shadow)',
            },
          },
        }}
        sx={{
          '& .MuiMenuItem-root': { borderBottom: 'none' },
          '& .MuiList-root': { p: 0 },
        }}>
        <Typography
          variant="caption"
          sx={{
            color: 'var(--tx-muted)',
            px: '12px',
            pb: '20px',
            display: 'block',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.3em',
          }}>
          BY CATEGORY
        </Typography>
        <MenuItem
          onClick={() => setCategoryFilter(new Set())}
          selected={categoryFilter.size === 0}
          sx={{
            color: categoryFilter.size === 0 ? 'var(--tx-text)' : 'var(--tx-muted)',
            bgcolor: categoryFilter.size === 0 ? 'var(--tx-fill-2)' : 'transparent',
            fontSize: 14,
            fontWeight: categoryFilter.size === 0 ? 600 : 400,
            borderRadius: '8px',
            paddingTop: '6px !important',
            paddingBottom: '6px !important',
            paddingLeft: '12px !important',
            paddingRight: '12px !important',
            lineHeight: '1.4 !important',
            '&.Mui-selected': {
              bgcolor: 'var(--tx-fill-2)',
              color: 'var(--tx-text)',
              '&:hover': { bgcolor: '#BBB7AF' },
            },
            '&:hover': {
              bgcolor: categoryFilter.size === 0 ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
            },
          }}>
          All categories
        </MenuItem>
        {allCategories.map((cat) => {
          const active = categoryFilter.has(cat);
          return (
            <MenuItem
              key={cat}
              onClick={() => toggleCategory(cat)}
              selected={active}
              sx={{
                color: active ? 'var(--tx-text)' : 'var(--tx-muted)',
                bgcolor: active ? 'var(--tx-fill-2)' : 'transparent',
                fontSize: 14,
                fontWeight: active ? 600 : 400,
                borderRadius: '8px',
                paddingTop: '6px !important',
                paddingBottom: '6px !important',
                paddingLeft: '12px !important',
                paddingRight: '12px !important',
                lineHeight: '1.4 !important',
                '&.Mui-selected': {
                  bgcolor: 'var(--tx-fill-2)',
                  color: 'var(--tx-text)',
                  '&:hover': { bgcolor: '#BBB7AF' },
                },
                '&:hover': {
                  bgcolor: active ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
                },
              }}>
              {cat}
            </MenuItem>
          );
        })}
      </Menu>
      {reviewRFQ && selectedProperty && (
        <ReviewAndShareRFQDialog
          open={Boolean(reviewRFQ)}
          userProperty={selectedProperty}
          requestForQuotation={reviewRFQ}
          suggestedTrades={reviewSuggestedTrades}
          onClose={() => {
            setReviewRFQ(null);
            setReviewSuggestedTrades([]);
            clearRfqSearchParam();
          }}
        />
      )}
      <RfqGenerationDialog
        open={Boolean(shareProgress)}
        issues={rfqGenerationIssues}
        progress={shareProgress}
      />
      {!m && selectedProperty && (
        <RfqHistoryDialog
          open={rfqHistoryOpen}
          propertyId={selectedProperty.property.id}
          issues={allIssues}
          onClose={() => setRfqHistoryOpen(false)}
          onOpenRFQ={(rfq) => {
            // Categories are 1:1 with the vendor directory's trade taxonomy —
            // reuse the linked issue's trade directly rather than re-guessing
            // it from RFQ prose (which was silently discarding it via `[]`).
            const linkedIssue = rfq.issueId
              ? allIssues.find((i) => i.id === rfq.issueId)
              : undefined;
            const trade =
              linkedIssue?.trade ?? linkedIssue?.componentCategory ?? linkedIssue?.category;
            setReviewSuggestedTrades(trade ? [trade] : []);
            setReviewRFQ(rfq);
          }}
        />
      )}
      {reviewCompileRFQs && selectedProperty && (
        <ReviewAndShareCompileDialog
          open={Boolean(reviewCompileRFQs)}
          userProperty={selectedProperty}
          rfqs={reviewCompileRFQs}
          suggestedTrades={reviewSuggestedTrades}
          onClose={() => {
            setReviewCompileRFQs(null);
            setReviewSuggestedTrades([]);
          }}
        />
      )}
    </div>
  );
}

function Checkbox({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onChange();
      }}
      style={{
        width: 16,
        height: 16,
        borderRadius: 4,
        border: `1.5px solid ${checked ? C.lime : '#555'}`,
        background: checked ? C.lime : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'all .15s',
      }}>
      {checked && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d="M2 5l2 2 4-4"
            stroke="#111"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}

function HeaderCell({
  label,
  active,
  dir,
  onClick,
  align,
  marginLeft,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
  align?: 'left' | 'right';
  marginLeft?: number;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        fontSize: 10,
        fontWeight: 600,
        color: C.muted,
        fontFamily: "'Manrope',sans-serif",
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: align ?? 'left',
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        marginLeft,
      }}>
      {label}
      <SortCaret active={active} dir={dir} />
    </div>
  );
}

// ─── MAINTENANCE RESERVE CARD ───────────────────────────────────────────────
const SEV_W: Record<string, number> = {
  Critical: 2.5,
  Urgent: 1.5,
  Moderate: 1,
  Low: 0.5,
  Monitor: 0.25,
};
const SEV_COLORS: Record<string, string> = {
  Critical: C.pink,
  Urgent: C.orange,
  Moderate: C.yellow,
  Low: C.teal,
  Monitor: C.muted,
};

export function MaintenanceReserveCard({
  homeValue,
  issues,
  conditionScore,
  onPrepareBidRequest,
}: {
  homeValue: number;
  issues: Issue[];
  conditionScore: number | null;
  /** When provided, renders a file-folder-tab CTA hanging off the
   *  bottom-left of the card that calls this on click. */
  onPrepareBidRequest?: () => void;
}) {
  const { scoreToGrade, gradeBands } = useScoringConfig();
  const [pct, setPct] = useState(1);
  const [prevPct, setPrevPct] = useState<number | null>(null);
  const [budgetEditing, setBudgetEditing] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState('');
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem('mr-open') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('mr-open', isOpen ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [isOpen]);
  const [showDetail, setShowDetail] = useState(false);
  // Inline description expansion for plan items — click toggles. Lets users
  // see the full inspector remark without leaving the card.
  const [expandedPlanIssueId, setExpandedPlanIssueId] = useState<string | null>(null);
  const m = useWindowWidth() <= 768;
  const budget = Math.round((homeValue * pct) / 100);
  const unresolvedWithCost = useMemo(
    () =>
      issues
        .filter((i) => !i.resolved && (i.costEstimateMin || i.costEstimateMax))
        .sort((a, b) => (SEV_W[b.urgency] ?? 0) - (SEV_W[a.urgency] ?? 0)),
    [issues],
  );
  const { plan, totalCost, projectedScore, bySeverity, costBySeverity } = useMemo(() => {
    let rem = budget;
    const selected: Issue[] = [];
    for (const issue of unresolvedWithCost) {
      const cost = issue.costEstimateMax ?? issue.costEstimateMin ?? 0;
      if (cost <= rem) {
        selected.push(issue);
        rem -= cost;
      }
    }
    const total = selected.reduce((s, i) => s + (i.costEstimateMax ?? i.costEstimateMin ?? 0), 0);
    const counts: Record<string, number> = {};
    const costBySeverity: Record<string, number> = {};
    for (const i of selected) {
      counts[i.urgency] = (counts[i.urgency] ?? 0) + 1;
      costBySeverity[i.urgency] =
        (costBySeverity[i.urgency] ?? 0) + (i.costEstimateMax ?? i.costEstimateMin ?? 0);
    }
    let projected = conditionScore ?? 0;
    if (conditionScore != null && issues.length > 0) {
      const allUnres = issues.filter((i) => !i.resolved);
      const totalW = allUnres.reduce((s, i) => s + (SEV_W[i.urgency] ?? 0), 0);
      if (totalW > 0) {
        const resolvedW = selected.reduce((s, i) => s + (SEV_W[i.urgency] ?? 0), 0);
        projected = Math.min(
          100,
          Math.round(conditionScore + (resolvedW / totalW) * (100 - conditionScore)),
        );
      }
    }
    return {
      plan: selected,
      totalCost: total,
      projectedScore: projected,
      bySeverity: counts,
      costBySeverity,
    };
  }, [budget, unresolvedWithCost, issues, conditionScore]);
  const baseScore = conditionScore ?? 0;
  const scoreDelta = projectedScore - baseScore;
  const remaining = budget - totalCost;
  const quickApplyPct = useMemo(() => {
    const priority = new Set(['Critical', 'Urgent', 'Moderate']);
    const needed = unresolvedWithCost
      .filter((i) => priority.has(i.urgency))
      .reduce((s, i) => s + (i.costEstimateMax ?? i.costEstimateMin ?? 0), 0);
    if (needed === 0 || homeValue === 0) return 0;
    return Math.min(10, (needed / homeValue) * 100);
  }, [unresolvedWithCost, homeValue]);
  const quickApplyScoreDelta = useMemo(() => {
    const priority = new Set(['Critical', 'Urgent', 'Moderate']);
    const priorityIssues = unresolvedWithCost.filter((i) => priority.has(i.urgency));
    if (conditionScore == null || issues.length === 0 || priorityIssues.length === 0) return 0;
    const allUnres = issues.filter((i) => !i.resolved);
    const totalW = allUnres.reduce((s, i) => s + (SEV_W[i.urgency] ?? 0), 0);
    if (totalW === 0) return 0;
    const resolvedW = priorityIssues.reduce((s, i) => s + (SEV_W[i.urgency] ?? 0), 0);
    return (
      Math.min(100, Math.round(conditionScore + (resolvedW / totalW) * (100 - conditionScore))) -
      conditionScore
    );
  }, [unresolvedWithCost, issues, conditionScore]);
  const quickApplyActive = quickApplyPct > 0 && pct === quickApplyPct;
  const quickApplyDisabled = quickApplyPct === 0;
  const onQuickApply = () => {
    if (quickApplyDisabled) return;
    if (quickApplyActive) {
      setPct(prevPct ?? 1);
      setPrevPct(null);
    } else {
      setPrevPct(pct);
      setPct(quickApplyPct);
    }
  };
  const fmtK = (n: number) =>
    n >= 1000 ? `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K` : `$${n.toLocaleString()}`;
  const fmtFull = (n: number) => `$${n.toLocaleString()}`;

  return (
    <>
      <div style={{ ...card() }}>
        <div
          onClick={() => setIsOpen(!isOpen)}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}>
          <div style={{ alignSelf: 'flex-start' }}>
            <div
              style={{
                fontSize: 17,
                fontWeight: 400,
                letterSpacing: '-0.015em',
                color: 'var(--tx-text)',
                fontFamily: "'Manrope',sans-serif",
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}>
              Repair planning
            </div>
            {!isOpen && (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'var(--tx-muted)',
                  fontFamily: "'Manrope',sans-serif",
                  lineHeight: 1.2,
                  marginTop: 6,
                }}>
                {fmtFull(budget)} budget · {plan.length} {plan.length === 1 ? 'item' : 'items'} ·{' '}
                {scoreDelta > 0 ? (
                  <>
                    <span style={{ color: C.lime }}>▲</span> {scoreDelta} EQ
                  </>
                ) : (
                  '—'
                )}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
            <span
              style={{
                color: 'var(--tx-faint)',
                lineHeight: 1,
                display: 'inline-flex',
                alignItems: 'center',
                transform: isOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform .2s',
              }}>
              <ChevronDown size={16} />
            </span>
          </div>
        </div>
        {isOpen && (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: m ? 'column' : 'row',
                gap: m ? '20px' : '50px',
                marginTop: m ? 8 : 20,
              }}>
              {/* LEFT */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: m ? 'stretch' : 'center',
                    flexDirection: m ? 'column' : 'row',
                    justifyContent: 'space-between',
                    gap: 12,
                    marginBottom: 16,
                  }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: 'var(--tx-muted)',
                      fontFamily: "'Manrope',sans-serif",
                      lineHeight: 1.5,
                      flex: 1,
                    }}>
                    Experts recommend 1% of home value annually for upkeep. Adjust the slider to see
                    impact on your health score.
                  </div>
                  <style>{`@keyframes eqBoostSpark{0%,80%,100%{transform:scale(1);filter:drop-shadow(0 0 0 rgba(192,255,2,0))}88%{transform:scale(1.25) rotate(-4deg);filter:drop-shadow(0 0 5px rgba(192,255,2,0.9))}94%{transform:scale(1.08) rotate(2deg);filter:drop-shadow(0 0 3px rgba(192,255,2,0.5))}}.eq-boost-spark{animation:eqBoostSpark 2.6s ease-out infinite;transform-origin:center;color:${C.lime}}`}</style>
                  <Tip
                    text={
                      quickApplyDisabled
                        ? 'No priority issues to address right now.'
                        : quickApplyActive
                        ? 'Revert to your previous budget.'
                        : `Intelligently sets your budget to address all Critical, Urgent, and Moderate issues.`
                    }
                    style={{
                      flexShrink: 0,
                      alignSelf: m ? 'flex-start' : 'auto',
                      borderBottom: 'none',
                    }}>
                    <DsButton
                      size="sm"
                      variant={quickApplyActive ? 'primary' : 'secondary'}
                      onClick={onQuickApply}
                      disabled={quickApplyDisabled}
                      sx={{ opacity: quickApplyDisabled ? 0.5 : 1 }}>
                      <svg
                          className={
                            !quickApplyDisabled && !quickApplyActive ? 'eq-boost-spark' : undefined
                          }
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none">
                          <path
                            d="M7 1L2 7h3l-1 4 5-6H6l1-4Z"
                            stroke="currentColor"
                            strokeWidth="1.2"
                            strokeLinejoin="round"
                          />
                        </svg>
                      <span>EQ Boost</span>
                    </DsButton>
                  </Tip>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: 22,
                    marginBottom: 20,
                  }}>
                  <div>
                    <div
                      style={{
                        // the budget as the card's light hero figure, editable in place; a hairline marks the field
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        gap: 4,
                        paddingBottom: 4,
                        borderBottom: `1px solid ${budgetEditing ? C.lime : 'var(--tx-line)'}`,
                        transition: 'border-color .15s',
                      }}>
                      <span
                        style={{
                          fontSize: 20,
                          fontWeight: 200,
                          color: 'var(--tx-muted)',
                        }}>
                        $
                      </span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={budgetEditing ? budgetDraft : budget.toLocaleString()}
                        onFocus={(e) => {
                          setBudgetEditing(true);
                          setBudgetDraft(String(budget));
                          setTimeout(() => e.target.select(), 0);
                        }}
                        onChange={(e) => setBudgetDraft(e.target.value.replace(/[^\d.]/g, ''))}
                        onBlur={() => {
                          const num = parseFloat(budgetDraft) || 0;
                          const newPct = homeValue > 0 ? Math.max(0, (num / homeValue) * 100) : 0;
                          setPct(newPct);
                          setBudgetEditing(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          if (e.key === 'Escape') {
                            setBudgetEditing(false);
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                        title="Click to enter a custom budget amount"
                        style={{
                          width: m ? 130 : 160,
                          background: 'transparent',
                          border: 'none',
                          outline: 'none',
                          padding: 0,
                          fontFamily: VALUE_FONT_SMALL,
                          fontSize: 36,
                          fontWeight: 200,
                          color: 'var(--tx-text)',
                          lineHeight: 1,
                          letterSpacing: '-0.05em',
                          fontVariantNumeric: 'proportional-nums',
                          textAlign: 'left',
                        }}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: 'var(--tx-muted)',
                        fontFamily: "'Manrope',sans-serif",
                        marginTop: 8,
                      }}>
                      {pct % 1 === 0 ? pct : pct.toFixed(2)}% of {fmtFull(homeValue)}
                    </div>
                  </div>
                  {conditionScore != null &&
                    (() => {
                      const g = scoreToGrade(projectedScore);
                      const color = gradeBands[g?.[0]?.toUpperCase()]?.color ?? C.teal;
                      return (
                        <ScoreRing
                          size={96}
                          strokeW={3}
                          value={projectedScore}
                          color={color}
                          scoreDelta={scoreDelta}
                        />
                      );
                    })()}
                </div>
                <style>{`.mr-slider{-webkit-appearance:none;appearance:none;width:100%;height:2px;border-radius:1px;outline:none;cursor:pointer}.mr-slider::-webkit-slider-thumb{-webkit-appearance:none;width:16px;height:16px;border-radius:5px;background:${C.lime};box-shadow:var(--tx-shadow-sm);cursor:pointer}.mr-slider::-moz-range-thumb{width:16px;height:16px;border-radius:5px;background:${C.lime};border:none;cursor:pointer}`}</style>
                <input
                  type="range"
                  min={0}
                  max={10}
                  step={0.25}
                  value={pct}
                  onChange={(e) => setPct(parseFloat(e.target.value))}
                  className="mr-slider"
                  style={{
                    marginTop: 10,
                    touchAction: 'pan-y',
                    background: `linear-gradient(to right, ${C.lime} ${(pct / 10) * 100}%, ${
                      C.bg4
                    } ${(pct / 10) * 100}%)`,
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'var(--tx-faint)',
                    fontFamily: "'Manrope',sans-serif",
                    marginTop: 8,
                    marginBottom: 13,
                  }}>
                  <span>0%</span>
                  <span>2%</span>
                  <span>4%</span>
                  <span>6%</span>
                  <span>8%</span>
                  <span>10%</span>
                </div>
                <div style={{ marginTop: 'auto' }} />
                <div
                  style={{
                    display: 'flex',
                    gap: '6px 20px',
                    flexWrap: 'wrap',
                    marginTop: 10,
                    marginBottom: 12,
                  }}>
                  {/* the HomeDNA legend grammar: hairline in the urgency colour, the name, the count */}
                  {(['Critical', 'Urgent', 'Moderate', 'Low'] as const).map((sev) => {
                    const count = bySeverity[sev];
                    if (!count) return null;
                    return <LegendItem key={sev} color={SEV_COLORS[sev]} label={sev} count={count} />;
                  })}
                </div>
                <div
                  style={{
                    height: 2,
                    background: 'var(--tx-fill-2)',
                    borderRadius: 1,
                    overflow: 'hidden',
                    display: 'flex',
                    gap: 2,
                  }}>
                  {(['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'] as const).map((sev) => {
                    const cost = costBySeverity[sev] ?? 0;
                    if (cost === 0 || budget === 0) return null;
                    const widthPct = Math.min(100, (cost / budget) * 100);
                    return (
                      <div
                        key={sev}
                        style={{
                          height: '100%',
                          width: `${widthPct}%`,
                          background: SEV_COLORS[sev],
                          transition: 'width .3s ease',
                        }}
                      />
                    );
                  })}
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'var(--tx-muted)',
                    fontFamily: "'Manrope',sans-serif",
                    marginTop: 8,
                  }}>
                  <span>{fmtK(totalCost)} allocated</span>
                  <span>{fmtK(remaining)} remaining</span>
                </div>
              </div>
              {/* RIGHT */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 10,
                  }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--tx-muted)',
                      fontFamily: "'Manrope',sans-serif",
                    }}>
                    {plan.length} {plan.length === 1 ? 'item' : 'items'} in plan
                  </div>
                  {plan.length > 0 && (
                    <button
                      onClick={() => setShowDetail(true)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: 12,
                        color: 'var(--tx-muted)',
                        cursor: 'pointer',
                        fontFamily: "'Manrope',sans-serif",
                        fontWeight: 500,
                        padding: 0,
                      }}>
                      See all ›
                    </button>
                  )}
                </div>
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    maxHeight: m ? 220 : 320,
                    display: 'flex',
                    flexDirection: 'column',
                    scrollbarWidth: 'none',
                  }}>
                  {plan.length === 0 && (
                    <div
                      style={{
                        padding: 20,
                        textAlign: 'center',
                        color: C.muted,
                        fontSize: 11,
                        fontFamily: "'Manrope',sans-serif",
                      }}>
                      {unresolvedWithCost.length === 0
                        ? 'No issues with cost estimates'
                        : 'Increase budget to see recommendations'}
                    </div>
                  )}
                  {plan.map((issue, planIndex) => {
                    const cost = issue.costEstimateMax ?? issue.costEstimateMin ?? 0;
                    const sevColor = SEV_COLORS[issue.urgency] ?? C.muted;
                    const isExpanded = expandedPlanIssueId === issue.id;
                    const hasDescription = !!issue.description?.trim();
                    return (
                      <div key={issue.id}>
                        <div
                          onClick={() => setExpandedPlanIssueId(isExpanded ? null : issue.id)}
                          // a flat hairline row: one urgency cue (a short tick beside the name), the trade beneath, the cost light
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '10px 0',
                            borderTop: planIndex === 0 ? 'none' : '1px solid var(--tx-line)',
                            cursor: 'pointer',
                          }}>
                          <div
                            style={{
                              width: 2,
                              height: 12,
                              borderRadius: 1,
                              background: sevColor,
                              flexShrink: 0,
                              alignSelf: 'flex-start',
                              marginTop: 3,
                            }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: 'var(--tx-text)',
                                fontFamily: "'Manrope',sans-serif",
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>
                              {issue.title}
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: 'var(--tx-muted)',
                                fontFamily: "'Manrope',sans-serif",
                                marginTop: 2,
                              }}>
                              {issue.trade ?? issue.category}
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 15,
                              fontWeight: 300,
                              letterSpacing: '-0.03em',
                              color: 'var(--tx-text)',
                              fontFamily: "'Manrope',sans-serif",
                              fontVariantNumeric: 'tabular-nums',
                              flexShrink: 0,
                            }}>
                            {fmtK(cost)}
                          </div>
                          <span
                            style={{
                              color: 'var(--tx-faint)',
                              flexShrink: 0,
                              marginLeft: 2,
                              display: 'inline-flex',
                              alignItems: 'center',
                              transform: isExpanded ? 'rotate(180deg)' : 'none',
                              transition: 'transform 0.15s',
                            }}>
                            <ChevronDown size={14} />
                          </span>
                        </div>
                        {isExpanded && hasDescription && (
                          <div
                            style={{
                              padding: '0 24px 12px 12px',
                              fontSize: 12,
                              fontWeight: 500,
                              color: 'var(--tx-muted)',
                              fontFamily: "'Manrope',sans-serif",
                              lineHeight: 1.5,
                            }}>
                            {issue.description}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {/* the bid request hangs off the card's bottom edge as a lime tab (Hauser's signature CTA, kept from before the port) */}
      {onPrepareBidRequest && (
        <div style={{ display: 'flex' }}>
          <button
            onClick={onPrepareBidRequest}
            data-testid="prepare-bid-request-tab"
            style={{
              marginLeft: 24,
              background: C.lime,
              border: `1px solid ${C.lime}`,
              borderTop: 'none',
              borderRadius: '0 0 12px 12px',
              padding: '8px 16px 9px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: '#262626',
              cursor: 'pointer',
              fontFamily: "'Manrope',sans-serif",
            }}>
            Prepare bid request
            <style>{`
              @keyframes prepareBidArrowBounce {
                0%, 100% { transform: translateX(0); }
                50% { transform: translateX(4px); }
              }
            `}</style>
            <span
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: '#262626',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                animation: 'prepareBidArrowBounce 1.2s ease-in-out infinite',
              }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M5 12h14M13 6l6 6-6 6"
                  stroke={C.lime}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
        </div>
      )}

      {/* Detail overlay */}
      {showDetail && (
        <div
          onClick={() => setShowDetail(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1250,
            background: 'var(--tx-glass-panel)',
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 900,
              maxHeight: 'calc(100vh - 96px)',
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '24px',
              boxShadow: 'var(--tx-shadow)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '20px 24px',
                borderBottom: `1px solid ${C.border}`,
                flexShrink: 0,
              }}>
              <div>
                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 600,
                    color: C.text,
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  Recommended plan
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: C.muted,
                    fontFamily: "'Manrope',sans-serif",
                    marginTop: 2,
                  }}>
                  {plan.length} {plan.length === 1 ? 'item' : 'items'} · {fmtFull(totalCost)} of{' '}
                  {fmtFull(budget)} budget
                </div>
              </div>
              <CloseButton onClose={() => setShowDetail(false)} label="Close" />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {plan.map((issue) => {
                  const cost = issue.costEstimateMax ?? issue.costEstimateMin ?? 0;
                  const sevColor = SEV_COLORS[issue.urgency] ?? C.muted;
                  return (
                    <div
                      key={issue.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '12px 16px',
                        background: C.bg3,
                        backgroundImage: 'var(--tx-surface-light)',
                        boxShadow: 'var(--tx-shadow)',
                        borderRadius: '12px',
                      }}>
                      <div
                        style={{
                          width: 4,
                          height: 32,
                          borderRadius: 2,
                          background: sevColor,
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: C.text,
                            fontFamily: "'Manrope',sans-serif",
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                          {issue.title}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: 'var(--tx-muted)',
                            fontFamily: "'Manrope',sans-serif",
                            marginTop: 2,
                          }}>
                          {issue.urgency} · {issue.trade ?? issue.category}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: C.text,
                          fontFamily: "'Manrope',sans-serif",
                          flexShrink: 0,
                        }}>
                        {fmtK(cost)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── RECOMMENDATIONS CARD ────────────────────────────────────────────────────
const RECS = [
  {
    icon: Icon.droplet,
    title: 'Dishwasher Gasket Replacement',
    body: 'Replacing door gaskets ($15–$45) every 3–5 years prevents leaks causing $3,000+ in mold and subfloor rot.',
  },
  {
    icon: Icon.bulb,
    title: 'Attic Insulation Top-Up',
    body: 'Adding R-30 insulation to an under-insulated attic ($500–$1,500) can cut heating and cooling costs by up to 20%.',
  },
  {
    icon: Icon.leaf,
    title: 'HVAC Filter Replacement',
    body: 'Replacing your HVAC filter every 90 days ($10–$30) keeps airflow efficient and extends system life by 2–5 years.',
  },
];

function RecommendationsCard() {
  const [idx, setIdx] = useState(0);
  const rec = RECS[idx];
  return (
    <div
      style={{ ...card({ padding: '20px' }), display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: C.muted,
            fontFamily: "'Manrope',sans-serif",
          }}>
          Recommendations
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => setIdx((i) => (i - 1 + RECS.length) % RECS.length)}
            style={{
              background: 'var(--tx-fill)',
              border: 0,
              borderRadius: 8,
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.muted,
              cursor: 'pointer',
              fontSize: 15,
            }}>
            ‹
          </button>
          <button
            onClick={() => setIdx((i) => (i + 1) % RECS.length)}
            style={{
              background: 'var(--tx-fill)',
              border: 0,
              borderRadius: 8,
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.muted,
              cursor: 'pointer',
              fontSize: 15,
            }}>
            ›
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div
          style={{
            width: 72,
            height: 72,
            background: C.bg3,
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            border: 0,
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: C.muted,
            flexShrink: 0,
          }}>
          <div style={{ transform: 'scale(2.8)' }}>{rec.icon}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 17,
              fontWeight: 600,
              color: C.text,
              fontFamily: "'Manrope',sans-serif",
              lineHeight: 1.3,
              marginBottom: 8,
            }}>
            {rec.title}
          </div>
          <div
            style={{
              fontSize: 12,
              color: C.muted,
              fontFamily: "'Manrope',sans-serif",
              lineHeight: 1.7,
            }}>
            {rec.body}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function HomeownerHub() {
  const [points, setPoints] = useState(0);
  const [lastEvent, setLastEvent] = useState<{ pts: number; label: string } | null>(null);
  const [sunburst, setSunburst] = useState<number | null>(null);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselInitialIssueId, setCarouselInitialIssueId] = useState<string | null>(null);
  const [conditionDetailsOpen, setConditionDetailsOpen] = useState(false);
  const [nextMoveOpen, setNextMoveOpen] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const m = useWindowWidth() <= 768;

  const { selectedProperty, propertyIssues, isInitialFetchingProperties } = usePropertiesContext();
  const { isAdvisor, canAccessAdmin } = useUserRole();
  const { groups, isLoading: isLoadingGroups } = useClientGroups();
  const isSelfServeOwner = !isAdvisor && !canAccessAdmin && !isLoadingGroups && groups.length === 0;
  const propertyId = selectedProperty?.property.id;
  const {
    conditionScore,
    propertyDetails,
    isProcessing: isEqProcessing,
    isLoading: isLoadingEqResults,
    data: hauserEqData,
  } = useHauserEQResults(propertyId);

  // Repair-cost-to-list-price ratio: prefer backend value, fall back to local calc
  const repairPriceRatio = useMemo<number | null>(() => {
    const backendRatio = hauserEqData?.condition_kpis?.repair_price_ratio;
    if (typeof backendRatio === 'number') return backendRatio;
    const totalCost = hauserEqData?.condition_kpis?.total_repair_cost ?? 0;
    const listPrice = propertyDetails?.price ?? selectedProperty?.property.price ?? 0;
    if (listPrice <= 0 || totalCost <= 0) return null;
    return (totalCost / listPrice) * 100;
  }, [hauserEqData?.condition_kpis, propertyDetails?.price, selectedProperty?.property.price]);
  const { scoreToGrade, gradeBands } = useScoringConfig();
  const unresolvedCount = (propertyIssues ?? []).filter((i) => !i.resolved).length;
  const criticalCount = (propertyIssues ?? []).filter(
    (i) => !i.resolved && i.urgency === IssueUrgency.Critical,
  ).length;
  const urgentCount = (propertyIssues ?? []).filter(
    (i) => !i.resolved && i.urgency === IssueUrgency.Urgent,
  ).length;

  // EQ score simulator — toggle severity tiers to project score impact
  const [simResolved, setSimResolved] = useState<Set<string>>(new Set());
  const toggleSim = (tier: string) =>
    setSimResolved((prev) => {
      const next = new Set(prev);
      next.has(tier) ? next.delete(tier) : next.add(tier);
      return next;
    });
  const SEV_W: Record<string, number> = {
    Critical: 2.5,
    Urgent: 1.5,
    Moderate: 1,
    Low: 0.5,
    Monitor: 0.25,
  };
  const projectedScore = useMemo(() => {
    if (conditionScore == null || !propertyIssues?.length) return conditionScore ?? 0;
    const issues = propertyIssues.filter((i) => !i.resolved);
    const totalW = issues.reduce((s, i) => s + (SEV_W[i.urgency] ?? 0), 0);
    if (totalW === 0) return conditionScore;
    const resolvedW = issues
      .filter((i) => simResolved.has(i.urgency))
      .reduce((s, i) => s + (SEV_W[i.urgency] ?? 0), 0);
    return Math.min(
      100,
      Math.round(conditionScore + (resolvedW / totalW) * (100 - conditionScore)),
    );
  }, [conditionScore, propertyIssues, simResolved]);

  const displayScore = simResolved.size > 0 ? projectedScore : conditionScore ?? 0;
  const displayGrade = conditionScore != null ? scoreToGrade(displayScore) : '--';
  const displayColor = gradeBands[displayGrade?.[0]?.toUpperCase()]?.color ?? C.teal;
  const baseScore = conditionScore ?? 0;
  const [animScore, setAnimScore] = useState(displayScore);
  useEffect(() => {
    const start = animScore;
    const end = displayScore;
    if (start === end) return;
    const duration = 500;
    const t0 = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimScore(Math.round(start + (end - start) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [displayScore]);
  const scoreDelta = simResolved.size > 0 ? Math.max(0, animScore - baseScore) : 0;

  // Live stats for quick access cards
  const { documentRecords } = useDocumentRecords(propertyId);
  const { allCalendarEntries } = useCalendarEntries(propertyId);
  const { vendors } = useVendors(propertyId);
  const fileCount = (documentRecords ?? []).filter((r: any) => r.type === 'file').length;
  const folderCount = (documentRecords ?? []).filter((r: any) => r.type === 'folder').length;
  const vendorCount = (vendors ?? []).length;
  const upcomingCount = (allCalendarEntries ?? []).filter(
    (e) => e.startDate * 1000 >= Date.now(),
  ).length;

  // Quick Access tile visuals — each derived from the same live data as the
  // tile's own count, not decorative filler.
  const urgencyTicks = useMemo(() => {
    const unresolved = (propertyIssues ?? []).filter((i) => !i.resolved);
    // Same scale as HomeDNA (urgencyOrder: Critical=1 ... Monitor=5), most
    // urgent first — but a soundwave: one thin line per issue, height (not
    // width, like HomeDNA) driven by that issue's estimated cost.
    const sorted = [...unresolved].sort(
      (a, b) => urgencyOrder[a.urgency] - urgencyOrder[b.urgency],
    );
    // every open item, so the strip really runs Critical → Monitor (lines thin out to fit)
    const shown = sorted;
    const costs = shown.map((issue) => {
      const lo = issue.costEstimateMin ?? 0;
      const hi = issue.costEstimateMax ?? 0;
      return lo > 0 && hi > 0 ? (lo + hi) / 2 : Math.max(lo, hi);
    });
    const maxCost = Math.max(1, ...costs);
    return shown.map((issue, i) => {
      const pct = costs[i] / maxCost;
      return {
        heightPct: 20 + pct * 80,
        // monochrome like the other tiles: one neutral ink, brighter the more urgent
        shade: PUNCH_SHADE[issue.urgency] ?? 0.3,
      };
    });
  }, [propertyIssues]);

  // One vendor count (capped at 3, our recommended depth) per directory
  // category — rendered as a category-wide x 3-row dot grid.
  const RECOMMENDED_VENDORS_PER_CATEGORY = 3;
  const directoryCategoryCounts = useMemo(() => {
    const allTrades = VENDOR_TRADE_GROUPS.flatMap((g) => g.trades);
    const counts: Record<string, number> = {};
    for (const v of vendors ?? []) {
      counts[v.trade] = (counts[v.trade] ?? 0) + 1;
    }
    return allTrades.map((trade) => Math.min(counts[trade] ?? 0, RECOMMENDED_VENDORS_PER_CATEGORY));
  }, [vendors]);

  // Records — same diagonal-line visual, filled left-to-right by raw file
  // count (no per-line fraction, a file either fills a line or doesn't).
  const RECORD_LINE_COUNT = 30;
  // one lit line per real file, at a fixed pitch (more than 30 files narrows the pitch); no track of empty "capacity"
  const recordsFileLines = useMemo(
    () => Array.from({ length: Math.max(RECORD_LINE_COUNT, fileCount) }, (_, i) => (i < fileCount ? 1 : 0)),
    [fileCount],
  );

  // This week + next week, aligned to real Sun–Sat columns (not a rolling
  // 14-day window) — index i's weekday is i % 7, and today lands wherever
  // it actually falls in the row instead of always at slot 0.
  const calendarWeekGrid = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const todayDow = now.getDay(); // 0 = Sunday
    return Array.from({ length: 14 }, (_, i) => {
      const dayOffset = i - todayDow;
      const dayStart = now.getTime() + dayOffset * 24 * 60 * 60 * 1000;
      const dayEnd = dayStart + 24 * 60 * 60 * 1000;
      const count = (allCalendarEntries ?? []).filter((e) => {
        const t = e.startDate * 1000;
        return t >= dayStart && t < dayEnd;
      }).length;
      return { count, isToday: dayOffset === 0 };
    });
  }, [allCalendarEntries]);

  const awardPoints = (eventKey: string) => {
    const ev = POINT_EVENTS[eventKey];
    if (!ev) return;
    setPoints((prev) => {
      const next = prev + ev.pts;
      const hit = MILESTONES.find((ml) => prev < ml && next >= ml);
      if (hit) setTimeout(() => setSunburst(hit), 100);
      return next;
    });
    setLastEvent(ev);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setLastEvent(null), 2200);
  };

  const navigate = useNavigate();

  return (
    <div
      style={{
        position: 'relative',
        paddingTop: m ? 0 : 20,
        paddingBottom: m ? 10 : 20,
        paddingLeft: m ? 10 : 0,
        paddingRight: m ? 10 : 0,
        margin: '0 auto',
        fontFamily: "'Manrope',sans-serif",
        color: C.text,
      }}>
      <div>
        {/* Condition Score recalculating banner */}
        {isEqProcessing && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: 'rgba(192,255,2,0.06)',
              border: 0,
              borderRadius: 10,
              padding: '10px 14px',
              marginBottom: 12,
              fontSize: 13,
              color: 'var(--tx-lime-ink)',
            }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: 'var(--tx-lime)',
                flexShrink: 0,
              }}
            />
            Calculating Condition Score…
          </div>
        )}

        {/* Points toast */}
        {lastEvent && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <div
              role={isSelfServeOwner && conditionScore != null ? 'button' : undefined}
              tabIndex={isSelfServeOwner && conditionScore != null ? 0 : undefined}
              onClick={() => {
                if (isSelfServeOwner && conditionScore != null) setConditionDetailsOpen(true);
              }}
              onKeyDown={(event) => {
                if (
                  isSelfServeOwner &&
                  conditionScore != null &&
                  (event.key === 'Enter' || event.key === ' ')
                ) {
                  event.preventDefault();
                  setConditionDetailsOpen(true);
                }
              }}
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: C.lime,
                fontFamily: "'Manrope',sans-serif",
                letterSpacing: '.04em',
              }}>
              +{lastEvent.pts} · {lastEvent.label}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: m ? 10 : 20 }}>
          {/* TOP ROW: EQ + IQ + Weather */}
          <div
            style={{
              position: 'relative',
              // The expanded condition-score panel below overlays this row at
              // position:absolute inset:0, so it can't grow this row's height
              // by itself (see the comment on the always-mounted grid below).
              // This pads the row out by 70px only while that panel is open,
              // pushing HOME DNA and everything after it down to make room.
              paddingBottom: conditionDetailsOpen && !m ? 70 : undefined,
            }}>
            {/* Always mounted — the panel below overlays this at position:absolute
                inset:0, so it inherits this row's real natural width/height exactly
                (whatever that computes to, e.g. from WeatherCard's own content)
                instead of guessing a pixel value that can drift out of sync. */}
            <div
              style={{
                display: 'grid',
                // 27" only: 25 / 25 / 50, so EQ and IQ grow with the page and weather stops at half the row; a laptop keeps
                // its fixed 275px EQ and IQ columns
                gridTemplateColumns: m
                  ? '1fr'
                  : window.innerWidth >= 1680 && window.innerHeight >= 1000
                  ? 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr)'
                  : '275px 275px 1fr',
                gap: m ? 10 : 20,
                visibility: conditionDetailsOpen && !m ? 'hidden' : 'visible',
              }}>
                {/* EQ CARD */}
            {conditionDetailsOpen && m ? (
              // Mobile expanded state — replaces the compact EQ card in place
              // (same grid cell, full column width, normal document flow) so
              // it pushes IQ/Weather below it down naturally, rather than
              // floating as a separate dialog/overlay.
              <div style={{ position: 'relative' }}>
                <CloseButton
                  onClose={() => setConditionDetailsOpen(false)}
                  label="Close home condition details"
                  sx={{ position: 'absolute', top: 18, right: 18, zIndex: 5 }}
                />
                <ConditionScoreView scorecardOnly />
              </div>
            ) : (
            <div
              role={isSelfServeOwner && conditionScore != null ? 'button' : undefined}
              tabIndex={isSelfServeOwner && conditionScore != null ? 0 : undefined}
              onClick={() => {
                if (isSelfServeOwner && conditionScore != null) setConditionDetailsOpen(true);
              }}
              onKeyDown={(event) => {
                if (
                  isSelfServeOwner &&
                  conditionScore != null &&
                  (event.key === 'Enter' || event.key === ' ')
                ) {
                  event.preventDefault();
                  setConditionDetailsOpen(true);
                }
              }}
              style={{
                ...card({ padding: '20px' }),
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                position: 'relative',
                minHeight: m ? 'auto' : 400,
                cursor: isSelfServeOwner && conditionScore != null ? 'pointer' : 'default',
              }}>
              <div
                style={{
                  position: 'absolute',
                  top: 20,
                  left: 20,
                  fontSize: 17,
                  fontWeight: 400,
                  letterSpacing: '-0.015em',
                  color: 'var(--tx-text)',
                  fontFamily: "'Manrope',sans-serif",
                }}>
                EQ
              </div>
              {isSelfServeOwner && conditionScore != null && (
                <div
                  aria-label="View condition scorecard"
                  style={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    width: 38,
                    height: 38,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--tx-muted)',
                    background: 'var(--tx-fill)',
                    border: 0,
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
                  }}>
                  {/* Nested hexagons — same shape language as the spider-view
                      toggle icon in ConditionScoreView.tsx's expanded panel. */}
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 14 14"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true">
                    <polygon
                      points="7,1.5 11.8,4.25 11.8,9.75 7,12.5 2.2,9.75 2.2,4.25"
                      strokeWidth="0.9"
                    />
                    <polygon
                      points="7,3.8 10,5.5 10,8.5 7,10.2 4,8.5 4,5.5"
                      strokeWidth="0.9"
                    />
                    <polygon
                      points="7,5.5 8.6,6.4 8.6,7.6 7,8.5 5.4,7.6 5.4,6.4"
                      strokeWidth="0.9"
                    />
                  </svg>
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingTop: 40,
                  paddingBottom: 20,
                }}>
                <ScoreRing
                  size={175}
                  strokeW={4}
                  value={animScore}
                  color={displayColor}
                  scoreDelta={scoreDelta}
                />
              </div>
              <div>
                <div
                  style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', lineHeight: 1.2, color: C.text }}>
                  {conditionScore == null
                    ? 'No score yet.'
                    : displayScore >= 80
                    ? 'Home is healthy.'
                    : displayScore >= 60
                    ? 'Needs attention.'
                    : 'Needs work.'}
                </div>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--tx-muted)', lineHeight: 1.55, marginTop: 6 }}>
                  {conditionScore == null
                    ? 'Upload an inspection report to generate your score.'
                    : (() => {
                        const modCount = (propertyIssues ?? []).filter(
                          (i) => !i.resolved && i.urgency === IssueUrgency.Moderate,
                        ).length;
                        const priorityCount = criticalCount + urgentCount + modCount;
                        const parts = [
                          criticalCount > 0 && `${criticalCount} critical`,
                          urgentCount > 0 && `${urgentCount} urgent`,
                          modCount > 0 && `${modCount} moderate`,
                        ].filter(Boolean);
                        return priorityCount > 0
                          ? `${unresolvedCount} total remarks — ${priorityCount} (${parts.join(
                              ', ',
                            )}) worth paying attention to.`
                          : `${unresolvedCount} total remarks. No priority items at this time.`;
                      })()}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto' }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: 'var(--tx-muted)',
                    fontFamily: "'Manrope',sans-serif",
                    marginRight: 2,
                  }}>
                  Simulate repairs
                </span>
                {(
                  [
                    { key: IssueUrgency.Critical, color: C.pink },
                    { key: IssueUrgency.Urgent, color: C.orange },
                    { key: IssueUrgency.Moderate, color: C.yellow },
                  ] as const
                ).map((t) => {
                  const on = simResolved.has(t.key);
                  // a square check in the urgency colour per tier: outlined when off, filled when its repairs are simulated
                  return (
                    <Tip
                      key={t.key}
                      text={`${on ? 'Undo' : 'Simulate'} ${t.key.toLowerCase()} repairs`}
                      style={{ borderBottom: 'none', lineHeight: 0 }}>
                      <button
                        type="button"
                        aria-pressed={on}
                        aria-label={`Simulate ${t.key.toLowerCase()} repairs`}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleSim(t.key);
                        }}
                        style={{
                          width: 14,
                          height: 14,
                          padding: 0,
                          borderRadius: 4,
                          cursor: 'pointer',
                          transition: 'background .15s, border-color .15s',
                          background: on ? t.color : 'transparent',
                          border: `1px solid ${on ? t.color : `${t.color}99`}`,
                        }}
                      />
                    </Tip>
                  );
                })}
              </div>
              {isSelfServeOwner && conditionScore != null && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: 12,
                    borderTop: '1px solid var(--tx-line)',
                    color: 'var(--tx-muted)',
                    fontSize: 11,
                    fontWeight: 600,
                  }}>
                  <span>View condition details</span>
                  <span aria-hidden="true">→</span>
                </div>
              )}
            </div>
            )}

            {/* IQ CARD (not built yet: hidden on a phone, where it was a screen of empty slate) */}
            <div
              style={{
                ...card({ padding: '20px' }),
                display: m ? 'none' : 'flex',
                flexDirection: 'column',
                gap: 14,
                position: 'relative',
                minHeight: m ? 'auto' : 400,
                overflow: 'hidden',
              }}>
              {/* Coming Soon overlay */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--tx-glass-panel)',
                  backdropFilter: 'blur(3px)',
                  WebkitBackdropFilter: 'blur(3px)',
                  borderRadius: '24px',
                }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--tx-muted)',
                    fontFamily: "'Manrope',sans-serif",
                  }}>
                  Coming soon
                </span>
              </div>
              <div
                style={{
                  position: 'absolute',
                  top: 20,
                  left: 20,
                  fontSize: 24,
                  fontWeight: 300,
                  color: 'var(--tx-text)',
                  fontFamily: "'Manrope',sans-serif",
                }}>
                IQ
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingTop: 40,
                  paddingBottom: 20,
                  position: 'relative',
                }}>
                <ScoreRing
                  size={175}
                  value={Math.min(100, points)}
                  color={getLevel(points).color}
                />
                <SunburstEffect active={!!sunburst} color={getLevel(points).color} />
              </div>
              <div>
                <div
                  style={{ fontSize: m ? 15 : 17, fontWeight: 600, lineHeight: 1, color: C.text }}>
                  {getLevel(points).label}
                </div>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--tx-muted)', lineHeight: 1.55, marginTop: 6 }}>
                  Explore your home, take action on issues, and grow your score.
                </div>
              </div>
              <div style={{ marginTop: 'auto' }}>
                {(() => {
                  const next = nextLevel(points);
                  return next ? (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: 5,
                        }}>
                        <span style={{ fontSize: 10, color: 'var(--tx-muted)' }}>Next: {next.label}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx-muted)' }}>
                          {next.min - points} pts away
                        </span>
                      </div>
                      <div
                        style={{
                          height: 4,
                          background: C.bg4,
                          borderRadius: 4,
                          overflow: 'hidden',
                        }}>
                        <div
                          style={{
                            height: '100%',
                            borderRadius: 4,
                            transition: 'width .4s ease',
                            background: getLevel(points).color,
                            width: `${Math.round(
                              ((points - getLevel(points).min) /
                                (next.min - getLevel(points).min)) *
                                100,
                            )}%`,
                          }}
                        />
                      </div>
                    </>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.lime }}>
                      Max level reached
                    </span>
                  );
                })()}
              </div>
            </div>

                {/* WEATHER CARD */}
                <WeatherCard />
            </div>
            {conditionDetailsOpen && !m && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  ...card({ padding: '20px' }),
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                <CloseButton
                  onClose={() => setConditionDetailsOpen(false)}
                  label="Close home condition details"
                  sx={{ position: 'absolute', top: 18, right: 18, zIndex: 5 }}
                />
                <div style={{ flex: 1, minHeight: 0 }}>
                  <ConditionScoreView layout="horizontal" />
                </div>
              </div>
            )}
          </div>

          {/* HOME DNA */}
          <InspectionSummaryVisualCard
            hideRepairBurden
            onSeeDetailsClick={() => {
              setCarouselInitialIssueId(null);
              setCarouselOpen(true);
            }}
            onIssueSelect={(issue) => {
              setCarouselInitialIssueId(issue.id);
              setCarouselOpen(true);
            }}
          />

          {/* QUICK ACCESS: the same 20px gutter between tiles as between the page's rows */}
          <div style={{ display: 'flex', flexDirection: m ? 'column' : 'row', gap: m ? 10 : 20 }}>
            {/* Punch List — urgency spread as a small colored bar chart,
                low-to-high severity left to right. */}
            <QuickAccessTile
              onClick={() => {
                navigate('/dashboard/home-hub/issues');
                awardPoints('panel_open');
              }}
              icon={Icon.checklist}
              label="Punch list"
              value={unresolvedCount}
              unit={unresolvedCount === 1 ? 'open item' : 'open items'}
              corner={<RFQCompileIndicator />}>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: 32 }}>
                {urgencyTicks.length === 0 ? (
                  <div
                    style={{
                      width: '100%',
                      height: 2,
                      borderRadius: 1,
                      background: 'var(--tx-fill-2)',
                    }}
                  />
                ) : (
                  urgencyTicks.map((t, i) => (
                    <div
                      key={i}
                      style={{
                        // 1px lines that may narrow further, so any count fits the tile edge to edge
                        width: 1,
                        flexShrink: 1,
                        minWidth: 0.5,
                        height: `${t.heightPct}%`,
                        borderRadius: 1,
                        background: 'var(--tx-text)',
                        opacity: t.shade,
                      }}
                    />
                  ))
                )}
              </div>
              <QuickAccessCaption left="Critical" right="Monitor" />
            </QuickAccessTile>

            {/* Records & Files — the diagonal-line visual, filled left to
                right by raw file count. */}
            <QuickAccessTile
              onClick={() => {
                navigate('/dashboard/home-hub/records');
                awardPoints('panel_open');
              }}
              icon={Icon.folder}
              label="Records & files"
              value={fileCount}
              unit={fileCount === 1 ? 'file' : 'files'}>
              {/* muted slashes run the full strip as texture; each lit one is a real file (the caption gives the count) */}
              <DiagonalFillLines fills={recordsFileLines} shear={14} />
              <QuickAccessCaption
                left={`${fileCount} ${fileCount === 1 ? 'file' : 'files'} · ${folderCount} ${folderCount === 1 ? 'folder' : 'folders'}`}
              />
            </QuickAccessTile>

            {/* Directory — one dot per vendor category, lit up by how many
                vendors are saved against our recommended depth of 3. */}
            <QuickAccessTile
              onClick={() => {
                navigate(`/dashboard/home-hub/${isSelfServeOwner ? 'directory' : 'vendors'}`);
                awardPoints('panel_open');
              }}
              icon={Icon.users}
              label="Directory"
              value={vendorCount}
              unit={vendorCount === 1 ? 'vendor saved' : 'vendors saved'}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${directoryCategoryCounts.length}, 1fr)`,
                  gridTemplateRows: `repeat(${RECOMMENDED_VENDORS_PER_CATEGORY}, 1fr)`,
                  gridAutoFlow: 'column',
                  justifyItems: 'center',
                  alignItems: 'center',
                  height: 32,
                  rowGap: 4,
                }}>
                {directoryCategoryCounts.flatMap((count, i) =>
                  Array.from({ length: RECOMMENDED_VENDORS_PER_CATEGORY }, (_, row) => (
                    <div
                      key={`${i}-${row}`}
                      style={{
                        width: 3,
                        height: 3,
                        borderRadius: '50%',
                        background:
                          count >= RECOMMENDED_VENDORS_PER_CATEGORY - row
                            ? '#bbb7af'
                            : 'rgba(255,255,255,0.08)',
                      }}
                    />
                  )),
                )}
              </div>
              <QuickAccessCaption
                left={`${directoryCategoryCounts.filter((n) => n > 0).length} of ${directoryCategoryCounts.length} trades covered`}
              />
            </QuickAccessTile>

            {/* Calendar — this week + next week as a real Sun–Sat grid,
                today outlined wherever it actually falls, event days
                filled and shaded by count. */}
            <QuickAccessTile
              onClick={() => {
                navigate('/dashboard/home-hub/calendar');
                awardPoints('panel_open');
              }}
              icon={Icon.calendar}
              label="Calendar"
              value={upcomingCount}
              unit={upcomingCount === 1 ? 'upcoming event' : 'upcoming events'}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gridTemplateRows: 'repeat(2, 1fr)',
                  gap: 4,
                  height: 32,
                }}>
                {calendarWeekGrid.map(({ count, isToday }, i) => (
                  <div
                    key={i}
                    style={{
                      borderRadius: 3,
                      border: isToday
                        ? '1px solid #bbb7af'
                        : count === 0
                        ? '1px solid rgba(255,255,255,0.12)'
                        : 'none',
                      background: count > 0 ? 'var(--tx-fill-2)' : 'transparent',
                      opacity: count > 0 ? Math.min(0.45 + count * 0.25, 1) : 1,
                    }}
                  />
                ))}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  marginTop: 4,
                }}>
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <span
                    key={i}
                    style={{ fontSize: 10, color: C.muted, textAlign: 'center' }}>
                    {d}
                  </span>
                ))}
              </div>
            </QuickAccessTile>
          </div>

          {!isSelfServeOwner && (
            <>
              {/* Existing advisor/client Home HQ remains unchanged. */}
              <RepairHorizon
                issues={propertyIssues ?? []}
                repairPriceRatio={repairPriceRatio}
                conditionScore={conditionScore ?? null}
                collapsible
                storageKey="hq-rh-open"
              />
              <MaintenanceReserveCard
                homeValue={propertyDetails?.price ?? selectedProperty?.property.price ?? 0}
                issues={propertyIssues ?? []}
                conditionScore={conditionScore ?? null}
              />
            </>
          )}
        </div>
      </div>
      <SelfServeOnboardingModal isMobile={m} />
      <Dialog
        open={nextMoveOpen}
        onClose={() => setNextMoveOpen(false)}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: m ? 'calc(100vw - 32px)' : 650,
              height: m ? 'auto' : 520,
              maxHeight: m ? 'calc(100dvh - 32px)' : 'none',
              maxWidth: 'none',
              boxSizing: 'border-box',
              bgcolor: 'var(--tx-glass-panel)',
              backgroundImage: 'none',
              border: 0,
              borderRadius: '25px',
              boxShadow: '0 28px 80px rgba(0,0,0,.55)',
              p: m ? '20px' : '25px',
              overflowY: m ? 'auto' : 'visible',
            },
          },
          backdrop: {
            sx: { bgcolor: 'rgba(0,0,0,.46)', backdropFilter: 'blur(6px)' },
          },
        }}>
        <Box
          sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CloseButton
            onClose={() => setNextMoveOpen(false)}
            label="Close"
            sx={{ position: 'absolute', right: -10, top: -10 }}
          />

          <Typography sx={{ fontSize: m ? 20 : 24, fontWeight: 300, letterSpacing: '-0.02em', color: 'var(--tx-text)', mb: '7px' }}>
            What’s your next move?
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#918d87', mb: m ? '20px' : '28px' }}>
            Choose the path that fits where you’re headed. We’ll connect you with the right team.
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: m ? '1fr' : '1fr 1fr',
              gap: '14px',
              flex: 1,
              minHeight: 0,
            }}>
            {[
              {
                brand: 'AVENIDA',
                title: 'Sell a home',
                body: 'Understand timing, positioning, and your clearest path to market.',
                action: 'Explore selling',
                url: 'https://avenidaliving.com',
                Icon: Home,
              },
              {
                brand: 'HAUSER',
                title: 'Buy a home',
                body: 'Evaluate homes clearly and move forward with greater confidence.',
                action: 'Explore buying',
                url: 'https://hauserhq.com',
                Icon: KeyRound,
              },
            ].map(({ brand, title, body, action, url, Icon: PathIcon }) => (
              <Box
                key={brand}
                component="button"
                type="button"
                onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                sx={{
                  appearance: 'none',
                  textAlign: 'left',
                  bgcolor: 'var(--tx-fill)',
                  border: 0,
                  borderRadius: '16px',
                  p: m ? '16px' : '22px',
                  color: 'inherit',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  transition:
                    'border-color 160ms ease, transform 160ms ease, background 160ms ease',
                  '&:hover': {
                    borderColor: 'var(--tx-line)',
                    bgcolor: 'var(--tx-fill)',
                    transform: 'translateY(-2px)',
                  },
                  '&:hover .path-arrow': { transform: 'translateX(4px)', color: '#c0ff02' },
                }}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '.14em',
                      color: 'var(--tx-faint)',
                    }}>
                    {brand}
                  </Typography>
                  <PathIcon size={22} color="#bbb7af" strokeWidth={1.8} />
                </Box>
                <Typography
                  sx={{
                    fontSize: m ? 20 : 22,
                    fontWeight: 600,
                    color: 'var(--tx-text)',
                    mt: m ? '18px' : '34px',
                  }}>
                  {title}
                </Typography>
                <Typography sx={{ fontSize: 12, lineHeight: 1.6, color: '#918d87', mt: '7px' }}>
                  {body}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    mt: 'auto',
                    pt: '18px',
                  }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)' }}>
                    {action}
                  </Typography>
                  <ArrowRight
                    className="path-arrow"
                    size={15}
                    color="#bbb7af"
                    style={{ transition: 'all 160ms ease' }}
                  />
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            component="button"
            type="button"
            sx={{
              mt: '16px',
              p: 0,
              border: 0,
              bgcolor: 'transparent',
              color: '#77736e',
              fontFamily: 'Manrope, sans-serif',
              fontSize: 11,
              textAlign: 'left',
              cursor: 'pointer',
              '&:hover': { color: 'var(--tx-muted)' },
            }}>
            I’m considering both / just exploring
          </Box>
        </Box>
      </Dialog>
      <InspectionCarouselOverlay
        open={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        initialIssueId={carouselInitialIssueId}
      />
    </div>
  );
}

export { CalendarPanel, DirectoryPanel, HomeownerHub, IssuesPanel, RecordsPanel };
